import { wallHuntGameSample, wallHuntImage, preloadWallHuntArt, wallHuntFacePoint } from './awoken_wall_hunt_game'
import { KNIGHT_SWEEP_ART } from './knight_sweep_motion'
import { isKnightRearmKey, KNIGHT_REARM_CONTACT_META, KNIGHT_REARM_PADDING } from './knight_reset_rearm'
import { knightHelmetScaleEnabled, knightHelmetScaleFactor } from './knight_helmet_scale'
import { knightColorGradedEnabled, knightSpriteSource } from './knight_color_grade'
import { KNIGHT_CHARGE_STRIDE } from './knight_wall_charge'
import { bossWalkSurgeOffsetPx } from './boss_walk_surge'
import { knightGaitPoseKey } from './knight_gait'
import { advanceKnightWalkBridge, knightWalkBridgeKey } from './knight_walk_bridge'
import { advanceKnightRestMid, advanceKnightScrapeTurn, knightRestMidPoseKey, knightRestMidRearmKey, knightRestMidShoveKey, knightRestMidTeleKey, knightScrapeTurnHistoryImage, knightScrapeTurnKey } from './knight_rest'
import { AWOKEN_FANG_EXTRA_IMAGES, awokenFangPose, awokenFangSourcePose } from './awoken_fang_cues'
import { AWOKEN_DEVOUR_JAW_IMAGE, awokenDevourJawEnabled, awokenDevourMode } from './awoken_devour'
import { POUNCE_LAUNCH_IMAGE, POUNCE_REACH_IMAGE } from './awoken_devour_pounce'
import { AWOKEN_SPIN_GHOST_ALPHA, awokenSpinGhostKey, awokenSpinMs, awokenSpinShakePx } from './awoken_spin_fx'
// スプライト管理: ボス6ステート画像のロード + キャッシュ + クロスフェード補助。
// 設計のキモ:
//   - 起動時にプリロード (Image オブジェクト生成)、初回 draw 時に未ロードなら fallback
//   - ステート遷移時に prevState/currState を持ち、crossfadeMs かけてα補間
//   - 合成モード 'screen' で暗背景を自然消滅、ネオン発光を強調
//
// 対応ボス: dream-hound (Phase 9-α POC)。気に入ったら他ボスへ展開

import type { Boss, BossSpriteRuntime, BossState, PlayerAttackContact, SpriteKey } from './types'
import { FAJIN_DOWNCUT_ART } from './executioner_fajin_art'
import { finisherFootAnchorFor } from './executioner_finisher_foot'
import { plantFootAnchorFor } from './exec_plant_foot'
import { buildTweenMesh, drawTweenWarp, knightRestElapsedMs, meshOffsetAt, tweenFixPlan, type TweenMesh } from './tween_fix'
import { NO_RECOVERY_SWAY, recoverySwayMotion, type RecoverySway } from './recovery_sway'
import { finisherStepTimeline } from './exec_finisher_step'
import { CANNON_FAJIN_ART, cannonFajinBodyMotion } from './executioner_cannon_fajin'
import { arenaBoundsFor } from './balance'
import { drawAwokenRushBrake, isAwokenRushOpening, isAwokenRushRelease } from './awoken_rush_study'
import {
  resolveBossClipKey, clipFrameIndex, clipDurationMs, loadBossClip,
  type LoadedBossClip, type ClipCursor,
} from './boss_clips'
import {
  BOSS_ATTACK_TIMELINES,
  EXEC_JAB_TIMELINE_VARIANTS,
  EXEC_FINISHER_TIMELINE_OLD,
  EXEC_FINISHER_TIMELINE_SUE,
  selectTimelineFrame,
  phaseProgressOf,
  resolveTimelineDraw,
  comboNoNeutralImage,
  knightComboContinuationImage,
  resolveTimelineMotion,
  getBrideMotionArt,
  NO_POSE_MOTION,
  knightResetRecoveryImage,
  BRIDE_ISSEN_ANCHOR_X,
} from './boss_pose_timeline'
import type { BossPoseMotion } from './boss_pose_timeline'
import { bossSoleLockEnabled, soleLockMotion } from './boss_sole_lock'
import { execJabLinkTimeline, execJabSkipsSwell, execJabSkipsTremble } from './exec_jab_tremble_1028'
import { knightSmoothGapOffsetX, knightSmoothLinkTimeline, knightSmoothRestShift, knightSmoothSkipsSwell } from './knight_smooth_flags_1028'
import type { BossAttackTimeline, TimelineFrame } from './boss_pose_timeline'
import { getBossProxyDataUrl } from './boss_proxy'
import { BLINK_REAPPEAR_MS, effectiveChargeMs, isExecutionerRegainWalk } from './boss'
import { awokenSpacingPoseKey, isAwokenSpacingWalk } from './awoken_spacing_choice'
import { roosterHopPoseKey } from './rooster_hop_move'
import { awokenBackWalkDrawFacing } from './awoken_back_walk'
import { bladeBackStepIsStep, bladeBackStepLockPose, bladeBackStepSettled, isBladeBackStepWalk } from './blade_back_step'
import { EXEC_PARRIED_ART_1021, EXEC_PARRIED_ART_1021_BOSSES, awokenParriedArtKey, execParriedTimeline, execParryRecoilNow } from './exec_parry_recoil'
import { BRIDE_EXEC_ART, BRIDE_EXEC_ART_PLACEMENT, execUniqueOn, execUniqueTimeline, KNIGHT_EXEC_ART, KNIGHT_EXEC_ART_PLACEMENT } from './exec_unique'
import { awokenDevourTimeline } from './awoken_devour'
import { bladeClapTimeline } from './blade_finisher_v2'
import { jabFixTimeline } from './exec_jab_fix'
import { executionerMorphTiming } from './executioner_weapon'
import { EXEC_CLOAK_SETTLE_MS, EXEC_CLOAK_SETTLE_ANCHOR_X, execCloakSettleOn, execCloakFlowOn, execCloakCannonOn, EXEC_CLOAK_FLOW_LIFT_FROM_MS, EXEC_CLOAK_FLOW_KEYS, EXEC_CLOAK_FLOW_ANCHOR_X, EXEC_CLOAK_CANNON_ANCHOR_X } from './exec_cloak'
import { variantSpriteFileListed } from './sprite_manifest'
import { EXEC_WEAPON_FIX_KEYS } from './executioner_weapon_fix'
import { brideKaeshiOn } from './bride_kaeshi'
import { brideKaeshiBladeOn } from './bride_kaeshi_blade'
import { bladeColorFilter } from './blade_color_match'
import { bladeClapKey } from './blade_dancer_clap'
import { execBodyImg, warmExecBodyImgs } from './exec_tint'
import { bossRiseBlend, bossRiseFitOn, bossRiseKey, bossRiseScaleY } from './boss_rise'

// 動画クリップのキャッシュ (bossId -> clipKey -> LoadedBossClip)。 preloadBossSprites で非同期充填。
const clipCache: Map<string, Map<string, LoadedBossClip>> = new Map()

// parried 割り込み時間に使う = parried クリップの総尺 (未配置なら undefined → 220ms fallback)。
export function getBossClipDurationMs(bossId: string, key: string): number | undefined {
  const c = clipCache.get(bossId)?.get(key)
  return c ? clipDurationMs(c.meta) : undefined
}

// 6ステートのキー (ファイル名と対応)
const STATE_KEYS: SpriteKey[] = ['idle', 'telegraph', 'active', 'parried', 'staggered', 'defeat']

// 非攻撃サブアニメ用の追加ポーズ (タイムラインに出ない=自動収集されないので明示プリロード)。
//   STAGE B: 歩き2枚 / 向き替え (騎士)。処刑人は歩き4コマ。 idle_breath は 1枚絵方式 §0.2 で撤去 (2026-06-15)。
const EXTRA_POSE_KEYS_BY_BOSS: Record<string, SpriteKey[]> = {
  // 王 (2026-08-11): 歩きの絵が無く、待機の絵のまま 270px/s で滑って寄ってきていた (ユーザー実機指摘)。
  //   ⚠️ walk_* は STATE_KEYS にも王の攻撃タイムラインにも出てこない = **ここへ書かないと一生ロードされない**。
  //      「public/ へ絵を置けば動く」は誤り (この行と WALK_DISCRETE_SNAP の登録が要る)。
  // 間合いの後ろ跳びの沈みと宙 (2026-11-01・src/spacing_all.ts)。どちらも配布版の技の並びに出ないので、ここで読む。
  'dream-hound': ['walk_passing', 'walk_reach', 'rise1', 'rise2', 'houndSmooth_sink', 'houndBackHop_air_1101'],
  // 花嫁 宵渡り分岐: reappear は攻撃timeline外なので、1撃/3撃の構えを明示プリロードする。
  // veil_link_snap_windup は ?brideMotion=old の比べ用 (表を切り替えても絵が出るように)。veil_open は他の技の表で読まれる。
  // issen_*_1023 は開発版 ?brideIssen=1 の一閃の比較版、kaeshi_* は ?brideKaeshi=new の返しの一閃 (表は起動後に差し替わるので明示ロード)。
  // kaeshi_tween_*_1023 は開発版 ?brideKaeshi=tween|both の返しの一閃の間の絵 (src/bride_kaeshi_tween.ts)。
  // embrace_hug_1023 は開発版 ?brideRedraw=hug の両腕で抱く抱擁 (src/bride_redraw_1023.ts)。
  'bride': ['blink_single_charge', 'blink_triple_charge', 'side_crouch', 'blink_triple_charge_1021', 'veil_link_snap_windup', 'issen_raise_1023', 'issen_swing_1023', 'issen_sink_1023', 'kaeshi_pray', 'kaeshi_sweep', 'kaeshi_cut_1023', 'kaeshi_tween_rise_1023', 'kaeshi_tween_reach_1023', 'kaeshi_tween_settle_1023', 'embrace_hug_1023',
    // 崩れから立ち上がる2枚 (2026-10-30・src/boss_rise.ts)。
    'rise1', 'rise2'],
  // linkWindup/拍間holdは timeline 外のoverrideなので明示ロードする。
  'vigil-knight': [
    // 技の後に足を寄せる絵 (2026-10-08 ユーザー採用・配布版でも読む。knight_rest.ts)。
    'rest_from_scrape', 'rest_from_rearm', 'rest_from_thrust',
    // 押し返し・石突・崩れの後の4枚 (2026-10-12 ユーザー採用・配布版でも読む。旧は DEV ?knightRest=mid)。
    'rest_from_shove', 'rest_from_butt', 'rest_from_stagger', 'rest_from_stagger2',
    // 構え直しの後の間の絵2枚 (2026-10-27 配布版・knight_rest.ts。旧は DEV ?knightRearmTween=off)。
    'rest_from_rearm_a', 'rest_from_rearm_b',
    // 壁ぎわの向き替えの前に石突を前へ運ぶ2枚 (2026-10-29 配布版の既定 plant・knight_rest.ts)。
    'rest_rise_55', 'rest_rise_70',
    // 崩れから立ち上がる2枚 (2026-10-30・src/boss_rise.ts)。
    'rise1', 'rise2',
    'walk_passing', 'walk_reach', 'suri_open', 'suri_draw', 'lanceSweep_reset', 'lanceSweep_rearm', 'lanceSweep_rearmMid', 'turn',
    'lanceSweep_linkWindup', 'lanceThrust_linkWindup',
    'lanceSweep_holdOn', 'lanceDouble_gap',
    // lanceSweep_release は 2026-09-29 使用禁止 (ユーザー「どう見ても疲れている絵」)・絵は public/sprites/archived/vigil-knight/。
    'charge_run1', 'charge_run2',
  ],
  // 処刑人: タイムライン外の override 専用ポーズ。walk_passing / walk_contact_b / walk_cross は
  // ステップ局面の1枚絵として使う。歩き4コマ一式もロールバック用に削除せず保持する。
  //   BOSS_ATTACK_TIMELINES に登場しないため自動収集されず、ここへ明示しないとプリロードされない。
  'the-executioner': ['walk_passing', 'walk_reach', 'walk_cross', 'walk_contact_b', 'turn', 'grabbed_hold', 'staggered_enter', 'staggered_hold',
    // 崩れから立ち上がる2枚 (2026-10-30・src/boss_rise.ts)。刃・砲それぞれのフォルダに置く。
    'rise1', 'rise2',
    'grab_reach',   // grab_reach は2026-08-12退避の旧fetch素材。実行経路では使わない。
    // ステップ移動 v2 (前ステップ型・2026-08-23 夕) の専用絵。沈み / 滑り / 着地 / 後退 (後退は砲だけ。刃は set_backstep を流用)。
    //   票 = docs/candidates/2026-08-23-executioner-step-v2-shunpo-ticket.md §3 / 割り当て = resolveExecutionerStepPoseKey。
    'step_crouch', 'step_smear', 'step_land', 'step_settle', 'step_back',  // step_settle = 外套のはためき (src/exec_cloak.ts・刃だけ)
    'step_cloak_lift', 'step_cloak_fall', 'step_cloak_drop',  // 外套のはためきのつなぎ (刃は既定・砲は ?execCloak=cannon)
    // 🔵 2026-09-02: set_backstep は刃の後退ステップ (resolveExecutionerStepPoseKey) が読む。08-26〜09-01 は
    //   斬り上げのタイムラインが読んでいたので自動収集に乗っていたが、5キー化で外れた = ここに無いとロードされない。
    'set_backstep',
    'morph_a', 'morph_b', 'morph_c', 'aim_hold', 'fire_recoil',
    // 🔵 2026-09-02 深夜: 榴弾ボム (exec_cannonMortar) 専用の構え／撃つ。
    'mortar_aim', 'mortar_fire',
    // 🔵 2026-09-02 深夜: 地割り (exec_maulQuake) の当たり = 刃を垂直に突き立てた瞬間。
    'quake_contact',
    // 🔵 2026-09-03 第3便: 速射 (exec_cannonVolley) の撃ち終わり専用絵。反動に耐えつつ砲口は相手へ・少し怯む。
    // 🔵 2026-09-03 第6便: 砲身薙ぎの振り抜き。
    // 🔵 2026-09-03 第8便: 砲身薙ぎの構え（候補は sweep_windup_<look> で切替）。
    'volley_after', 'sweep_contact', 'sweep_windup',
    // 🔵 2026-09-04: 据えの突き (exec_maulSetPierce) の専用絵。候補 b はタイムライン切替でしか読まれない
    //   (自動収集に乗らない) のでここに明示する。
    'pierce_draw', 'pierce_thrust', 'pierce_draw_b', 'pierce_thrust_b',
    // 旧溜めで起動しても、新しい溜めへその場で切替できるように先読み。
    'fajin_draw' as SpriteKey, 'fajin_thrust' as SpriteKey,
    'fajin_settle' as SpriteKey, 'fajin_coil' as SpriteKey, 'fajin_release' as SpriteKey,
    // 🔵 2026-09-22 突き当て A-1 (踏み込み突き) の本番絵。?jabArt=lunge でだけ読まれるので明示する。
    'jab_coil' as SpriteKey, 'jab_lunge' as SpriteKey,
    // 🔵 2026-10-28 突き当ての入りと戻りの段の絵。配布版の 1028e が戻りに、比較版の 1028d が入りにも使う (src/exec_jab_steps_1028.ts)。
    'jab_sink_1' as SpriteKey, 'jab_return_1' as SpriteKey, 'jab_return_2' as SpriteKey,
    ...Object.keys(FAJIN_DOWNCUT_ART) as SpriteKey[],
    // 🔵 2026-09-04: sweep_slash は既定のタイムラインから外れた (据えの突きが専用絵へ)。?pierceArt=old の比較用に
    //   だけ読まれる＝自動収集に乗らないので明示する。絵は残置 (常時ルール4)。
    'sweep_slash',
    // 🔵 2026-10-20: 致命「執行」は据え斬り (sue_*) が既定。前の並びの4枚は ?execFinisherFx=old と砲の間だけ読まれるので明示する。
    'overhead_raise' as SpriteKey, 'downswing_smear' as SpriteKey, 'exec_contact', 'overhead_recover' as SpriteKey],
  // 覚醒王 (2026-07-07 Fable5 ビルド): 高速歩行の足滑り対策 (walk 2枚) + 崩れ二段 (staggered_*)。
  //   絵は DEVプロキシ (awoken_king_proxy.ts) が供給。 最終絵バッチでも同名で発注する。
  'crowned-king-awoken': ['walk_passing', 'walk_reach', 'staggered_enter', 'staggered_hold',
    // 崩れから立ち上がる2枚 (2026-10-30・src/boss_rise.ts)。
    'rise1', 'rise2',
    'pocket_spin_wind_1001' as SpriteKey,
    'pocket_spin_rear_1002' as SpriteKey,
    'pocket_spin_sweep_1001' as SpriteKey,
    'pocket_spin_front_1002' as SpriteKey,
    'pocket_spin_settle_1001' as SpriteKey,
    // 10-04 顔の描き直し（案 C 採用・ユーザー）。1001 は残置。
    'pocket_spin_settle_1002' as SpriteKey,
    'back_hop_crouch_1001' as SpriteKey, 'back_hop_air_1001' as SpriteKey, 'back_hop_land_1001' as SpriteKey,
    ...AWOKEN_FANG_EXTRA_IMAGES as SpriteKey[]],
}

// generic STATE_KEYS のうち「このボスにはファイルが存在しない」ものの除外表 (検収 2026-07-06)。
//   処刑人バッチ1で旧6枚を退役 → telegraph/active は全技タイムライン化で不要・parried=recoil方式・
//   staggered=staggered_enter/hold の二段 override。defeat はバッチ3で本番絵が入ったため標準ロードへ戻した。
//   除外しないとプリロードが 404 を吐き、壊れ Image (complete=true) が cache に残る。
//   ⚠️ここへ足す時は、除外キーを描画が絶対に要求しないこと (effectiveKey/override の差し替え先) を確認する。
const STATE_KEYS_OMITTED_BY_BOSS: Record<string, SpriteKey[]> = {
  'the-executioner': ['telegraph', 'active', 'parried', 'staggered'],
  // 覚醒王 (2026-08-07 本番絵バッチ): 刑吏と同じ理由で4つとも要求されない。 実確認:
  //   telegraph/active = 技11本 (feralBite/BiteDelayed/Frenzy/FrenzyExtend/Rush/RushChase/rampage/
  //     WallDash/WallDashBack/feralRoar/kingExecutionAwoken) が**全て** BOSS_ATTACK_TIMELINES に有る
  //   parried = PARRIED_OVERLAY_DISABLED に登録済 (recoil+FX で出す)
  //   staggered = staggered_enter/hold の二段 override (下の 1148 行付近・両方 loaded なら通らない)
  //   退役した旧6枚 (真紅焼き込みマスター) は public/.../_archived-2026-08-07/ へ退避。
  'crowned-king-awoken': ['telegraph', 'active', 'parried', 'staggered'],
}

// 撃破 (hp<=0) 時、 専用の「崩れ落ちる」死の絵を見せるボス。
//   ※花嫁は当初 death_collapse (立ち姿) を専用ポーズ+保持/沈み/細グレインで作り込んだが、 実機で
//     「棒立ちが長く騎士に劣る=ほぼ0点」(ユーザー 2026-06-29)。 騎士の制御 (跪き defeat.png を即・通常
//     ディゾルブ) に寄せるため花嫁の専用処理を撤去 → effectiveKey が標準の defeat (跪き) を返す。
//     death_collapse.png はアセットとして残置 (将来の演出用・§2.1)。
const BOSS_DEATH_COLLAPSE: Record<string, { key: SpriteKey; holdSec: number; fadeSec: number }> = {
}

// スプライトを持っているボスID
//   export は整合性テスト用 (tests/safety_invariants.test.ts: 登録IDが実在ロースターか /
//   SOURCE_FACING が全員分あるか)。 ゲームコードからは hasBossSprite() を使うこと。
export const SPRITE_BOSSES = new Set<string>(['dream-hound', 'bride', 'vigil-knight', 'former-princess', 'the-executioner', 'inverted-jester', 'blade-dancer', 'crowned-king-awoken'])

// スプライトの画像形式。既定は png。WebP へ移したボスだけここに足す (1行で戻せる)。
// 出所 = docs/candidates/2026-08-04-sprite-webp-ticket.md
export const SPRITE_EXT_BY_BOSS: Record<string, 'png' | 'webp'> = {
  'the-executioner': 'webp',
  // 2026-08-05: 本編で戦う残り4体。合計 123MB -> 13MB。画質は111+107枚を実測して
  //   「抜いた縁は完全一致 (アルファ差 0.000) / 色の差は平均 2/255」= 等倍でも見分けがつかない。
  //   ⚠️ 'nameless-shade' は SPRITE_BOSSES 経路を通らない。render.ts の shadeImg() が別に引いている。
  'nameless-shade': 'webp',
  'dream-hound': 'webp',
  'vigil-knight': 'webp',
  'bride': 'webp',
  // 覚醒王 (2026-08-07 本番絵バッチ): 27枚で 32.9MB -> 4.0MB (1/8.2)。PNG のままだと
  //   8Mbps 回線で「20秒経っても絵が届かない」既知の症状 (刑吏で実測) をそのまま踏む。
  'crowned-king-awoken': 'webp',
}

// 元画の向き (どちらを向いて描かれているか) を boss ごとに登録。
// boss.facing と一致するなら flip しない、不一致なら水平反転する。
// (export は整合性テスト用。 未登録は 'right' フォールバック = スプライト差し替え時の入れ忘れに注意)
export const SOURCE_FACING: Record<string, 'left' | 'right'> = {
  'dream-hound': 'left',  // ハウンド差分シートは左向きで描画されている
  // bride: 初期値 left (王と同じ=右から来るプレイヤーへ向く)。後続タスクの実機検収で生成素材の
  //   向きに合わせて確定する。正面ポーズの反転是非もそこで確認。
  'bride': 'left',
  'vigil-knight': 'left',  // 騎士の生成3枚 (idle/active/defeat) は左向き (E-1)
  'former-princess': 'left',  // 姫の攻撃/撃破は左向き (E-2)
  // 🔴 2026-08-22 再是正: 'right' → **'left' へ戻した**。
  //   ~~2026-08-21「描き直した絵は全キー右向き」~~ は**事実と逆**だった (拡大して見た6枚のうち
  //   たまたま walk_passing と roar_pose が例外だったため、そちらを多数派と誤認した)。
  //   ユーザーが元絵 54枚を1枚ずつ判定した結果 = **48枚が左向き / 右向きは6枚だけ**
  //   (`walk_passing` / `turn` / `roar_pose` の3キー × 刃・砲)。判定表 = `facing.json` の `perKey`。
  //   → `walk_passing` と `roar_pose` の4枚は **描き直した** (刃・砲。反転だと刃が反対の腕へ移るため)。
  //   → `turn` の2枚は **元のまま**。向きも腕も正しい絵が作れなかったので、
  //     **向き替えの1拍そのものを出さない** ことにした (TURN_FLIP_BEAT_DISABLED)。
  //     変形 (morph) の中でだけ使う = ボスが実際に回っている最中なので逆向きで辻褄が合う。
  //   ⚠️ **絵を差し替えたら向きを判定し直す。** `tests/boss_sprite_facing.test.ts` が
  //     `facing.json` のハッシュと突き合わせて、判定していない絵が混ざったら落ちる。
  'the-executioner': 'left',
  'inverted-jester': 'right', // 鶏の絵 (rooster_*_1027) は右向き。道化の6枚は 10-29 に退避
  'blade-dancer':    'left', // 剣舞 = B の立ち絵 (2026-10-19・左向き)。旧 E-5 の右向きは DEV ?bladeArt=old (blade_dancer_art.ts)
  'crowned-king-awoken': 'left', // 覚醒王の3枚は左向き (E-6)
}

// ===== 見た目バリアント =====
// 請われぬ介錯は武器の腕が刃/腕砲へ組み変わるため、形態ごとに同名ポーズを分離する。
// ⚠️ 「右腕」と書かない (2026-08-22 裁定)。画面では反転が常態なので、どちらの腕かは元絵では決まらない。
//    台帳 = docs/decision-ledger.md 2026-08-22 行。
//   従来の「1ボス1ポーズ = 1ファイル」だと 4種が同じ overhead_raise.png を取り合って成立しない。
//   → ボスごとに *バリアント* を持てるようにし、絵は `/sprites/bosses/<id>/<variant>/<key>.png` から引く。
// ⚠️ 平置きパスへの自動フォールバックは 2026-08-20 に廃止（介錯の平置きは旧デザインで退避済み）。
//   無いコマは pickDrawableKey が直前の絵→最後に描けた絵→idle の順に代える。
//   介錯の blade/・cannon/ は実在するコマの一覧（src/sprite_manifest.ts）を持ち、一覧に無いコマは取りに行かない（10-14）。
//   絵を足したら一覧へ1行足す（tests/sprite_variant_manifest.test.ts が実物との一致を縛る）。
// 🔴 2026-08-18: フォルダ名・キーとも `maul` → `blade` へ改名 (票 = docs/candidates/2026-08-18-executioner-blade-27-install-ticket.md §8)。
const BOSS_SPRITE_VARIANTS: Record<string, readonly string[]> = {
  'the-executioner': ['blade', 'cannon'],
}

// ラボの表示条件は特定ボスIDでなく「見た目バリアントを持つか」を問う。
// 2体目が増えても、UI側へID分岐を足さずにここだけで追従できる。
export function bossHasSpriteVariants(bossId: string): boolean {
  return (BOSS_SPRITE_VARIANTS[bossId]?.length ?? 0) > 0
}

// いまこのボスが使うバリアント名。 バリアントを持たないボスは null (＝従来どおりの平置き)。
//   🔵2026-08-20: held が未設定のとき null を返していた = **平置き (旧デザイン) を引く穴**だった。
//     宣言してある最初のバリアントを既定にする (介錯 = 'blade')。
//     平置きの旧デザイン (フードの巨躯+大槌) は
//     public/sprites/bosses/the-executioner/_archived-2026-08-20-maul-olddesign/ へ退避済みなので、
//     ここが null へ落ちると絵が出ない (404) = 穴が見える形で壊れる。
//     出所 = docs/candidates/2026-08-20-executioner-olddesign-archive-ticket.md
export function bossSpriteVariant(boss: Boss): string | null {
  const variants = BOSS_SPRITE_VARIANTS[boss.id]
  if (!variants || variants.length === 0) return null
  return boss.exec?.held ?? variants[0]!
}

// キャッシュのキー。 バリアント無し = ボスIDそのもの (既存の挙動と一致)。
function cacheKeyOf(bossId: string, variant: string | null): string {
  return variant ? `${bossId}#${variant}` : bossId
}

const cache: Map<string, Map<SpriteKey, HTMLImageElement>> = new Map()
const loaded: Map<string, Map<SpriteKey, boolean>> = new Map()
// バリアント経路で最終的にロードした画像が「そのバリアント自身のファイル」かを本番でも記録する。
// false = variant/<key>.png が無く、平置き旧画像へフォールバックした。
// rectFor が実ロード元に合う基準高/POSE_SCALE_FIXを選ぶため、DEV限定にはできない。
const variantFileLoaded: Map<string, Map<SpriteKey, boolean>> = new Map()
// 実ファイルをロードできたか。DEVプロキシやバリアントの平置きフォールバックは false。
// ラボの「専用の絵があるか」は、描けるかではなくこの実ファイル有無を表示する。
const sourceFileLoaded: Map<string, Map<SpriteKey, boolean>> = new Map()
// 未ロードの要求キーを代替するときだけ使う。武器をまたいだ絵の混入を防ぐため variant 単位で保持する。
const lastDrawableKeyByVariant: Map<string, SpriteKey> = new Map()

// 描こうとしたキーと優先順の代替候補から、使える最初の1枚を選ぶ。
export function pickDrawableKey(
  wanted: SpriteKey,
  candidates: readonly (SpriteKey | null)[],
  isUsable: (key: SpriteKey) => boolean,
): SpriteKey | null {
  if (isUsable(wanted)) return wanted
  for (const candidate of candidates) {
    if (candidate !== null && isUsable(candidate)) return candidate
  }
  return null
}

// DEV限定の描画読み出し口。タイムラインから再計算せず、drawBossSprite が最終的に
// 採用したキーと実ロード元を、モーション確認ラボへそのまま渡す。
export type BossSpriteDrawProbe = {
  bossId: string
  key: string
  previousKey: string | null
  substitutedFrom: SpriteKey | null
  variant: string | null
  variantFileExists: boolean
  fellBackToFlat: boolean
  facing: 'left' | 'right'
}
let bossSpriteDrawProbe: BossSpriteDrawProbe | null = null
/** ボスの揺れを外から決める (null = 状態どおり)。一突きの版の撃破で render.ts が描く間だけ置く。 */
let bossShakeOverride: { x: number; y: number } | null = null
export function setBossShakeOverride(v: { x: number; y: number } | null): void { bossShakeOverride = v }

export function readBossSpriteDrawProbe(): BossSpriteDrawProbe | null {
  return import.meta.env.DEV ? bossSpriteDrawProbe : null
}

export type BossSpriteFileStatus = 'loading' | 'file' | 'fallback' | 'generated' | 'missing'

// 既存プリロードの結果だけを読む。追加のfetch/HEADは行わない。
// variantの実ファイルが無く平置きへ落ちた場合と、DEVプロキシを実ファイルから区別する。
export function readBossSpriteFileStatus(boss: Boss, key: SpriteKey): BossSpriteFileStatus {
  if (wallHuntGameSample(boss)?.art.file === `${key}.webp`) {
    return wallHuntImage(`${key}.webp`) ? 'file' : 'loading'
  }
  const variant = bossSpriteVariant(boss)
  const ck = cacheKeyOf(boss.id, variant)
  const img = cache.get(ck)?.get(key)
  if (!img) return 'missing'
  if (loaded.get(ck)?.get(key) === true) {
    if (sourceFileLoaded.get(ck)?.get(key) === true) return 'file'
    if (variant !== null && variantFileLoaded.get(ck)?.get(key) === false) return 'fallback'
    return 'generated'
  }
  return img.complete ? 'missing' : 'loading'
}

// スプライト内容の bbox + 面積メタ (サイズ一貫性のための面積正規化に使う)。 初回描画時に1度だけ算出しキャッシュ。
//   背景: キーポーズ画像は透過余白の割合がコマで違い、 旧「画像高さ→displayHeight」だと獣の画面サイズが
//   変動した。 中身の面積で「ピクセル/単位」を揃え、 bbox 下端を接地に固定することで一貫させる。
type SpriteMeta = { x0: number; y0: number; x1: number; y1: number; cx: number; w: number; h: number; area: number }

export type BossSpriteRect = { x: number; y: number; width: number; height: number }

export type BossSpritePlacement = {
  imageKey: string
  sourceSize?: { width: number; height: number }
  faceAnchorInArt?: BossFaceAnchor
  // drawImage が使う画像全体の矩形。Canvas変換より前の座標。
  imageRect: BossSpriteRect
  // alpha>16 の不透明bbox。新しい顔アンカー [0..1, 0..1] の基準矩形。
  opaqueRect: BossSpriteRect
  // 左右反転とタイムライン回転を本体と同じ順で再現するための最終変換。
  pivot: { x: number; y: number }
  facingMul: 1 | -1
  rotationRad: number
}

export type BossSpritePlacementInput = {
  imageKey: string
  imageWidth: number
  imageHeight: number
  opaqueBounds: Pick<SpriteMeta, 'x0' | 'y0' | 'x1' | 'y1'> | null
  pivotX: number
  pivotY: number
  scaleX: number
  scaleY: number
  offsetX: number
  offsetY: number
  facingMul: 1 | -1
  rotationRad: number
  // 接地点の比率 (2026-08-19・省略可)。不透明bboxの上端=0 / 下端=1 として「この高さを床に置く」。
  //   省略時は従来どおり bbox 下端 (=1) を床に置く。刃先や手が靴底より下へ出る絵で体が浮くのを、
  //   倍率ではなく位置で直すための口。出所 = docs/candidates/2026-08-18-blade-measure-ground-and-head-ticket.md §5
  groundAnchorRatio?: number
  // Optional authored boot-center in image pixels; sword extent must not shift the body.
  horizontalAnchorX?: number
}

// 突き当て A-1 (2026-09-22) の横の置き場所 (原画px)。刃が前へ長く伸びる絵なので bbox 中心だと体が後ろへずれる。
//   jab_coil = 両足の中点 1013 (09-22 腕の左右を直した版) を idle と同じ「足が中心より少し前」(182px) に置く。
//   jab_lunge = 後ろ足 (1665) が溜めの後ろ足と同じ床の位置に残るよう逆算 (前進 44px・offset −14→+20・倍率 0.1545px/原画px)。
//   = A-1「後ろ足を残して前足だけ踏み出す」。刃先の見た目は元の位置から約 209px 先 (当たり判定は 164px まで)。
//   10-28 の段の絵 (溜めと同じ背丈で描いた) は、溜めと同じく両足の外側の中点から +210 の所を置き場所にする。
const EXEC_JAB_LUNGE_ANCHOR_X: Record<string, number> = { jab_coil: 1195, jab_lunge: 944, jab_sink_1: 1162, jab_return_1: 1164, jab_return_2: 1095 }

// 立ち上がりの2枚の大きさの合わせ (2026-10-30・docs/research/2026-10-30-boss-rise-both.md)。
//   崩れ→rise1・rise2→構えを溶かすと、同じ姿勢のまま片方だけ大きく、輪郭が二重に見えた。溶け合う相手と一番重なる倍率。
const BOSS_RISE_SCALE: Record<string, { rise1?: number; rise2?: number }> = {
  'the-executioner': { rise1: 0.86 }, bride: { rise1: 0.92 }, 'vigil-knight': { rise2: 0.95 },
}

// 本体が drawImage に渡す矩形と、付随FXが読む不透明bboxを同時に返す唯一の入口。
// 画像・Canvas・乱数に触れない純関数なので、描画と顔FXの座標契約を単体テストできる。
export function resolveBossSpritePlacement(input: BossSpritePlacementInput): BossSpritePlacement {
  const bounds = input.opaqueBounds
  const anchorX = input.horizontalAnchorX ?? (bounds ? (bounds.x0 + bounds.x1) / 2 : input.imageWidth / 2)
  const ratio = input.groundAnchorRatio
  const anchorY = bounds
    ? (ratio !== undefined ? bounds.y0 + (bounds.y1 - bounds.y0) * ratio : bounds.y1)
    : input.imageHeight
  const imageRect: BossSpriteRect = {
    x: input.pivotX - anchorX * input.scaleX + input.offsetX,
    y: input.pivotY - anchorY * input.scaleY + input.offsetY,
    width: input.imageWidth * input.scaleX,
    height: input.imageHeight * input.scaleY,
  }
  const opaqueRect: BossSpriteRect = bounds
    ? {
        x: imageRect.x + bounds.x0 * input.scaleX,
        y: imageRect.y + bounds.y0 * input.scaleY,
        width: Math.max(1, bounds.x1 - bounds.x0) * input.scaleX,
        height: Math.max(1, bounds.y1 - bounds.y0) * input.scaleY,
      }
    : { ...imageRect }
  return {
    imageKey: input.imageKey,
    sourceSize: { width: input.imageWidth, height: input.imageHeight },
    imageRect,
    opaqueRect,
    pivot: { x: input.pivotX, y: input.pivotY },
    facingMul: input.facingMul,
    rotationRad: input.rotationRad,
  }
}

export function projectBossSpriteRelativePoint(
  placement: BossSpritePlacement,
  point: readonly [number, number],
): { x: number; y: number } {
  const localX = placement.opaqueRect.x + placement.opaqueRect.width * point[0]
  const localY = placement.opaqueRect.y + placement.opaqueRect.height * point[1]
  const dx = localX - placement.pivot.x
  const dy = localY - placement.pivot.y
  const cos = Math.cos(placement.rotationRad)
  const sin = Math.sin(placement.rotationRad)
  const rotatedX = dx * cos - dy * sin
  const rotatedY = dx * sin + dy * cos
  return {
    x: placement.pivot.x + rotatedX * placement.facingMul,
    y: placement.pivot.y + rotatedY,
  }
}
/** Project an authored source pixel through the actual drawImage placement (including flip/rotation). */
export function projectBossSpriteSourcePoint(
  placement: BossSpritePlacement, point: readonly [number, number],
): { x: number; y: number } | null {
  const size = placement.sourceSize
  if (!size) return null
  const x = placement.imageRect.x + placement.imageRect.width * point[0] / size.width
  const y = placement.imageRect.y + placement.imageRect.height * point[1] / size.height
  return projectBossSpriteRelativePoint(placement, [
    (x - placement.opaqueRect.x) / placement.opaqueRect.width,
    (y - placement.opaqueRect.y) / placement.opaqueRect.height,
  ])
}

const spriteMeta: Map<string, Map<SpriteKey, SpriteMeta>> = new Map()
let metaScratch: HTMLCanvasElement | null = null
// idle (面積 677k / bbox高 903 / canvas高 922) を従来サイズに保つよう校正した係数 (王=横広シルエット基準)。
const AREA_SCALE_K = 0.89
// ★per-boss 形状係数 (2026-06-13): 面積正規化 (sqrt(area)) は王のような「横広」シルエット基準。
//   騎士のように「縦長スリム」(槍持ち全身 = 面積は小さいのに背が高い) だと content_h が過大に膨張する
//   (騎士 idle は AREA_SCALE_K=0.89 だと content_h≈364px = 王 181px の 2.3 倍に膨張していた = 「でかすぎ」の主因)。
//   そのボスだけ K = sqrt(idle面積)/idle_bbox高 で上書きし、「idle の content_h = displayHeight」へ正規化する
//   (= displayHeight = PLAYER_DISPLAY_HEIGHT × BOSS_HEIGHT_RATIO が見た目の高さに直結)。
//   攻撃ポーズは引き続き面積比でスケール = ポーズ間のサイズ一貫性は保つ。
//   ※他の縦長ボス (処刑人/姫/道化/剣舞) も実装時に各 idle で同式の K を測って登録する。
const AREA_SCALE_K_BY_BOSS: Record<string, number> = {
  'vigil-knight': 0.387,  // sqrt(211211)/1188。 騎士 idle を displayHeight ちょうどに収める (211211 = 2026-09-27 切り抜き直し前の idle 面積。以後の差は KNIGHT_RECUT_AREA で打ち消す)
}
// ★per-boss 固定スケール (2026-06-14): 面積正規化 (sqrt(area)) は「面積が大きいポーズほど縮小」する副作用が
//   あり、 花嫁の veil_open(両翼全開=最大面積) が一番小さく表示されるバグになった。 これを opt-in で外し、
//   「idle の bbox 高さ = REF_H」を基準に全ポーズ同一 px/単位 で描く (= 翼開き=大きく/跪き=低く が素直に出る)。
//   base = displayHeight × fix / REF_H。 REF_H = idle の bbox 高さ。
//   ※理想はロード済 idle meta から実測キャッシュ (絵差し替えに追従)。 当面ハードコード (絵を差し替えたら更新)。
const BOSS_FIXED_SCALE_REF_H: Record<string, number> = {
  'bride': 1262,  // 花嫁 idle.png の bbox 高さ (sqrt(615513)/1262 の分母)。 絵差し替え時は要更新
  // 処刑人 バッチ1 (検収 2026-07-06): 面積正規化だと巨躯+ポーズ間の余白差でサイズが跳ね、主人公の
  //   約3倍で表示された → 花嫁方式の固定スケールへ (HEIGHT_RATIO 1.5 が素直に idle の見た目へ効く)。
  'the-executioner': 1408,  // 平置き idle.png (master v2) の bbox 高さ。絵差し替え時は要更新
  // 🔴 2026-08-18 新デザイン idle 実測: alpha>16 の blade/idle.png bbox 高さ (票 §6 = 刃27枚差し替え)。
  //   ~~新maul 再実測 (2026-07-31) 1170~~ は絵の入れ替え・改名 (maul→blade) で前提が消えた → 1342。
  'the-executioner#blade': 1342,
  // 腕砲 (2026-08-14): alpha>16 の cannon/idle.png bbox 高さ。#blade と同じ決め方 (花嫁方式)。
  //   🔴 ~~フェーズ1は maul と同じ画像を引くため同値 1170~~ は、専用絵22枚が入って前提が消えた。
  //   🔵 2026-08-19: 新デザイン (E案) の22枚へ差し替え。~~旧デザインの 1163~~ → 1354 (cannon/idle.webp の alpha>16 bbox 高さ・y1-y0)。
  'the-executioner#cannon': 1354,
  // 晒し籠 (2026-07-31): alpha>16 の yoke/idle.png bbox 高さ。#blade と同じ決め方 (花嫁方式)。
  //   結果として人物は実機 236.3px = 旧maul の 235.3px と 1px 差 (2026-07-31 当時の実測。刃へ改名後は未再検証)。
  'the-executioner#yoke': 1228,
  // 鋸 (2026-08-03): alpha>16 の wheel/idle.png bbox 高さ。#blade #yoke と同じ決め方 (花嫁方式)。
  //   人物の体格は旧maul・晒し籠と揃えてある (実測の肩幅 147〜178px / 旧maul 116〜131px)。
  //   ⚠️ POSE_SCALE_FIX['the-executioner#wheel'] は**意図的に作っていない**。既定=補正なし。
  //      倍率はユーザーが scale-review.html で実機を見て決める (背丈から機械計算する方式は🔴取消済)。
  'the-executioner#wheel': 1174,
  // 柄 (2026-08-06): alpha>16 の haft/idle.png bbox 高さ。#blade #yoke #wheel と同じ決め方 (花嫁方式)。
  //   武器が壊れた後の姿なので大きな塊が無く、bbox が人物そのもの = 他の3種より高く出る。
  //   人物の体格は旧maulと一致させてある (実測 頭幅50.2/肩幅120.2/肩÷頭2.40 ⇔ 旧maul/idle 50.2/117.6/2.34)。
  //   ⚠️ POSE_SCALE_FIX['the-executioner#haft'] は**意図的に作っていない**。既定=補正なし。
  //      倍率はユーザーが scale-review.html で実機を見て決める (背丈から機械計算する方式は🔴取消済)。
  'the-executioner#haft': 1323,
  // 覚醒王: 面積正規化は細脚の獣絵で過大化 (実測 ~2.5倍) → 固定スケールへ。 全ポーズ同一 px/単位が正
  //   (跳ねの高さ・伏せの低さが素直に出る)。
  //   🔴 2026-08-07: 本番絵 27枚へ差し替え。 ~~421 (DEVプロキシ idle の実測)~~ → 850。
  //   850 = 抽出後 idle.png の alpha>16 bbox 高さ (刑吏 #blade/#yoke/#wheel/#haft・花嫁と同じ決め方)。
  //   抽出 = scripts/awoken_extract.py (緑クロマキー)。 絵を差し替えたら同スクリプト再実行 → 再実測。
  'crowned-king-awoken': 850,
  // 剣舞 B の立ち絵 (2026-10-19): idle.png の alpha>16 bbox 高さ。6キーとも同じ絵なので大きさが揃う。
  'blade-dancer': 1437,
}

/** DEV の旧絵比べ用 (blade_dancer_art.ts)。null で表から外す = 面積で縮尺。 */
export function setBossFixedScaleRefH(bossId: string, h: number | null): void {
  if (h === null) delete BOSS_FIXED_SCALE_REF_H[bossId]
  else BOSS_FIXED_SCALE_REF_H[bossId] = h
}
// 面積正規化の残差 (ポーズで limb の重なり量が違う) を手で詰めるための per-key 補正。 既定=1 (無補正)。
// per-pose の拡大補正 (固定スケール= bbox 高さ基準だと「横広・縦低」の絵が小さく出る対策)。
//   キーは画像名 (SpriteKey 未登録のキーポーズ名も許可するため string)。 値=base への倍率。
const SIDE_CROUCH_SCALE = 0.90 // 頭の幅を idle にそろえる = 背丈は idle の約74%
const TRIPLE_1021_SCALE = 0.956 // 頭頂〜接地を flurry_swing_right (1362px) へ = 1362/1424
export const BRIDE_BRANCH_CUE_SCALE_FIX = {
  veil_link_snap_windup: 1.270, // C08 → veil_open
  blink_single_charge: 1.268,   // C02 → veil_open
  // 2026-07-19 監修裁定 (ユーザー指摘「C03だけ一回り大きい」の根治):
  //   検収シート実測の頭頂→足元は idle 493 / C08 502 / C02 502 に対し C03 533 (=idle比 +8%)。
  //   一方 頭幅は C08 53 / C02 52 / C03 51 で C03 が最小 → 頭身比 8.50 / 9.47 / 9.65 / 10.45。
  //   「小顔+長身」= 知覚上は一回り大きく読める。 接地ズレではない (頭直下の裾 605 vs 最下端 609)。
  //   1.161 × (502/533) = 1.0935 ≒ bbox基準 1.161 × (509/540) = 1.095 → 独立2法が一致。
  //   ★時間で 509→540 へ育てる案は不採用。 均一な拡大縮小は身体的な意味を持たず「画像のリサイズ」に
  //     読める (11%成長で一度失敗済)。 820ms 保持中の 6% 変化は他に動くものが無いぶん逆に目立つ。
  //     不連続は姿勢が激変する接触ハードカット (C03→flurry_swing_right 541px) 側へ寄せる。
  //   ⚠️代償: 接触の瞬間に約6%拡大する。 姿勢の激変に紛れる想定だが実機1回で要確認。
  //     見えた場合の次手は 1.12〜1.13 (520px前後) の中間値。
  //   ⚠️残件: C03 の頭は idle 比 -12% (縮小後 -17%)。 頭身比は倍率では直せない=絵側の課題。
  blink_triple_charge: 1.095,   // C03 → flurry_swing_right
  // 2026-10-21 手本合わせ (BRIDE_MOTION_TIMELINE_VARIANTS.a)。直後の接触絵と同じ頭頂・接地へ合わせて測った倍率。
  side_crouch: SIDE_CROUCH_SCALE,              // → veil_sweep_contact (返しの一閃・瞬き一打)
  blink_triple_charge_1021: TRIPLE_1021_SCALE, // → flurry_swing_right
} satisfies Partial<Record<SpriteKey, number>>

// 裾乱れの高速連は3枚を250ms間隔で巡回する。元絵の頭頂〜接地高が
// right=1366 / cross=1397 / left=1274px と揃っておらず、固定スケールでは
// 体が最大8.8%脈打つ。tool Aと同じ頭頂・接地基準の画素実測でhit0(right)へ揃えた倍率。
export const BRIDE_FLURRY_SWING_SCALE_FIX = {
  flurry_swing_right: 1.000,
  flurry_swing_cross: 0.978,
  flurry_swing_left: 1.072,
} satisfies Record<string, number>

// 🔵 2026-09-27 騎士の切り抜き直し (scripts/knight_recut_birefnet.py・ユーザー裁定「切り抜き直し」)。
//   旧抽出は体の内側の黒 (下着・墨線・脇の影) を穴にしていた → 呪いの赤いふちが穴ごとに縁取っていた。
//   塗り直すと不透明面積 (alpha>16) が増える。騎士は面積正規化 (base = displayHeight × K ÷ √面積) なので、
//   そのままでは各ポーズが 1〜7% 縮む。[旧面積, 新面積] を持ち、倍率 √(新÷旧) を掛けて **差し替え前と同じ px/単位** に保つ
//   (= ゲーム内の大きさは変えない)。K (AREA_SCALE_K_BY_BOSS) は旧 idle 面積 211211 のまま据え置く。
//   不透明 bbox は旧と1画素も変えていない (足元の位置も同じ)。
//   ⚠️ 絵を差し替えたら、そのキーの行を消すか測り直す (tests/knight_recut_area.test.ts が PNG の実面積と照合する)。
//   ⚠️ 戻す時 = C:/dev/LUNAIRE-notes/assets/2026-09-27-knight-recut-before/ の絵を戻し、この表を空にする。
//   同日 検収パック K-3「マントの裏として塗ってほしい」で体とマントの間の袋を裏地色で塗り、idle / parried / turn /
//   walk_passing / lanceGround_windup / lanceGround_contact の新面積を測り直した (旧面積はそのまま)。
export const KNIGHT_RECUT_AREA: Record<string, readonly [before: number, after: number]> = {
  idle: [211211, 225400],
  active: [233690, 248326],
  defeat: [388671, 433887],
  telegraph: [258882, 275633],
  parried: [290342, 314509],
  staggered: [340031, 388611],
  turn: [257822, 270833],
  walk_passing: [228658, 240757],
  walk_reach: [233650, 247227],
  lanceThrust_coilStart: [256479, 269526],
  lanceThrust_windup: [258882, 275633],
  lanceThrust_strike: [236063, 248723],
  lanceThrust_overextend: [217228, 226206],
  lanceThrust_recover: [302389, 320195],
  lanceThrust_full: [153175, 158378],
  lanceSweep_windup: [272309, 301432],
  lanceSweep_contact: [318366, 345142],
  lanceGround_windup: [156276, 169745],
  lanceGround_contact: [276166, 306842],
  lanceButt_windup: [359012, 375403],
  lanceButt_contact: [355824, 378058],
  lanceButt_slam: [187172, 192062],
  lanceDouble_gap: [317640, 342114],
  knightExecution_windup: [201095, 216260],
  knightExecution_impale: [268971, 288548],
}
function knightPoseScaleFix(): Record<string, number> {
  const out: Record<string, number> = Object.fromEntries(Object.entries(KNIGHT_SWEEP_ART).map(([key, art]) => [key, art.scaleFix]))
  for (const [key, [before, after]] of Object.entries(KNIGHT_RECUT_AREA)) out[key] = (out[key] ?? 1) * Math.sqrt(after / before)
  return out
}

const POSE_SCALE_FIX: Record<string, Record<string, number>> = {
  // 10-01 back hop: torso silhouette circle, excluding head/tail (diagnostic overlay in task checks).
  // Existing awoken pictures retain their uncorrected scale.
  'crowned-king-awoken': {
    // 10-02 5枚版: 見本GIF (spin_mock.py) の「胴＋たてがみの面積」揃え SIZE_FIX を掛けた値
    //   (wind 99/103・rear 1.0・sweep 1.17・front 1.125・settle 99/95)。さらに 10-02 夜、見た目の面積を wind に揃えるため rear×0.929・sweep×0.898・front×0.910（ユーザー指示）。settle は顔を描き直した 1002（10-04・案 C）で面積を待機に揃える（×0.9335）。AWOKEN_POCKET_SPIN_ART の anchorX もこの値から出す。
    pocket_spin_wind_1001: 0.8529354341292219,
    pocket_spin_rear_1002: 0.896315506672283,
    pocket_spin_sweep_1001: 0.7963903640440342,
    pocket_spin_front_1002: 0.8600154524152146,
    pocket_spin_settle_1001: 0.948307008465327,
    // 顔の描き直し（案 C）：体は 1001 と同寸法・同位置。不透明面積が 1001（＝待機と同面積）になるよう √(673626/695105) を掛けた。
    pocket_spin_settle_1002: 0.933540523341273,
    back_hop_crouch_1001: 0.850376,
    back_hop_air_1001: 1.020714,
    back_hop_land_1001: 0.773708,
    // 10-09 夜 ?devourJaw=1 の「床に頭を突っ込んで顎を閉じた絵」(floorbite_closed_1009)。frenzy_bite2_fangs_1006 を下地に、
    //   キャンバスへ余白を足して生成したので原画の大きさが違う。覚醒王は固定縮尺 (BOSS_FIXED_SCALE_REF_H 850) なので、
    //   体・脚・尾を重ね合わせて測った縮尺 0.8235 (この絵の1画素 = 旧の絵の 0.8235 画素) をそのまま倍率にする。
    //   これで旧の絵と同じ画面の大きさになり、後ろ足・前足の爪が旧と重なる (tests/awoken_devour.test.ts が実寸で照合)。
    floorbite_closed_1009: 0.8235,
    // 10-09 r2 ?awokenDevour=gladius の「踏み切りの途中」(pounce_launchmid_1009・新規生成を chroma_extract.py で抜いた絵)。
    //   原画の描き縮尺が隣の絵 (bite_windup_deep・bite_contact_up_0910) より約 9% 大きい。形の変わらない尾の先の渦巻きの高さ
    //   (隣の2枚 136〜137px・この絵 149px) と冠の高さで揃えた倍率 (tests/awoken_devour_pounce.test.ts が渦巻きの比を照合)。
    pounce_launchmid_1009: 0.92,
    // 10-27 DEV ?awokenNeckBack の首を引いた噛みの予備動作 (bite_windup_neckback_1027・新規生成を chroma_extract.py で抜いた絵)。
    //   原画の描き縮尺が大きい。待機・いまの bite_windup と足元を揃えて並べ、胴・後ろ脚・尾の渦巻きが bite_windup と同じ大きさに見える倍率
    //   (面積では待機比 0.95・bite_windup 比 0.84、爪の幅では 0.84〜1.03 とばらつくので並べて目で決めた)。
    bite_windup_neckback_1027: 0.85,
  },
  'vigil-knight': knightPoseScaleFix(),
  // 王 歩行2枚 (2026-08-11)。王は BOSS_FIXED_SCALE_REF_H に**登録が無い**= 面積正規化ボスなので、
  //   絵の不透明面積が変わるとスケールが動く (base = displayHeight × K ÷ √面積)。
  //   実測: idle 面積 677,797 (√823.3) に対し walk_passing 726,740 / walk_reach 552,554。
  //   値 = √(そのポーズの面積) ÷ √(idle の面積) = **idle と同じ px/単位へ固定する**倍率。
  //   これを入れないと、面積の小さい walk_reach が**拡大されて**上下動が打ち消される
  //   (面積正規化は「面積が小さいポーズほど大きく描く」ため)。
  //   🔴 「歩いて見える」の中身は**胴の高さではなく冠(頭)の動き方**だった (2026-08-11 実測)。
  //      1歩あたり 覚醒王 = 横4.5px/縦21.6px、この2枚 = 横1.8px/縦21.4px。
  //      四つ足の頭は上下に弾む。前後に滑ると不自然になる (旧版は横15.0px/縦6.3px で逆だった)。
  //      ⚠️ 差し替え時はこの2枚の**冠の位置**を測ること (tmp/crown.py と同じ測り方)。
  //         胴の bbox 高さだけ合わせても、頭が滑れば不自然なままになる。
  //   🔴 花嫁のように BOSS_FIXED_SCALE_REF_H へ引っ越すのは**採らない**。王は配布済みで、
  //      引っ越すと既存15枚**全部**の大きさが動く。ここは2キーだけに閉じた補正。
  //   ⚠️ 絵を差し替えたら面積が変わる = この2つの値も測り直す (scripts/hound_walk_extract.py が出す)。
  //   ⚠️ 頭の横位置は**絵でしか直せない**。描画は不透明 bbox の中心を boss.x に合わせるので、
  //      キャンバスへ透明の余白を足しても bbox は動かない (bbox は不透明画素から出る)。
  //      2枚で bbox 中心から見た冠の位置がズレていると、その差がそのまま頭の横滑りになる。
  //      現状のズレ = 11.1px (覚醒王 4.5px)。縦の弾み 28.0px が勝っているので許容。
  'dream-hound': {
    walk_passing: 1.000,
    walk_reach: 0.948,
    // 駆け抜けの専用の絵 (2026-10-11・DEV ?houndPassThrough=1)。Codex が待機の絵 (2048 幅の台紙の上) を手本に 1672 幅で描いたので、
    //   体の描き縮尺は待機の 0.816 倍 (台紙の比・冠と鎖の輪の大きさでも 0.75〜0.8 で一致)。
    //   値 = √(この絵の面積) ÷ (√(待機の面積 677797) × 0.816) = 待機と同じ「体の単位あたりの画面 px」に揃える倍率。
    //   絵を差し替えたら面積が変わる = 測り直す (そざい/boss-study/hound-pass-1011/sheet.py・tests/hound_pass_through.test.ts が PNG の面積と照合)。
    houndPass_runPush: 0.920,
    houndPass_runExtend: 0.962,
    houndPass_skid: 0.998,
    houndPass_turnLook: 1.007,
    houndPass_turnFront: 1.036,
    // 突進・跳びかかりの段の絵 (2026-10-28・DEV ?houndSmooth=1028)。面積をそろえた並べ見本で、冠の大きさが隣の待機・立ち上がりと
    //   そろう所 (目で ±5%)。起こす途中は冠がやや大きく出るので少し縮め、沈みは立てた尾の分だけ体が小さく出るのでほぼそのまま。
    houndSmooth_rise: 0.96,
    houndSmooth_sink: 0.98,
    // 道1の描き直し (2026-10-31・DEV ?houndRoar=a|b|c・?houndTripleArt=1031・src/hound_road1_1031.ts)。
    //   吠え: 面積をそろえると冠が待機より小さく出るので、並べた見本で冠がそろう所まで大きくした (目で ±5%)。
    //   三連: 新しい2枚は体が前後に長い。踵の位置と当たりの距離の両方に収まる大きさ (tests/hound_road1_1031.test.ts)。
    houndRoar_1031a: 1.1,
    houndRoar_1031b: 1.12,
    houndRoar_1031c: 1.05,
    houndTriple_swipeFar_1031: 0.91,
    houndTriple_slam_1031: 1.04,
    // 8コマの歩き (2026-11-04・DEV ?houndWalk=8・src/hound_walk8_1104.ts)。待機と同じ縮尺で描かれた絵 (体の幅 1495 対 待機 1509)
    //   = √(この絵の面積 ÷ 待機の面積 677797)。絵を差し替えたら測り直す (tests/hound_walk8_1104.test.ts が PNG の面積と照合)。
    houndWalk8_t000: 0.917,
    houndWalk8_t125: 0.946,
    houndWalk8_t250: 0.920,
    houndWalk8_t375: 0.910,
    houndWalk8_t500: 0.921,
    houndWalk8_t625: 0.928,
    houndWalk8_t750: 0.927,
    houndWalk8_t875: 0.913,
    // 16コマの歩き (2026-11-04・DEV ?houndWalk=16・src/hound_walk16_1104.ts)。同じ数え方 (tests/hound_walk16_1104.test.ts)。
    houndWalk16_t062: 0.903,
    houndWalk16_t188: 0.932,
    houndWalk16_t312: 0.915,
    houndWalk16_t375: 0.910,
    houndWalk16_t438: 0.896,
    houndWalk16_t562: 0.918,
    houndWalk16_t688: 0.921,
    houndWalk16_t812: 0.905,
    houndWalk16_t938: 0.912,
  },
  // 剣舞の技ごとの絵 (2026-10-19・DEV ?bladeMoves=1・src/blade_dancer_art.ts)。Codex が B の立ち絵 (idle) を見本に
  //   1536×1024 の台紙へ描いたので、体の描き縮尺が idle より小さい。値 = idle と同じ「体の単位あたりの画面 px」にする倍率。
  //   物差しは頭 (冠と面) の幅と胴 (首元→帯の結び目) の長さ。並べた見本で頭の大きさがそろう所に置いた (目で ±5% ほど)。
  //   弾かれ・崩れ・撃破は幹の名前 (<key>_1019) で引く = 配布版で idle を読んでいる時には掛からない。
  'blade-dancer': {
    waltz_ready: 1.45,
    waltz_cut1: 1.32,
    waltz_cut2: 1.42,
    waltz_cut2b: 1.4,
    pirouette_wind: 1.4,
    pirouette_turn: 1.32,
    pirouette_thrust: 1.4,
    hem_sweep: 1.25,
    hem_rise: 1.05,
    thrust_recover: 1.05,
    glide: 1.05,
    exec_slash: 1.35,
    swap_draw: 1.4,
    parried_1019: 1.35,
    staggered_1019: 1.15,
    defeat_1019: 1.15,
    // 動きを1から作った円舞 (2026-10-20・DEV ?bladeMotion=v2・src/blade_dancer_art.ts)。Codex が1枚の紙に4コマずつ描いた3枚から切り出した12コマ。
    //   紙の上の体の縮尺は12コマ共通 = 倍率も共通。物差しはスカートの幅と頭の大きさ (idle ÷ 2.4 の見本と並べてそろう)。
    m1020_waltz_1: 2.4,
    m1020_waltz_2: 2.4,
    m1020_waltz_3: 2.4,
    m1020_waltz_4: 2.4,
    m1020_waltz_5: 2.4,
    m1020_waltz_6: 2.4,
    m1020_waltz_7: 2.4,
    m1020_waltz_8: 2.4,
    m1020_waltz_9: 2.4,
    m1020_waltz_10: 2.4,
    m1020_waltz_11: 2.4,
    m1020_waltz_12: 2.4,
    // 溜めの3コマ (10-20 磨き・紙 waltz_windup.png・1段3コマ) は紙の上の体が大きいので 2.4 × 0.74。頂点 w3 を 1 に重ねて形が一番重なる倍率を測った。
    m1020_waltz_w1: 1.776,
    m1020_waltz_w2: 1.776,
    m1020_waltz_w3: 1.776,
    // 見得から立ち姿へ戻る2コマ (10-20 磨き・紙 waltz_settle.png の右2コマ)。立ち姿 idle に重ねて 0.56 = 溜めの紙と同じ 1.776。
    //   立ち姿は円舞の絵より暗い (色の平均で 0.84 倍) ので、s1 は半分・s2 は全部その暗さへ寄せてある (色がいきなり沈まない)。
    m1020_waltz_s1: 1.776,
    m1020_waltz_s2: 1.776,
    // 振り抜き 11 から見得 12 へ降りる途中の1コマ (10-20 磨き・紙 waltz_lower.png の真ん中)。紙の左右のコマを 11・12 に重ねて 0.80〜0.84 → 2.4 × 0.82。
    m1020_waltz_c1: 1.97,
    // 振りかぶり w3 から踏み込み 2 へ出る途中の1コマ (10-20 磨き・紙 waltz_launch.png の真ん中)。紙の左右を w3・2 に重ねると 1.74〜2.26 とぶれるので、頭と腰の大きさを 2 にそろえて目で 2.0。
    m1020_waltz_c2: 2.0,
    // 紙 waltz_turn.png の真ん中 (4 から 5 へ回り始める途中)。左右の 4・5 に重ねると 2.02〜2.30 とぶれるので、頭と腰の大きさを並べて目で 2.2 にした
    m1020_waltz_c3: 2.2,
    // 紙 waltz_rise.png の真ん中 (溜めの w1 から w2 へ剣を上げる途中)。左右を w1・w2 に重ねるとどちらも 0.98 倍 = 1.74
    m1020_waltz_c4: 1.74,
    // 紙 waltz_spin2.png の真ん中 (2発目の振り抜き 8 から二度目の回転 9 へ伸び上がる途中)。左右を 8・9 に重ねるとどちらも 0.96 倍 = 2.3
    m1020_waltz_c5: 2.3,
    // 紙 waltz_stand.png の真ん中 (戻りの s2 から立ち姿へ膝を伸ばす途中)。左を s2 に重ねると 0.94 倍 = 1.67、右を idle に重ねると 1.61。間を取って 1.64
    m1020_waltz_c6: 1.64,
    // 終舞の構え (10-19・紙 cres_aim.png の中と右)。紙の左のコマ (w2 と同じ姿) の高さ 820 を w2 の 975 にそろえて 1.776 × 975 ÷ 820
    m1020_waltz_a1: 2.11,
    m1020_waltz_a2: 2.11,
    // 終舞の誘い (10-20 4回目・紙 cres_invite.png の3コマ)。頭とスカートの大きさが溜めの w1・w2 と同じなので同じ 1.776
    m1020_waltz_b1: 1.776,
    m1020_waltz_b2: 1.776,
    m1020_waltz_b3: 1.776,
    // 連撃を1発ずつ描き直した 28コマ (10-20 5回目・10-21 から配布版の既定・紙 そざい/concept/blade-dancer-motion-1020v6/ の7枚 × 4コマ)。
    //   体の大きさ (細い所を除いた面積の平方根) の中央値が円舞の 1〜12 と +1.6% しか違わないので、全部同じ 2.4。
    m1020_v6_a1: 2.4,
    m1020_v6_a2: 2.4,
    m1020_v6_a3: 2.4,
    m1020_v6_a4: 2.4,
    m1020_v6_b1: 2.4,
    m1020_v6_b2: 2.4,
    m1020_v6_b3: 2.4,
    m1020_v6_b4: 2.4,
    m1020_v6_c1: 2.4,
    m1020_v6_c2: 2.4,
    m1020_v6_c3: 2.4,
    m1020_v6_c4: 2.4,
    m1020_v6_d1: 2.4,
    m1020_v6_d2: 2.4,
    m1020_v6_d3: 2.4,
    m1020_v6_d4: 2.4,
    m1020_v6_e1: 2.4,
    m1020_v6_e2: 2.4,
    m1020_v6_e3: 2.4,
    m1020_v6_e4: 2.4,
    m1020_v6_f1: 2.4,
    m1020_v6_f2: 2.4,
    m1020_v6_f3: 2.4,
    m1020_v6_f4: 2.4,
    m1020_v6_g1: 2.4,
    m1020_v6_g2: 2.4,
    m1020_v6_g3: 2.4,
    m1020_v6_g4: 2.4,
    // 10-22 円舞 v7 (?bladeWaltz=v7・周りは C の回り・決めは B の X 頭上)
    m1022_w7_a1: 2.4,
    m1022_w7_a2: 2.4,
    m1022_w7_a3: 2.4,
    m1022_w7_a4: 2.4,
    m1022_w7_b1: 2.4,
    m1022_w7_b2: 2.4,
    m1022_w7_b3: 2.4,
    m1022_w7_b4: 2.4,
    m1022_w7_c1: 2.4,
    m1022_w7_c2: 2.4,
    m1022_w7_c3: 2.4,
    m1022_w7_c4: 2.4,
  },
  // 花嫁の veil_open (全開) は横広で bbox 高さが低く、 高さ基準固定スケールだと縮む → 持ち上げ (要実機微調整)。
  // 2026-07-01: staggered (bbox 930 = idle 比 73.6%) を持ち上げて 88% に揃える (ユーザー指摘:
  //   致命入れた直後に staggered→idle 遷移で「ダウン→致命受け」 の絵が急に大きくなる違和感)。
  //   完全に揃える (1.36×) と「ダウンなのに巨大」 で不自然 → 中間 1.20× で「ダウンの低さは残しつつ差を縮める」。
  // defeat (bbox 1071 = 84.7%) も同じ理由で軽く持ち上げて 95% 相当に揃える (1.12×)。
  // 2026-07-02: prep 中割り 2枚 (invitation_prep bbox 1413 / exec_prep 1409) は idle (1262) より 12% 高く、
  //   前後の絵 (reach 0.93 / rise 1.06 / idle 1.0) と並べると crossfade 中に 5-17% サイズが跳ねる=カクッの主因。
  //   invitation_prep 0.85 → 1201 (≒ reach 1174 と idle 1262 の中間・両側の crossfade を滑らかに)。
  //   exec_prep 0.90 → 1268 (≒ idle 1262・prep は静止気配で idle と同サイズが自然)。
  //   embrace_open (0.887) → embrace_close (0.987) の +11% pop も同時に均す (open ×1.11 → 0.985)。
  //   veil_sweep_contact (0.814) は veil_open 1.3 補正後 (1.019) からの -20% shrink で打点カクッ = ×1.20 → 0.977。
  bride: {
    veil_open: 1.3,
    veil_sweep_contact: 1.20,
    embrace_open: 1.11,
    invitation_prep: 0.85,
    exec_prep: 0.90,
    // 2026-07-02: 前回 1.20 (0.884 レンダー) だと idle と背丈が同じに見えて「ダウン感が消えた」
    //   (ユーザー実機指摘)。 補正解除して bbox 比 0.736 のまま縮こまった姿勢に戻す。
    //   defeat も同じ理由で軽く抑える (1.12→1.00)。
    staggered: 1.0,
    defeat: 1.0,
    // 2026-07-11: 振りかぶり(slam_windup)は担ぎ上げたヴェールで bbox が縦長(1508=idle比1.2倍) →
    //   固定スケール(bbox高基準)だと体が下に小さく詰まる。 体を旋回コマと揃えるため拡大。
    //   ✅ 1.2 = tool A のガイド線 (正しい頭頂) にユーザーが実機で一致確認 (2026-07-11)。
    flurry_slam_windup: 1.2,
    ...BRIDE_FLURRY_SWING_SCALE_FIX,
    // 2026-07-19: v4分岐予告。直後の接触絵と同じ頭頂・接地へ合わせる。
    // rectFor は bbox 寸法の描画矩形へ元画像全体を入れるため、上下の透過余白ぶん
    // visible height = bboxH^2 / sourceH になる。実描画式まで含めて接触絵と一致させる。
    ...BRIDE_BRANCH_CUE_SCALE_FIX,
    // 10-23 描き直し (開発版 ?brideRedraw・src/bride_redraw_1023.ts)。頭のてっぺん→腰の黒い薔薇の長さを元の絵と同じ画面の長さにする倍率。
    //   キーはファイルの幹 (差し替え中だけ効く)。抱擁は新しいキーなので embrace_open と同じ体の大きさにした。
    embrace_hug_1023: 1.059,
    // 返しの一閃でヴェールを刀にして振り抜いた絵 (開発版 ?brideKaeshi=blade・src/bride_kaeshi_blade.ts)。頭→黒い薔薇を issen_sink_1023 と同じ長さに。
    kaeshi_cut_1023: 1.16,
    // 返しの一閃の間の絵 (開発版 ?brideKaeshi=tween)。体の高さを前後の絵 (side_crouch 0.90・veil_sweep_contact 1.20・veil_follow) の間に。
    kaeshi_tween_rise_1023: 0.90,
    kaeshi_tween_reach_1023: 1.0,
    kaeshi_tween_settle_1023: 1.0,
    flurry_swing_right_floor_1023: 0.926,
    flurry_swing_cross_floor_1023: 0.986,
    flurry_swing_left_floor_1023: 0.962,
    flurry_slam_windup_floor_1023: 1.118,
    exec_found_1023: 0.901,
    exec_glide_1023: 0.896,
    exec_coil_1023: 0.839,
    exec_okaeri_1023: 0.929,
    // 10-25 立ち姿を騎士の線で描き直した見本 (開発版 ?brideArt=a|b|c・src/bride_art_sample_1025.ts)。頭のてっぺん→腰の黒い薔薇を元の idle (613px) と同じ長さに。
    idle_art_1025_a: 0.869,
    idle_art_1025_b: 0.855,
    idle_art_1025_c: 0.858,
    // 10-25 いつも見える状態の絵を案A の線で描き直した絵 (開発版 ?brideArt=a)。立ち姿は頭のてっぺん→薔薇、崩れ・倒れは薔薇→床を元の絵と同じ長さに。
    active_art_1025_a: 0.978, parried_art_1025_a: 1.013, staggered_art_1025_a: 0.847, defeat_art_1025_a: 0.948,
    // 10-25 剣舞を案A の線で描き直した絵 (開発版 ?brideArt=a)。頭のてっぺん→薔薇 (叩きつけは薔薇→床) を元の絵と同じ長さに。
    flurry_charge_art_1025_a: 0.976,
    flurry_swing_right_art_1025_a: 0.904,
    // 10-26 右・交差・左は 250ms ごとに入れ替わるので、頭のてっぺん→床を右 (1492×0.904=1349) と同じに (元の決まり ±3%・sprites.test.ts)。薔薇合わせの 0.986・0.941 だと交差が 11% 跳ねた。
    flurry_swing_cross_art_1025_a: 0.891,
    flurry_swing_left_art_1025_a: 0.956,
    flurry_slam_windup_art_1025_a: 1.117,
    flurry_slam_art_1025_a: 0.988,
    // 10-25 技ごとの絵 (ヴェール・招待・茨の道・とどめ) を案A の線で描き直した絵 (開発版 ?brideArt=a)。頭のてっぺん→薔薇 (膝つき・前かがみは薔薇→床) を元の絵と同じ長さに。
    veil_coil_art_1025_a: 0.813, veil_twist_art_1025_a: 0.99, veil_sweep_contact_art_1025_a: 1.015,
    veil_follow_art_1025_a: 0.925, veil_open_art_1025_a: 1.228,
    invitation_prep_art_1025_a: 0.815, invitation_reach_art_1025_a: 0.885, invitation_full_art_1025_a: 0.908,
    aisle_rise_art_1025_a: 0.852, aisle_kneel_art_1025_a: 0.726,
    exec_found_art_1025_a: 0.926, exec_glide_art_1025_a: 0.896, exec_coil_art_1025_a: 0.839, exec_okaeri_art_1025_a: 0.929,
    exec_parried_snap_art_1025_a: 0.925, exec_cover_art_1025_a: 0.785,
    // 10-26 残りの技の絵 (返しの予告・三重の返礼の溜め・抱擁) を案A の線で。立ち姿は頭のてっぺん→裾の高さ、しゃがみは頭→薔薇を元の絵と同じに。
    side_crouch_art_1025_a: 0.90, blink_triple_charge_1021_art_1025_a: 0.97, embrace_open_art_1025_a: 0.993, embrace_close_art_1025_a: 0.85,
    // 10-27 返しの一閃の間の絵3枚と両腕で抱く絵 (案A)。頭のてっぺん→裾の高さを元の絵 (rise は ×0.90・抱く絵は ×1.059) と同じに。
    kaeshi_tween_rise_1023_art_1025_a: 0.85, kaeshi_tween_reach_1023_art_1025_a: 0.975,
    kaeshi_tween_settle_1023_art_1025_a: 0.953, embrace_hug_1023_art_1025_a: 1.002,
  },
  // 処刑人 (2026-07-06 バッチ2検収): 「槌を高く構える」系はソース絵の段階で体が縮んで描かれる
  //   (キャンバスに収めるため)。同一倍率ラインナップ (そざい/bosses/the-executioner/_check/
  //   scale_lineup_batch12.png) で実測した体格比から補正。ユーザー指摘 2026-07-06
  //   「縦に武器を構えると相対的に体が小さくなってる」の根治。
  //   ⚠️これは平置き用。新blade (旧maul) へは漏らさない。
  'the-executioner': {
    overhead_raise: 1.20,    // 体格 83% → 揃える
    overhead_repull: 1.12,   // 反り姿勢ぶんを残しつつ持ち上げ
    bigcharge_raise: 1.15,
    slam_windup: 1.08,
    uppercut_smear: 1.10,
    downswing_smear: 1.05,
    // 2026-07-06 身長2倍化に伴う全ポーズ総当たり視覚検収 (pose-lineup-2x) で追加:
    uppercut_contact: 1.18,  // 一番縮んでいた (idle 体格比 ~76%)。A2 掬い上げ頂点
    spin_contact: 1.10,
    spin_smear: 1.06,        // spin_contact と連続表示 = ペアで体格を揃える
    horizontal_slash: 1.10,
    dash_travel: 1.06,
    shove_contact: 1.05,
    // バッチ3: 槌を真上へ掲げるため生成原本の胴体が idle 比約74%へ縮んだ。
    // hood→足元の実測を idle の体格へ戻す。槌を含む全高は大きくなるのが意図どおり。
    exec_windup: 1.35,
  },
  // 🔴 廃止 (2026-07-31 夕): ~~処刑人 maul 全20キーの背丈から機械計算した倍率表~~。
  //   「頭のてっぺん〜足元」を全キーで揃える方式だったが、**屈んだ絵は頭が低いぶん
  //   「縮んでいる」と誤判定されて体ごと拡大される**。実測 dash_travel ×1.712 /
  //   slam_windup ×1.461 / slam_hold ×1.324 = 溜めと突進が巨人になっていた
  //   (ユーザー実機指摘・変更前後の並べ = docs/reports/2026-07-30-executioner-art-status.md)。
  //   🔴 この方式はこの1回しか使われていない。騎士は面積正規化、花嫁と 2026-07-06 の刑吏は
  //      「既定は補正なし・実機でおかしいキーだけ手で直す」。**そちらがこの作品のやり方。**
  //   ⚠️ 同じ失敗は花嫁で一度起きて、下の bride 欄 (2026-07-02) に「ダウン感が消えた」として
  //      記録済みだった。補正を外して解決している。次に触る人は先にそれを読むこと。
  //   ⚠️ 空オブジェクトのまま残すこと。キーごと消すと POSE_SCALE_FIX['the-executioner']
  //      (平置き=旧デザイン用) へ落ちて、別の絵のための補正が漏れる。
  //   背丈の実測値そのものは捨てていない = docs/reports/2026-07-30-executioner-art-status.md。
  //
  // 🔴 2026-08-18: 刃27枚差し替え + maul→blade 改名 (票 = docs/candidates/2026-08-18-executioner-blade-27-install-ticket.md §6)。
  //   下の 2026-08-01 表は**旧デザイン (maul) の絵に対して測った値**で、新しい刃27枚には当たらない
  //   (絵が変われば体格・余白比が変わるため)。**空 {} へリセットする**＝補正なしからやり直す。
  //   🔴 空オブジェクトのままにすること。キーごと消すと POSE_SCALE_FIX['the-executioner']
  //      (平置き=旧デザイン用) へ落ちて、別の絵のための補正が漏れる。
  //   🔴 ~~倍率は今回もユーザーが scale-review.html の刃タブで1枚ずつ目で決める~~ は 2026-08-18 裁定
  //      「大きさは目で合わせるのをやめ、測って決める」で前提が死んだ (docs/decision-ledger.md 2026-08-18 行)。
  //   ⚠️ 旧maul (2026-08-01確定) の値は戻せるようにここへ保存する:
  //     downswing_smear 1.08 / exec_contact 1.20 / exec_hold 1.20 / exec_windup 1.24 /
  //     horizontal_slash 1.10 / kick_windup 1.02 / set_place 0.95 / set_backstep 0.95 /
  //     overhead_hang 1.095 / overhead_raise 1.10 / overhead_recover 0.96 / overhead_repull 1.12 /
  //     roar_pose 1.04 / shove_contact 1.08 / slam_contact 1.02 / slam_windup 1.10 /
  //     staggered_enter 1.08 / turn 1.14 / walk_passing 0.98
  //
  // 🔵 2026-08-19 (夜間便・Fable 5): **測った値** = 頭の高さ (頭頂〜顎・姿勢で変わらない) を idle と揃える倍率。
  //   fix = headH(idle) / headH(key)。裏取り = 刃の一番広い所の幅 (fixBlade)。
  //   出どころ = scripts/blade_measure_marks.py + そざい/_inbox/2026-08-18-blade-measure/_marks.json (人が2周読んだ点)
  //   → _calc.json。票 = docs/candidates/2026-08-18-blade-measure-ground-and-head-ticket.md。
  //   採用の規則: 既定は頭。**頭が伏せる/仰ぐ絵は頭頂〜顎が伸び縮みして使えないので刃**を採る (コメントに「刃」)。
  //   両方壊れている絵 (roar_pose = 頭は上を向き・刃は腕が手前へ伸びて広がる) は補正なし 1.00 で入れて朝の判断へ。
  //   ⚠️ 頭と刃が 5% 以上食い違うキーは括弧に両方の値を残す = 描き崩れの候補 (どちらが正しいかはユーザーが決める)。
  //   ⚠️ 2026-08-19 の値は**まだユーザーの実機検収前**。実プレイで気になったキーはここを直す (機械計算へ戻す話ではない
  //      = 背丈からの計算ではなく頭の高さ。屈んだ絵が巨大化する旧方式の失敗とは別物)。
  //
  // 🔴 2026-08-20 **基準が変わった (Fable 5 裁定・ユーザー指名)**。~~技の最中のコマも頭の高さで揃える~~ は廃止。
  //   きっかけ = ユーザー実機「絵の大小が気になって“ため”の時間に注目できない。姿勢を低くしている絵の時に
  //   小さくなっている印象」。測って出たこと:
  //     ・頭は全キー 124px で完璧に揃っていた。それでも「小さい」と読まれた
  //       = **解剖学的な正しさは、コマ送りの知覚では大きさとして読まれない**
  //     ・目が追っているのは**シルエットの量 (体+武器の塗り面積)**。頭で揃えると屈み姿勢の絵は量が半減する
  //       (overhead_contact 54% / set_place 59%)。同じ技の中で量が **2.10倍** 動いていた
  //     ・**裏取り** = ユーザーが実機で自分で決めた唯一の値 exec_contact 1.066 は、頭を揃えた値 (1.138) ではなく
  //       **量 112%** の位置だった。ユーザーの目は量で合わせている
  //   → **屈み系のキーは「量 84〜101% / 背丈 idle 比 75〜80%」の帯へ乗せる**。量のばらつき 2.10倍 → 1.35倍。
  //   🔴 **「頭を揃える」計器でここを再調整しないこと。それが犯人だった。**
  //   🔴 **overhead_raise を下げないこと** = 溜めが大きいのは「問題ない」とユーザーが逐語で言明している。
  //   🔴 **POSE_GROUND_ANCHOR は無罪**。倍率を変えても接地は保たれる。触らない。
  //   出所 = Fable 5 の裁定 (2026-08-20)。根拠シート = _確認用/68_fable_scale/sheet_chain_now_vs_new.png ほか。
  //   分担の規則 = docs/ai/model-routing.md §0-1c (見た目の裁定は Fable。Opus は測るところまで)。
  'the-executioner#blade': {
    // 🔵 2026-08-25 夜 変形の3コマ (刃側 = **剣 → 砲**)。倍率の決め方 = **人物の背丈 (頭頂→靴底) が idle と揃う値**
    //   = idle の人物高 ÷ その絵の人物高。姿勢の深さを画面へ出す step_* とは決め方が違う (あちらは屈む絵)。
    //   🔴 頭の位置は「暖色 (髪・面) の最上行」で測る。**bbox の上端や『幅のある最上行』で測ると、
    //      振り上げた武器を頭と読み違える** (morph_a で実際に 1割ずれた)。鋼は寒色なので暖色では拾われない。
    //   刃 idle の人物高 = 1333px。実測 = Claude 2026-08-25。
    //   出所 = docs/candidates/2026-08-25-executioner-morph-enchant-ticket.md
    morph_a: 0.9859,           // 人物高 1352px (振り上げ)
    morph_b: 1.0076,           // 人物高 1323px (振り抜き)
    morph_c: 1.0038,           // 人物高 1328px (収まり)
    // 🔴 2026-09-02 夜 実プレイ NG で戻した: ~~1.05 (量 87%・地割りと同じ帯へ)~~ は「突進の瞬間だけ大きく見える」(検収パック B-2)。
    //   量 70% でも前傾の走りは小さく読まれていなかった＝屈み系でも「走り」は帯の外でよい (外した予想として記録)。
    dash_travel: 0.94,         // 頭 (頭 0.939 / 刃 1.252 = 刃が細く見える)
    defeat: 0.96,              // 刃 (頭 0.689 = 深く伏せる / 刃 0.959)
    // 🔵 2026-08-20 Fable 5 裁定: ~~0.93~~ → 0.99。振りの1コマ。量 84→95% で raise(114%)→contact の谷を埋める
    downswing_smear: 0.99,
    // 🔵 2026-08-19 夜: 弧の中割り2枚 (新規発注)。 頭の高さ (頂点→顎の下端) を実測して idle=124px と揃えた。
    //   downswing_mid 147px → 124/147 = 0.844 / downswing_trail 151px → 124/151 = 0.821。
    //   どちらも人物が idle より 2割ほど大きく描かれている (弧の絵なので画角が寄った)。
    //   出所 = docs/candidates/2026-08-19-executioner-arc-and-followthrough-5-sprites-order.md
    downswing_mid: 0.844,
    // 🔵 2026-08-28 エフェクト除去版へ差し替え (blade/_archived-2026-08-28-effect-baked/)。値は**据え置き**:
    //   bbox は 1077→1004px と縮んだが、差は消した弧と刃の角度の分。人物の背丈は 950→960px (±1%) で
    //   不変を等倍目視で確認 (_確認用/168_body_check_downswing_trail.png)。bbox 比で 0.8807 にすると人物が 6% 膨らむ。
    downswing_trail: 0.821,
    // 🔵 2026-08-19 夕: ~~0.84 (刃の計器)~~ → 1.066。 打点だけ頭が 26% 縮み、実機で「当たる瞬間に小さくなる」
    //   とユーザーが報告した (0.57秒 = この技で一番長く映る絵)。 実機のツマミで4候補を撮って画素で確定:
    //   1.066 で 直前コマとの刃長差 0% / 頭 -26%→-6% / recover への跳び +53%→+20%。
    //   出所 = docs/reports/2026-08-19-executioner-exec-motion-after.md
    exec_contact: 1.066,       // 実機実測 (頭 1.138 と 刃 0.842 が 35% 食い違う唯一級のキー = 計器で決めない)
    // 2026-10-09 採用 (配布版の刃の打点 = exec_contact_v2 = 前足のブーツが見える打点。旧 exec_contact は DEV ?execFinisherFoot=old|foot で読む)。1356x1160。旧 exec_contact との重ね合わせ (後ろ足の靴で揃え・輪郭の一致 95%) で絵の大きさが 1.1414 倍 → 1.066/1.1414。
    exec_contact_v2: 0.934,
    // 2026-10-20 致命「執行」の据え斬り (Codex 描画・そざい/bosses/the-executioner/2026-10-20-finisher-sue/)。
    //   立ち姿 idle と同じ床に並べ、頭の大きさと刃の長さで揃えた (sue_raise は描いた人物が小さい・刃は Claude が縦1.45倍に伸ばした)。
    sue_raise: 1.2,
    sue_contact: 0.92,
    // 2026-10-21 致命を弾かれた絵 (src/exec_parry_recoil.ts)。頭と靴の大きさを idle・sue_contact と並べてそろえた。
    exec_parried_1021: 1.05,
    sue_flick: 1.08,
    exec_hold: 1.08,           // 頭 (頭 1.078 / 刃 1.153)
    exec_windup: 1.30,         // 頭 (刃 1.342 と一致)
    grabbed_hold: 0.95,        // 頭 (頭 0.947 / 刃 1.166 = 刃が細い)
    // 🔵 2026-08-19 夜: ~~1.00~~ → 1.042。 絵そのものを描き直した (候補3 = ユーザー選定) ので、
    //   旧い絵に対して測った値は死んでいる。 新しい絵の頭の高さ 119px → 124/119 = 1.042。
    // 🔵 2026-08-28 エフェクト除去版へ差し替え: ~~1.042~~ → 1.016 (bbox 1090→1118px = 人物 2.6% 大きめ。
    //   画面上の大きさを据え置く比。誤差帯 ≤2.6% なので bbox 比をそのまま採用)。
    horizontal_slash: 1.016,
    // 咆哮の振り抜き (新規)。 頭 104px = 人物が idle より2割小さく描かれている → 124/104 = 1.192。
    // 🔵 2026-08-28 エフェクト除去版へ差し替え: ~~1.192~~ → 1.138 (人物の頭頂 990→1035px = 4.7% 大きく
    //   描かれた。等倍目視 _確認用/168_body_check_slash_followthrough.png。画面上の大きさは変えない)。
    // 🔵 2026-08-29: ~~1.138~~ → 1.16。B-3 と同じ持ち上げ (背丈 91→93%)。
    slash_followthrough: 1.16,
    kick_windup: 1.00,         // 頭 (頭 1.0 / 刃 1.146)
    // 🔵 2026-08-20 Fable 5 裁定: ~~0.77~~ → 1.05。この技で一番長く映る打点。
    //   量 54→101% (ユーザーが実機で自分で決めた exec_contact 1.066 = 量112% と同じ帯)。背丈は idle の 75%。
    //   ⚠️ 引き換えに、絵の中の頭が紙の上で +36% になる (124→169px)。伏せた頭巾頭なので等倍では破綻しない、
    //     というのが Fable の判定 (シート = _確認用/68_fable_scale/zoom_contact_a.png・b.png)。
    //     実機で頭が気になったら 0.95 へ落とす (ただし recover への戻りで +15% の段差が復活する)。
    overhead_contact: 1.05,
    overhead_hang: 1.46,       // 頭 (頭 1.459 / 刃 1.66) ⚠️ 体が 9頭身しかない絵 (idle 10.8) = 頭を合わせると背が 83% になる。現行タイムライン未使用
    overhead_raise: 1.18,      // 頭 (刃 1.229 と一致)。据え置き = 溜めが大きいのは「問題ない」(ユーザー逐語 2026-08-20)
    // 2026-10-22 振り下ろしの着弾後の沈みの絵 (?execTwoKeys=1・docs/research/2026-10-22-exec-settle-redo.md)。
    //   当たった絵 overhead_contact (1.05) と刃・体の幅を画面上でそろえた (絵のキャンバスが 1.1208 倍なので 1.05/1.1208)。
    exec_key_settle_1022: 0.937,
    // 🔵 2026-08-20 Fable 5 裁定: ~~0.97~~ → 1.00。「大きい所から通常へ戻る」段差の最後の1段をならす (量 91→96%)
    overhead_recover: 1.00,
    overhead_repull: 1.15,     // 頭 (頭 1.148 / 刃 1.955 = 刃が縁寄りで細い)
    // 🔵 2026-08-22 絵を描き直したので再計算: 1.00 ÷ √(604274/566124) = 0.968 (見えている量を保つ)
    roar_pose: 0.968,
    // 🔵 2026-08-20 Fable 5 裁定: ~~0.99~~ → 1.05。量 75→85%。頭は +6% しか増えないので安い
    set_backstep: 1.05,
    // 🔵 2026-08-27: ひねり薙ぎ (exec_maulSet) の巻きの構え。補正なしだと **量 77% / 背丈 78%** で、
    //   08-20 に合格した振り下ろしの帯 (量 84〜101%・谷は 95%) より一段深く沈んでいた
    //   = 技の入りでボスが一段小さくなる。1.13 で量 98% / 背丈 88%。
    //   ⚠️ 基準は 08-20 の裁定どおり **量 (体+武器の塗り面積)**。頭の計器では決めていない
    //      (2026-08-27 に頭の計器を校正したら idle=124 の所を 70 と返した = 壊れている)。
    //   ツマミで比べる: __poseFix['sweep_coil'] = 1.00 (現状) / 1.13 (量98%) / 1.22 (量114% = 振り下ろしの溜めと同じ)
    // 🔵 2026-08-29: ~~1.13~~ → 1.19。実プレイ (08-28 パック B-3)「溜めと攻撃で小さい」→ 背丈 88→93% へ
    //   系列ごと持ち上げ (検収パック 2026-08-28 の B-3 調査追記が正本)。
    sweep_coil: 1.19,
    // 🔵 2026-08-27: ひねり薙ぎの斬りの瞬間。補正なしだと **背丈 73%** で、前後のコマ
    //   (sweep_coil 88% / slash_followthrough 91%) より一段沈む = 当たる瞬間にボスが縮む。
    //   1.22 で 89%。連なりは idle 100 → 88 → 89 → 91 → 100 で、最大の段差 9pt。
    //   🔴 武器の大きさが変わる絵なので「量」ではなく**背丈**で決めた（量には刃の面積が混ざる）。
    //   ツマミで比べる: __poseFix['sweep_slash'] = 1.00 / 1.22
    //   🔵 2026-08-28 エフェクト除去版へ差し替え (白線の軌跡を除去)。bbox 979px 不変 = 値は据え置き。
    // 🔵 2026-08-29: ~~1.22~~ → 1.28。B-3 と同じ持ち上げ (背丈 89→93%)。
    // 🔵 2026-09-02 見た目の棚卸し §B4 (docs/reports/2026-09-02-executioner-visual-audit.md): 09-01 便の a (刃が先へ細る版・ユーザー確定済み)
    //   へ差し替え。旧 (板のように太い刃) は _archived-2026-09-02-sweep-slash-thick/。新しい絵は人物の頭が小さめに描かれている
    //   (1.28 のままだと頭 idle 比 86%・量 114%) ので、頭 91% / 量 129% / 刃 134px の中間 1.36 に置く。~~1.28~~
    sweep_slash: 1.36,
    // 🔵 2026-08-27: 斬り上げ (exec_maulHold) の2枚は **測ったうえで補正なし (1.00) のまま**。
    //   scoop_drag = 量 87% / 背丈 82%、scoop_smear = 量 77% / 背丈 84%。
    //   🔴 実機で報告された「武器の大きさが変わる」の原因は倍率ではなかった = **scoop_smear の絵で刃が
    //      1674px と、他の4枚 (idle 1103 / set_backstep 1066 / scoop_drag 1062 / sweep_coil 1072) より
    //      57% 長く描かれていた**。倍率で刃を揃えると人物が背丈 52% の別人になるので直せない。
    //      → **絵を1枚描き直して差し替えた** (2026-08-27 ユーザー裁定。刃 1674 → 1403px = 隣のコマとの
    //      差が +58% → +32%。旧い絵は blade/_archived-2026-08-27-scoop-smear-longblade/)。
    //   🔴 **この絵の大きさを「量」で決めないこと。** 量は 91% → 77% へ落ちたが、**減ったのは刃の分だけ**で
    //      人物の背丈は 82% → 84% とほぼ動いていない。**武器の大きさが変わる絵では、量は人物の大きさを
    //      測れない** (刃の面積が混ざる)。ここは背丈で見て、補正なしが正しい。
    //   🔵 2026-08-28 scoop_smear はエフェクト除去版へ再差し替え (弧を除去・判定シートC OK)。
    //      bbox 1324px 不変 = 引き続き補正なし (旧 = blade/_archived-2026-08-28-effect-baked/)。
    // 🔵 2026-08-29: 実プレイ (08-28 パック B-3)「斬り上げも小さい」→ ~~補正なし~~ を改め、
    //   scoop_drag は背丈 82→92% へ持ち上げ。scoop_smear は刃が 27% 長い板挟み (体を上げると刃が伸びる)
    //   のため**折衷 1.06 の仮置き** (背丈 84→89%)。本決着は刃 ≤1150px での描き直し (保留・ユーザー判断)。
    scoop_drag: 1.12,
    // 🔵 2026-09-02 見た目の棚卸し §B2 (docs/reports/2026-09-02-executioner-visual-audit.md): 刃が idle の 2/3 しか無い絵を
    //   Codex で「同じ姿勢のまま刃だけ idle と同じ長さ」に描き直した a へ差し替え (旧 = _archived-2026-09-02-scoop-smear-shortblade/)。
    //   絵の人物は旧と同じ枠で idle より小さめ (1.06 のままだと頭 idle 比 83%・前後の scoop_lift/through は 89〜96%) →
    //   1.20 で頭 93%・刃 約134px (idle 151 の 89%)。~~1.06~~
    scoop_smear: 1.20,
    // 🔵 2026-09-04 据えの突きの専用絵 (Codex imagegen・そざい/_inbox/2026-09-04-executioner-pierce/)。
    //   物差し＝刃の幅 (硬い板・姿勢で変わらない。scratchpad の計測: idle 77px / draw_a 69 / thrust_a 49 / draw_b 69.5 / thrust_b 61)。
    //   ⚠️ 横向きの刃は陰影で鋼の連続が切れ、計器が細めに出る (sweep_slash で raw 1.93 に対し採用 1.36)。
    //   突きは深く踏み込む絵なので背丈 (thrust_a 749px / idle 1342) で揃えると別人になる＝背丈では決めない。
    //   → 刃の幅の比を上限、背丈×ランジの深さ (≈0.72) を下限として、その間に置いた仮値。実機/ラボで詰める。
    //   ツマミで比べる: __poseFix['pierce_draw'] / __poseFix['pierce_thrust'] (候補 b は *_b)。
    pierce_draw: 1.10,
    pierce_thrust: 1.45,
    pierce_draw_b: 1.10,
    pierce_thrust_b: 1.25,
    // 2026-09-11 発勁専用絵。同じ頭・刃の尺度で表示し、屈みを背丈で拡大しない。
    fajin_draw: 1.20,
    fajin_thrust: 1.20,
    fajin_settle: 1.20,
    fajin_coil: 1.20,
    fajin_release: 1.20,
    // 2026-09-22 突き当て A-1 の本番絵 (Codex imagegen・experiments/executioner-jab-lunge-art-0922/)。
    //   物差し＝頭の高さ (髪の頂〜顎: idle 140px / coil 128 / lunge 130) → 1.08 で idle と同じ頭。
//   coil は 09-22 に腕の左右を直した版 (刃 5.7頭・伸ばしなし)。旧版は experiments/…/jab_coil.png。
    //   lunge は刃が頭4.6個分と短く出たので、刃の帯だけ横に 1.31 倍して idle と同じ頭6個分へ揃えてある。
    //   🔵 2026-10-23 検収 kensyu-exec-quake-size-1023 Q-2「中くらい」: 1.08 → 1.20 (頭と刃の長さの間)。
    //   1.08 は頭はそろうが刃が他のコマより 17% 短く、黒い体も細く見えて飛んでいた。旧は DEV ?execJabSize=old (src/exec_jab_size.ts)。
    jab_coil: 1.2,
    jab_lunge: 1.2,
    // 2026-10-28 段の絵 (src/exec_jab_steps_1028.ts) は溜めと同じ背丈・同じ画布で描かせたので同じ倍率。
    jab_sink_1: 1.2,
    jab_return_1: 1.2,
    jab_return_2: 1.2,
    ...Object.fromEntries(Object.entries(FAJIN_DOWNCUT_ART).map(([key, art]) => [key, art.scale])),
    // 🔵 2026-09-02 斬り上げの5キー化 (ChatGPT 壁打ちの5枚・1086x1448 で同一縮尺)。
    //   立ち姿相当 (scoop_through の頭頂→靴底 ≈1020px) を idle の背丈 (bbox 1342px) に揃える = 1.32。
    //   同じ便の絵は同じ倍率にする (コマ間で人物の大きさが跳ねないことを刃の長さより優先・08-27 の教訓)。
    //   結果 = 踏み込み3枚 (touch/lag/lift・bbox 895〜924px) は 88〜91% = 溜め系の帯 (92〜93%) の下端。
    //   🟨 仮値 (ラボ・実プレイで詰める)。刃の長さの計器は較正が通らないので使っていない (handoff 09-02)。
    //   scoop_twist だけは Codex の描き直し (別便・人物が大きい: bbox 1186px) なので 1342/1186 = 1.13。
    scoop_twist: 1.13,
    scoop_touch: 1.32,
    scoop_lag: 1.32,
    scoop_lift: 1.32,
    scoop_through: 1.32,
    // 🔵 2026-08-20 Fable 5 裁定: ~~0.84~~ → 1.00。構えの膝立ち。刃の板が idle と同じ大きさに戻り、
    //   背丈 75% / 量 84% で「低いが縮んでいない」に読める
    set_place: 1.00,
    // 2026-09-11: 刃の幅を戻した候補Aをユーザー採用。ラボで比較した倍率を反映。
    // 頭頂〜靴底を旧絵と揃える (0.893 * 1212 / 1202)。旧絵と戻し方は _archived-2026-09-11-shove-thin/。
    shove_contact: 0.9004292845257904,
    // 🔵 2026-09-02 見た目の棚卸し (docs/reports/2026-09-02-executioner-visual-audit.md §A1):
    //   地割り (exec_maulQuake) の3枚だけが 08-20 の「量 84〜101%」の帯の外に残っていた
    //   (量 49 / 71 / 74%・頭の大きさも idle の約8割)。実舞台で「しゃがむと別人のように小さい」→ 帯へ乗せる。
    //   ~~slam_contact 0.74 / slam_hold 0.92 / slam_windup 0.91~~ (頭の高さで揃えた 08-19 の値)。
    //   新値 = 量 84 / 96 / 88% (頭の大きさ idle 比 103 / 96 / 95%)。POSE_GROUND_ANCHOR は不変 (接地は保たれる)。
    slam_contact: 0.98,
    // しゃがみ＝量 90% に乗せる値。slam_contact 0.98 の隣に置く
    quake_contact: 0.90,
    slam_hold: 1.03,
    slam_windup: 1.00,
    staggered_enter: 0.93,     // 刃 (頭 0.838 = 深く伏せる / 刃 0.925)
    staggered_hold: 0.95,      // 刃 (頭 0.844 = 伏せる / 刃 0.952)
    turn: 1.10,                // 頭 (頭 1.097 / 刃 1.601 = 背面から刃が細い)
    // 🔵 2026-08-22 絵を描き直したので再計算: 0.95 ÷ √(675692/445287) = 0.771
    //   新しい絵は人物が2割大きく描かれている (画角が寄った) ため、そのままだと歩きだけ巨大化する。
    // 🔵 2026-08-23 夕: 絵を「上半身が動く」版へ描き直したので再計算。前傾した分だけ靴底→頭が縮む
    //   (1537→1463px) = **これが歩きの沈み**。足が揃う絵 (walk_cross) の 95% になる値を入れる。
    walk_passing: 0.7695,
    // walk_reach = 4コマの並びから外したキー (値は 2026-08-22 のまま = 旧 walk_passing の頭の 97%)。
    walk_reach: 0.760,
    // 🔵 2026-08-23: 歩きの4コマは**同じ頭の高さへ揃える**。絵ごとの高さの差で上下動を作ると
    //   ①全身が膨らむ ②絵が切り替わる瞬間 (210msに1回) にしか動かない、の2つが起きた
    //   (ユーザー実機「ちょこちょこ走り。体が動いてなくて足だけ」)。
    //   上下動は WALK_BODY_BOB_BY_BOSS が**移動距離の連続関数**として作る。
    walk_cross: 0.767,            // 4コマで**一番高い**コマ (基準)
    walk_contact_b: 0.7851,       // 同上 = walk_cross の 95%
    // 🔵 2026-08-23 夕: ステップ v2 (前ステップ型) の専用絵。**発注した姿勢の深さ (画面の靴底→頭 = 土台の何%) を
    //   そのまま画面に出す値** = 目標高 × idle bbox ÷ 絵の bbox (scripts/executioner_step_scale.py の注記・生成が
    //   人物を 8〜10% 縮めて描いたので「量」で揃えると沈みが消える 97.6% になった = 採らず)。土台 = idle / dash_travel。
    step_crouch: 1.053,           // 目標 = idle の 92% (沈み)
    step_smear: 0.974,            // 目標 = dash_travel (画面 67.2%) の 90% = idle の 60.5% (低く滑る)
    step_land: 1.066,             // 目標 = idle の 96% (着地の膝の沈み)
    step_settle: 0.984,           // 外套のはためき (src/exec_cloak.ts・2026-11-02): 背丈 1336px → 画面で idle の 98%
    // 外套の流れ (?execCloak=flow・2026-11-03): 着地 96% → 舞い上がり 97% → 翻り 98% → 落ち 99% → 垂れかけ 99.5% → idle と背丈をつなぐ。
    step_cloak_lift: 0.992,       // 背丈 1310px (布が頭より上へ出るので bbox でなく頭のてっぺん〜靴底)
    step_cloak_fall: 0.990,       // 背丈 1338px
    step_cloak_drop: 0.997,       // 背丈 1337px
    // idle は 1.00 (基準そのもの)。
  },
  // 🔴 廃止 (2026-07-31 夕): ~~晒し籠 全25キーの背丈から機械計算した倍率表~~。
  //   上の #blade (旧#maul) と同じ方式・同じ理由で取り消し。shove_contact ×1.693 / defeat ×2.729 /
  //   slam_windup ×1.614 が実機で巨大化していた。**機械計算へ戻さないこと。**
  //
  // ✅ 現行 (2026-08-01): **ユーザーが1枚ずつ目で決めた値**。検収ページ (scale-review.html) で
  //   idle を薄く重ねて「体の大きさが合うまで」合わせたもの。廃止した機械計算とは出どころが違う。
  //   例) defeat = 機械計算 2.729 → 実際に必要だったのは 1.58。倍率の根拠は「人の体格が idle と同じに
  //   見えること」であって、頭の高さでも全高でもない (屈む絵は頭が低いのが正しい)。
  //   ⚠️ 25キー中 23キーに補正が要った = **生成時に人の大きさが揃っていない**のが原因。
  //      次に絵を発注する時は「人物の大きさを idle と揃える」を条件に入れる (art-status §🔖 地雷)。
  //   ⚠️ 全部 1.0 未満のスケールで描画されるため (236.3/1228 × 倍率 ≦ 0.31)、拡大でも絵は荒れない。
  //   ⚠️ キーごと消さないこと。消すと POSE_SCALE_FIX['the-executioner'] (平置き=旧デザイン用) へ落ちる。
  //   ツマミ = boss-motion-lab.html で __poseFix['<key>'] = 倍率 (実行時上書き)。
  // 🔴 晒し籠(yoke) は 2026-08-12 に退役（枷・車輪とも全滅）。いま生きているのは #blade と #cannon。
  //   表は残す（常時ルール4）。復活したら下の「2026-08-14 の測り直し」を先に検討する。
  //   ⚠️ 2026-08-14、ユーザーが**腕砲を合わせるつもりでこのタブを触った**（ラボの既定タブが yoke だった）。
  //     その時の値を捨てないでここに置く。**採用していない**＝いまの表は 08-01 の確定値のまま。
  //     dash_travel 0.92 / defeat 1.40 / exec_windup 0.92 / grab_close 0.82 / horizontal_slash 1.34 /
  //     overhead_contact 1.14 / overhead_raise 1.48 / overhead_repull 1.02 / roar_pose 1.14 /
  //     turn 0.84 / walk_passing 0.98 / walk_reach 1.26（他の11キーは下の値と同じ）
  'the-executioner#yoke': {
    defeat: 1.58,
    downswing_smear: 1.36,
    exec_contact: 1.20,
    exec_windup: 0.84,
    grab_close: 0.80,
    grab_reach: 0.82,
    grab_whiff: 0.82,
    grabbed_hold: 1.10,
    horizontal_slash: 1.52,
    kick_windup: 0.88,
    overhead_contact: 1.12,
    overhead_raise: 1.14,
    overhead_recover: 1.18,
    overhead_repull: 0.92,
    roar_pose: 1.04,
    shove_contact: 1.54,
    slam_windup: 1.26,
    spin_smear: 1.12,
    // staggered_enter は 2026-08-01 の描き直し後にユーザーが測り直し「大きさは変えなくてよい」＝補正なし。
    //   ⚠️ 足が浮いて見えるのは大きさではなく**籠が足より下まで垂れている**のが原因 (実測 9.4px相当)。
    //   接地は bbox 下端で決まる (rectFor の dy) ので、倍率では直らない。絵側で直す。
    staggered_hold: 1.12,
    turn: 0.80,
    walk_passing: 0.96,
    walk_reach: 1.24,
    // idle と dash_travel は 1.00 (補正なし) = ユーザー判定。
  },
}

// 掴み・体幹崩れは変形より優先する。変形は idle を占有する時だけ見せるため、
// 攻撃や崩れへ遷移した後に turn / set_place / shove_contact / morph_* が残らない。
export function resolveBossStateOverrideKey(
  boss: Pick<Boss, 'state' | 'stateElapsed' | 'hp' | 'exec'>,
  playerGrabbed: boolean,
  isLoadedKey: (key: SpriteKey) => boolean,
): SpriteKey | null {
  if (playerGrabbed && boss.hp > 0 && isLoadedKey('grabbed_hold')) return 'grabbed_hold'
  const rise = bossRiseKey()
  if (rise && boss.hp > 0 && isLoadedKey(rise)) return rise
  if (boss.state === 'staggered' && boss.hp > 0
      && isLoadedKey('staggered_enter') && isLoadedKey('staggered_hold')) {
    return boss.stateElapsed < 0.25 ? 'staggered_enter' : 'staggered_hold'
  }
  if (boss.state === 'idle' && boss.exec?.morph === 'morphing' && boss.hp > 0) {
    const timing = executionerMorphTiming(boss.exec)
    if (boss.exec.morphElapsedMs < timing.turnMs && isLoadedKey('turn')) return 'turn'
    if (boss.exec.morphElapsedMs < timing.shoveHitAtMs && isLoadedKey('set_place')) {
      return 'set_place'
    }
    if (boss.exec.morphElapsedMs < timing.shoveEndMs && isLoadedKey('shove_contact')) return 'shove_contact'
    if (boss.exec.morphElapsedMs >= timing.shoveEndMs) {
      // 刃・砲で向き別の専用絵を各フォルダから読むため、どちらも a→b→c（出所: docs/candidates/2026-08-25-executioner-morph-enchant-ticket.md）。
      const morphKeys: readonly SpriteKey[] = ['morph_a', 'morph_b', 'morph_c']
      if (morphKeys.every(isLoadedKey)) {
        const morphThirdMs = (timing.durationMs - timing.shoveEndMs) / 3
        const morphIndex = Math.min(2, Math.floor(
          (boss.exec.morphElapsedMs - timing.shoveEndMs) / morphThirdMs,
        ))
        return morphKeys[morphIndex]
      }
    }
    if (isLoadedKey('roar_pose')) return 'roar_pose'
    if (isLoadedKey('bigcharge_raise')) return 'bigcharge_raise'
  }
  return null
}

export function resolveExecutionerStepPoseKey(
  exec: NonNullable<Boss['exec']>,
  isLoaded: (key: SpriteKey) => boolean,
): SpriteKey | null {
  const step = exec.step
  if (step === null) return null
  if (step.kind === 'back') {
    if (isLoaded('step_back')) return 'step_back'
    return exec.held === 'blade' ? 'set_backstep' : 'walk_passing'
  }
  if (step.phase === 'crouch' || (step.phase === 'travel' && step.style === 'shunpo')) {
    if (isLoaded('step_crouch')) return 'step_crouch'
    return exec.held === 'blade' ? 'set_backstep' : 'walk_passing'
  }
  if (step.phase === 'travel') {
    if (isLoaded('step_smear')) return 'step_smear'
    return exec.held === 'blade' ? 'dash_travel' as SpriteKey : 'walk_passing'
  }
  const cloakFlow = execCloakFlowOn() && (exec.held === 'blade' || (exec.held === 'cannon' && execCloakCannonOn()))
  if (step.phase === 'land') {
    // 外套のはためき (?execCloak=flow): 着地の終わりに布が胸から剥がれて舞い上がる。
    //   砲は舞い上がりを出さない (src/exec_cloak.ts の 11-04)。
    if (cloakFlow && exec.held === 'blade' && step.elapsedMs >= EXEC_CLOAK_FLOW_LIFT_FROM_MS && isLoaded('step_cloak_lift')) return 'step_cloak_lift'
    if (isLoaded('step_land')) return 'step_land'
    return 'walk_contact_b'
  }
  if (cloakFlow && step.elapsedMs < EXEC_CLOAK_SETTLE_MS) {
    const key = EXEC_CLOAK_FLOW_KEYS[Math.min(2, Math.floor(step.elapsedMs / (EXEC_CLOAK_SETTLE_MS / 3)))]
    if (isLoaded(key)) return key
  }
  // 外套のはためき (src/exec_cloak.ts・DEV ?execCloak=settle): 立ち直りの頭だけ、布が後ろへ翻る1枚。
  if (execCloakSettleOn() && exec.held === 'blade' && step.elapsedMs < EXEC_CLOAK_SETTLE_MS && isLoaded('step_settle')) return 'step_settle'
  return 'idle'
}
// 🔴 2026-08-14: 砲の専用絵22枚が入ったので、~~maul の表を共有する~~ のをやめた。
//   共有していた理由は「cannon は maul (現#blade) と同じ画像を引くから」で、その前提が消えた。
//   砲は刃より長い＝武器込みの全体を正規化したときの人物の縮み方が別なので、
//   刃の値を当てると人物の背丈がズレる。
//   🔴 **2026-08-14 夜: ユーザーが 21キーを目で決めた（下の表）。** ~~意図的に作っていない・既定＝補正なし~~
//      は同日中に解消。`idle` だけ 1.00（基準そのもの）。
//      決め方＝`scale-review.html` の腕砲タブで、**同じキーの刃 (当時の呼び名は大槌/maul) の絵を白いシルエットで重ねて**合わせた。
//      腕砲の絵は `maul/<キー>.png` (2026-08-18 以降は `blade/<キー>.png`) をトレースして右腕の肘から先だけ差し替えて作ったので、
//      **重なるのが正解**になる（旧maul側の倍率は 08-01 にユーザー確定済＝信用できる物差し）。
//      🔴 背丈から機械計算する方式は 2026-07-31 に取消済み。この21個も機械計算ではない。
//      ⚠️ 全部 1.00 未満（0.78〜1.02）＝**砲の絵は総じて刃より人物が大きく描かれていた**。
//         絵を描き直したら測り直す。
//      ⚠️ 2026-08-18: 刃(#blade) 側の POSE_SCALE_FIX は27枚差し替えに伴い {} へリセット済み。
//         この砲21キーの値は**旧maulの絵と重ねて確定した値のまま**＝新しい刃27枚とは未検証。
//   🔴 **表は空にしない。** 消すと `POSE_SCALE_FIX['the-executioner']`
//      (平置き＝旧デザインのフード男のために手で入れた表・exec_windup 1.35 等) へ落ちて、
//      別の絵のための補正が砲へ漏れる (同ファイル #blade 欄の 2026-07-31 の警告どおり)。
//      ⚠️ #wheel #haft は表そのものが無い＝この漏れが起きる状態のまま。現行仕様に無いので触っていない。
// 🔵 2026-08-19 (夜間便・Fable 5): 砲22枚を**新デザイン (E案 = 刃が筒へ畳まれた砲) へ差し替えた**ので、下の表も差し替え。
//   旧デザインの絵は public/sprites/bosses/the-executioner/_archived-2026-08-19-cannon-olddesign/ へ退避 (戻し方は同フォルダの README)。
//   ⚠️ 旧表 (2026-08-14 夜・ユーザーが目で決めた値・旧デザインの絵に対するもの) は戻せるようにここへ保存:
//     defeat 0.84 / downswing_smear 0.90 / exec_contact 0.84 / exec_windup 0.80 / grabbed_hold 0.92 / kick_windup 0.86 /
//     overhead_contact 0.82 / overhead_raise 0.88 / overhead_recover 0.84 / overhead_repull 0.96 / roar_pose 0.88 /
//     set_place 0.84 / shove_contact 0.92 / slam_contact 0.82 / slam_hold 0.78 / slam_windup 0.86 / staggered_enter 0.80 /
//     staggered_hold 0.78 / turn 1.02 / walk_passing 0.86 / walk_reach 0.88 (idle 1.00)
//   新しい値の出どころ = **刃の同キーとの位置合わせ**。砲の絵は blade/<キー>.png をトレースして袖から先だけ差し替えたので
//   人物は同一 → 「頭頂〜接地点の距離」が刃と同じ実寸になる倍率を機械で出した:
//     fix_cannon = fix_blade × (refH_cannon / refH_blade) × (頭頂〜接地[刃] / 頭頂〜接地[砲])
//   (そざい/_inbox/2026-08-18-cannon-measure/_calc_from_blade.json・道具 = scripts/blade_measure_marks.py BLADE_VARIANT=cannon)。
//   ⚠️ 砲の絵は総じて刃より 7〜24% 大きく描かれていた (前回 08-14 と同じ傾向) ので全部 1.00 未満〜1.07。
//   ⚠️ まだユーザーの実機検収前。刃側の値 (上の #blade 表) を直したら、ここも同じ比で動く。
POSE_SCALE_FIX['the-executioner#cannon'] = {
  ...Object.fromEntries(Object.entries(CANNON_FAJIN_ART).map(([key, art]) => [key, art.scale])),
  // 🔵 2026-08-25 夜 変形の3コマ + 砲の構え/発射 (砲側)。決め方は刃側と同じ = **人物の背丈が idle と揃う値**。
  //   🔴 変形の3枚は刃側と**別の絵**（砲 → 剣。筒が開いて刃へ戻る）。同名だが中身が違う。
  //   砲 idle の人物高 = 1347px。実測 = Claude 2026-08-25。
  morph_a: 1.0623,           // 人物高 1268px (振り上げ)
  morph_b: 1.0181,           // 人物高 1323px (振り抜き)
  morph_c: 1.0243,           // 人物高 1315px (収まり)
  // 🔵 2026-09-02 深夜 斜め下撃ちの絵へ。人物が小さく描かれているので 1.15（量 = aim の 98%）。旧 1.0135 / 1.0082
  aim_hold: 1.15,
  fire_recoil: 1.15,
  // しゃがみ＝量 90% に乗せる値（08-20 の帯 84〜101%）
  mortar_aim: 1.04,
  mortar_fire: 1.05,
  // 🔵 2026-09-03 第3便: 速射の撃ち終わり。量 90% の実測 1.054。
  volley_after: 1.05,
  sweep_contact: 1.15,   // 🔵 2026-09-03 第6便: aim_hold/fire_recoil と同じ描画枠 (1254px 正方形・同じ発注) なので同値。実機で見て調整
  sweep_windup: 1.15,    // 🔵 2026-09-03 第8便: sweep_contact と同じ 1254px 正方形・同じ発注の枠なので同値。
  defeat: 0.85,
  downswing_smear: 0.82,
  // 🔵 2026-08-19 夜: 弧の中割り2枚 (砲版)。 刃からの機械換算ではなく**砲の絵の頭を直接測った**
  //   (砲は刃をトレースさせたが、人物の描かれる大きさが 1〜2割ずれていたため)。
  //   基準 = (124 / 1342) × 1354 = 125.1px。 mid 123px → 1.017 / trail 150px → 0.834。
  downswing_mid: 1.017,
  downswing_trail: 0.834,
  // 🔵 2026-08-19 夕: ~~0.75~~ → 0.952。 刃側を 0.84→1.066 へ直したので、上の式どおり同じ比で動かした
  //   (0.75 / 0.84 × 1.066 = 0.952)。 砲でも実機で撮って画素で確認済み。
  exec_contact: 0.952,
  exec_windup: 1.07,          // 腕1本で真上へ掲げる姿勢に描き直された (刃版は両手上げ)。頭〜足の距離で合わせてある
  grabbed_hold: 0.80,
  kick_windup: 0.84,
  overhead_contact: 0.71,
  overhead_raise: 1.02,
  overhead_recover: 0.91,
  overhead_repull: 1.03,
  // 🔵 2026-08-22 絵を描き直したので再計算: 0.94 ÷ √(714548/673868) = 0.913
  roar_pose: 0.913,
  set_place: 0.72,
  shove_contact: 0.89,
  // 🔵 2026-09-02 見た目の棚卸し (docs/reports/2026-09-02-executioner-visual-audit.md §A1): 榴弾 (exec_cannonMortar) の
  //   3枚は刃側の地割りと同じ絵の構図で、同じく量 50 / 73 / 75% と縮んでいた → 刃側と同じ倍率 (×1.32 / ×1.12 / ×1.10) で帯へ。
  //   ~~slam_contact 0.63 / slam_hold 0.82 / slam_windup 0.80~~
  slam_contact: 0.83,
  slam_hold: 0.90,
  slam_windup: 0.87,
  staggered_enter: 0.82,
  staggered_hold: 0.78,
  turn: 0.98,
  // 🔵 2026-08-22 絵を描き直したので再計算: 0.86 ÷ √(648062/561667) = 0.801
  // 🔵 2026-08-23 夕: 刃と同じ (絵を描き直した = 前傾の分だけ縮む。walk_cross の 95%)。
  walk_passing: 0.7843,
  // walk_reach = 4コマの並びから外したキー (値は 2026-08-22 のまま)。
  walk_reach: 0.772,
  // 🔵 2026-08-23: 刃と同じ = 4コマは同じ頭の高さへ揃え、上下動は WALK_BODY_BOB_BY_BOSS が連続で作る。
  walk_cross: 0.796,            // 4コマで**一番高い**コマ (基準)
  walk_contact_b: 0.7647,       // 同上 = walk_cross の 95%
  // 🔵 2026-08-23 夕: ステップ v2 の専用絵 (刃と同じ決め方 = 発注した姿勢の深さをそのまま画面に出す)。土台 = idle / walk_passing。
  step_crouch: 1.024,           // 目標 = idle の 92%
  step_smear: 0.866,            // 目標 = walk_passing (画面 85.8%) の 90% = idle の 77.2%
  step_land: 1.044,             // 目標 = idle の 96%
  step_back: 1.076,             // 目標 = idle の 95.5% (後ろ足へ体重・少し沈む)
  // 外套の流れの砲版 (?execCloak=cannon・2026-11-03): 刃と同じ 97 / 98 / 99 / 99.5%。背丈 = 頭のてっぺん〜靴底 (この測り方で idle 1352px)。
  step_cloak_lift: 1.047,       // 背丈 1253px
  step_settle: 1.030,           // 背丈 1287px
  step_cloak_fall: 1.000,       // 背丈 1338px
  step_cloak_drop: 1.011,       // 背丈 1331px
  // idle は 1.00 (基準そのもの)。
}
// 砲側の接地点は POSE_GROUND_ANCHOR['the-executioner#cannon'] (この下の表・同じ _calc_from_blade.json が出どころ)。

export type BossFaceAnchor = [number, number]

// 顔の虚無 (口) の位置を**絵の中の点**として持つ。不透明bboxの左上=(0,0)、右下=(1,1)。
// 🔴 なぜこの持ち方か = 以前は「足元から何px上」で持っていたが、絵に動き (offset/scale/rot) を
//   足すたびに光が置いていかれた。実際 2026-08-08 に3回続けて外している
//   (offset を足す → scale を掛ける → それでも合わない)。
//   ここは drawBossSprite が実際に絵を描いた矩形へそのまま写すので、
//   **どんな変換が増えても構造的にズレない。**
// 値の出どころ = scripts/awoken_face_anchor.py (金の縁の連結成分から口の中心を割り出す)。
//   🔴 機械の出力をそのまま信じたのではなく、27枚に印を打った検証シート
//   (tmp/face_anchor_check.png) を人間が1枚で目視して全数を承認した (2026-08-08)。
//   絵を差し替えたらスクリプトを流し直し、**必ずシートを目で見てから**入れ替える。
export const BOSS_FACE_ANCHOR_IN_ART_BY_POSE: Record<string, Record<string, BossFaceAnchor>> = {
  'crowned-king-awoken': {
    bite_contact:     [0.087, 0.400],
    // 09-12 口閉じ候補。口の細い紫の隙間に合わせた、不透明bbox内の相対位置。
    bite_contact_closed_0912: [0.058, 0.425],
    bite_step_closed_0912:    [0.068, 0.324],
    // 10-09 r2 ?awokenDevour=gladius の宙で口を開けて首を伸ばす絵 (bite_contact_closed_0912 と同じ体で口が開いた 09-10 の絵)。
    //   値 = 口の中の紫 (虚無) の画素の重心 (頭の前 15%・上 75% の範囲・印を打った絵を目で見て虚無の芯に乗ることを確かめた)。
    bite_contact_up_0910:     [0.083, 0.517],
    // 10-09 r2 踏み切りの途中 (同じ測り方: 口の中の紫の重心・印を打った絵で虚無の芯に乗ることを目で確かめた)。
    pounce_launchmid_1009:    [0.101, 0.408],
    // 10-27 DEV ?awokenNeckBack 首を引いた噛みの予備動作 (同じ測り方: 口の中の紫の重心・印を打った絵で虚無の芯に乗ることを目で確かめた)。
    bite_windup_neckback_1027: [0.145, 0.220],
    bite_follow:      [0.174, 0.727],
    bite_recover:     [0.124, 0.224],
    // Toothless recovery face redesigned toward idle; measured inside each new void.
    bite_step_rest_idle_0917: [0.080, 0.393],
    bite_follow_rest_idle_0917: [0.081, 0.463],
    bite_recover_rest_idle_0917: [0.071, 0.543],
    // 2026-09-08 追加。値 = scripts/awoken_face_anchor.py の自動値 (0.115,0.384) ではなく、
    //   虚空の光の芯へ手で合わせ直した値 (tmp/bite_step_anchor_check3.png で目視確認)。
    bite_step:        [0.092, 0.495],
    bite_windup:      [0.116, 0.337],
    bite_windup_deep: [0.181, 0.469],
    chase_coil:       [0.115, 0.180],
    dash_crouch:      [0.112, 0.400],
    dash_form:        [0.169, 0.313],
    defeat:           [0.082, 0.877],
    exec_bite:        [0.148, 0.832],
    exec_windup:      [0.151, 0.411],
    extend_coil:      [0.108, 0.434],
    frenzy_bite2:     [0.170, 0.761],
    // 10-09 夜 ?devourJaw=1: frenzy_bite2 の顔の点を重ね合わせで写した値 (同じ点が新の不透明bbox の [0.177, 0.762])。
    floorbite_closed_1009: [0.177, 0.762],
    frenzy_open:      [0.150, 0.273],
    idle:             [0.117, 0.333],
    pocket_spin_wind_1001: [0.088, 0.525],
    // rear は尻が手前で顔が隠れる (予告の最後の70ms)。パリィ合図の収束が途切れないよう頭の位置に置く。
    pocket_spin_rear_1002: [0.777, 0.259],
    pocket_spin_sweep_1001: [0.942, 0.510],
    pocket_spin_front_1002: [0.195, 0.435],
    pocket_spin_settle_1001: [0.084, 0.455],
    pocket_spin_settle_1002: [0.084, 0.455],
    back_hop_crouch_1001: [0.080, 0.514],
    back_hop_air_1001:    [0.068, 0.452],
    back_hop_land_1001:   [0.085, 0.581],
    rampage_leap:     [0.148, 0.436],
    rampage_slam:     [0.141, 0.537],
    roar_up:          [0.291, 0.092],
    rush_lunge:       [0.147, 0.343],
    rush_overextend:  [0.240, 0.655],
    rush_windup:      [0.116, 0.282],
    slam_settle:      [0.124, 0.497],
    staggered_enter:  [0.201, 0.780],
    staggered_hold:   [0.128, 0.349],
    walk_passing:     [0.105, 0.309],
    walk_reach:       [0.126, 0.331],
    wall_kick:        [0.170, 0.319],
    // 2026-09-26 追加 (大暴れ第5案の横切りの噛み・叩きつけの着地)。未登録で顔の光が旧方式に落ちていた。
    //   値 = 口の虚無の重心 (swoop 236,414 / slam 220,755 原画px) を、同じ頭を移植した *_head8_0925 の
    //   登録済みの口 (awoken_wall_hunt_game.ts wallHuntFacePoint) と同じ量だけ寄せた点 (swoop 232,422 / slam 212,770)。
    //   当たり判定の口 (awoken_bite_mouth.ts) もこの点から測った。
    swoop_bite_0920:   [0.106, 0.564],
    slam_landing_0920: [0.1315, 0.765],
  },
}

export function resolveBossFaceAnchorInArt(
  bossId: string,
  imageKey: string,
): BossFaceAnchor | undefined {
  const exactImageKey = imageKey
  imageKey = awokenFangSourcePose(bossId, imageKey)
  const devOverrides = import.meta.env.DEV
    ? (globalThis as { __faceArtFix?: Record<string, BossFaceAnchor> }).__faceArtFix
    : undefined
  const anchors = BOSS_FACE_ANCHOR_IN_ART_BY_POSE[bossId]
  const stem = SPRITE_FILE_OVERRIDE.get(`${bossId}#/${imageKey}`)
  return devOverrides?.[exactImageKey] ?? devOverrides?.[imageKey]
    ?? (exactImageKey !== imageKey ? anchors?.[exactImageKey] : undefined)
    ?? (stem ? anchors?.[stem] : undefined) ?? anchors?.[imageKey]
}

// 🔴 旧方式 (削除しない): 画像キーごとの「足元からの距離」。単位・符号・facing 反転は
// render.ts の BOSS_FACE_ANCHOR と同じ。2026-08-07 に実機を見て手で決めた19件を保持する。
// 新方式の値が無い画像はこの表へ落ちるため、切替途中でも既存の見た目は変わらない。
//   なぜ要るか = 顔の虚無の発光 (drawBossFaceGlow) は boss.state ごとの8個の固定値で置いていたが、
//   本番キーポーズは頭が絵ごとに大きく動く (咆哮=天/致命=真下/壁蹴り=宙) ため光が顔から外れた。
//   ⚠️ **機械計算ではない。** 実機で全ポーズを撮った一覧シートを見て1枚ずつ目で合わせた値
//      (2026-08-07)。撮り直し = tmp スクリプトではなく下の手順で再現する:
//        DEV コンソールで __faceFix['<画像キー>'] = [x, y] → その場で動く → 良ければここへ書く。
//   ⚠️ **効くのは攻撃タイムラインが出している絵だけ。** 歩き (walk_*) / 崩し (staggered_*) /
//      撃破 (defeat) はタイムラインの外なので、ここに書いても効かず boss.state のアンカーが出る
//      (実機で見て顔に乗っていたため、そのままにしてある)。
const BOSS_FACE_ANCHOR_BY_POSE: Record<string, Record<string, BossFaceAnchor>> = {
  'crowned-king-awoken': {
    idle:             [-0.60, -0.52],
    bite_windup:      [-0.58, -0.52],
    bite_windup_deep: [-0.49, -0.28],
    bite_contact:     [-0.65, -0.28],
    bite_follow:      [-0.49, -0.14],
    bite_recover:     [-0.55, -0.55],
    frenzy_open:      [-0.41, -0.71],
    rush_windup:      [-0.57, -0.60],
    rush_lunge:       [-0.57, -0.28],
    rush_overextend:  [-0.57, -0.12],
    rampage_leap:     [-0.57, -0.49],
    rampage_slam:     [-0.52, -0.22],
    slam_settle:      [-0.52, -0.28],
    dash_crouch:      [-0.60, -0.22],
    dash_form:        [-0.57, -0.55],
    wall_kick:        [-0.20, -0.47],
    roar_up:          [-0.14, -1.09],   // 頭が天を向く = 既定値との差がいちばん大きい絵
    exec_windup:      [-0.52, -0.68],
    exec_bite:        [-0.44, -0.20],
  },
}

// バリアント固有表があればそれを丸ごと採用し、無ければボス単独表へ落とす。
// POSE_SCALE_FIX は両表をマージしない (平置き用補正が新デザインへ漏れるため)。
export function resolveBossFixedScaleRefH(bossId: string, variant: string | null): number | undefined {
  return BOSS_FIXED_SCALE_REF_H[cacheKeyOf(bossId, variant)] ?? BOSS_FIXED_SCALE_REF_H[bossId]
}

// ===== 覚醒王の体格補正の試験表 (DEV 専用・2026-09-07) =====
//   🔴 本番 (試験名 null) は **補正なし** = POSE_SCALE_FIX に覚醒王の行は無い (= 09-07 朝までの見た目)。
//   経緯: 09-07 昼に「冠 (顔の金の縁) の大きさを全29枚で揃える」表を既定に入れたが、ユーザーの実機で不合格
//     (「冠基準で揃えると、もう体が大小動いてもう明らかにおかしい」)。測り直すと、冠は揃ったぶん胴のズレが倍増していた。
//     しかも発注カード (docs/departments/art/crowned-king-awoken-appearance-now.md §3) が **冠は物差しにならない**
//     (顎と一体で口の開きで形が変わる) と既に書いていた失敗の再演で、さらに当時の測り方は金の爪まで冠に数えていた。
//   🔴 歩きの2枚 (walk_passing / walk_reach) は **同じ体格で描かれていない** = 頭÷胴の比が 22% 違う
//     (scripts/awoken_body_check.py)。1枚の絵の拡大縮小では頭と胴の比は変えられない = 倍率では直らない。根治は絵
//     (発注文 = docs/candidates/2026-09-07-awoken-walk-pair-art-order.md)。下の試験は「絵が来るまでのつなぎ」の候補。
//   試験名 (URL ?awokenWalk= / ?awokenScale= と、ラボ「覚醒王の歩き」の select が切り替える):
//     'crown'    = 冠基準の29枚表 (🔴 不合格・記録のため残す。?awokenScale=crown でだけ見える)
//     'walk-mid' = 旧の絵のまま walk_reach だけ 0.93 = 冠・胴・全体の3部位の外れ方を均した折衷
//                  (旧: 冠 +14% / 胴 -7% / 全体 +18% → 折衷後: +6% / -13% / +9%。walk_passing 比)
//     'walk-v2'  = 描き直し候補 walk_reach_v2 を 0.88 = 同じ3部位の折衷 (→ 冠 -8% / 胴 -2% / 全体 +10%)。
//                  v2 は頭÷胴の比が walk_passing と 6% しか違わない (旧 walk_reach は 22%) = いまある絵では一番「同じ獣」
//   ⚠️ 値は機械の出力 = 叩き台。合格はラボの見た目で決める (数値は裏付けだけ)。絵を差し替えたら計器で測り直す。
const POSE_SCALE_FIX_TRIAL: Record<string, Record<string, Record<string, number>>> = {
  'crowned-king-awoken': {
    'walk-mid': { walk_reach: 0.93 },
    'walk-v2': { walk_reach_v2: 0.88 },
    // 🔴 不合格 (2026-09-07 ユーザー実機)。値 = idle の冠の大きさ ÷ その絵の冠の大きさ (scripts/awoken_crown_scale.py)。
    crown: {
      bite_contact: 0.763,
      bite_follow: 0.954,
      bite_recover: 1.000,
      bite_windup: 0.951,
      bite_windup_deep: 0.920,
      chase_coil: 0.854,
      dash_crouch: 0.565,
      dash_form: 0.935,
      defeat: 0.628,
      exec_bite: 0.788,
      exec_windup: 1.289,
      extend_coil: 0.841,
      extend_coil_v2: 1.021,
      frenzy_bite2: 0.658,
      frenzy_open: 0.967,
      idle: 1.000,
      rampage_leap: 1.300,
      rampage_slam: 0.744,
      roar_up: 1.326,
      rush_lunge: 0.828,
      rush_overextend: 0.994,
      rush_windup: 1.095,
      slam_settle: 0.895,
      staggered_enter: 0.881,
      staggered_hold: 0.856,
      walk_passing: 1.077,
      walk_reach: 0.894,
      walk_reach_v2: 0.945,
      wall_kick: 1.244,
    },
  },
}
const POSE_SCALE_FIX_TRIAL_ACTIVE = new Map<string, string>()
export function setBossPoseScaleFixTrial(bossId: string, trial: string | null): void {
  if (trial === null) POSE_SCALE_FIX_TRIAL_ACTIVE.delete(bossId)
  else POSE_SCALE_FIX_TRIAL_ACTIVE.set(bossId, trial)
}

export function resolveBossPoseScaleFix(bossId: string, variant: string | null, key: SpriteKey): number {
  key = awokenFangSourcePose(bossId, key) as SpriteKey
  const trial = POSE_SCALE_FIX_TRIAL_ACTIVE.get(bossId)
  const fixes = (trial !== undefined ? POSE_SCALE_FIX_TRIAL[bossId]?.[trial] : undefined)
    ?? POSE_SCALE_FIX[cacheKeyOf(bossId, variant)] ?? POSE_SCALE_FIX[bossId]
  // ファイルの幹を差し替えている時 (setSpriteFileOverride: walk_reach → walk_reach_v2 等) は、
  //   幹の名前の補正があればそれを優先する = 別の絵には別の倍率 (2026-09-07)。無ければ従来どおりキー名。
  const stem = SPRITE_FILE_OVERRIDE.get(`${bossId}#${variant ?? ''}/${key}`)
  return (stem !== undefined ? fixes?.[stem] : undefined) ?? fixes?.[key] ?? 1
}

// DEV 専用: 歩きの2枚の組を差し替える (2026-09-07・覚醒王の試験 'walk-idle' = walk_passing ↔ idle)。
//   歩きの相方を、体格が walk_passing と揃っている idle (冠 100% / 胴 103% / 全体 100%) にする候補。
//   足は「揃う (passing)」↔「開く (idle の立ち姿)」の2値になる。読めるかはラボで決める。本番は誰も呼ばない。
const WALK_PAIR_OVERRIDE = new Map<string, readonly [SpriteKey, SpriteKey]>()
export function setWalkPairOverride(bossId: string, pair: readonly [SpriteKey, SpriteKey] | null): void {
  if (pair === null) WALK_PAIR_OVERRIDE.delete(bossId)
  else WALK_PAIR_OVERRIDE.set(bossId, pair)
}
export function resolveWalkPair(bossId: string): readonly [SpriteKey, SpriteKey] {
  return WALK_PAIR_OVERRIDE.get(bossId) ?? ['walk_passing', 'walk_reach']
}

// 歩きを N コマの1周で回す (2026-11-04・戴冠獣の8コマ歩き・src/hound_walk8_1104.ts)。
//   コマ = floor(進んだ距離 ÷ stridePx × N)。絵は並びに出ないので、ここに置いた時だけ読み込む。
//   smooth (DEV の比べ物・?houndWalkFix): 1コマの間に体と一緒に滑る床の足を、どう見せるか。
//     'lock' = 1コマの間は絵を止め、コマが替わる時に1コマ分進む (足は止まる・体が段で進む)。
//     'fade' = 今のコマと次のコマを進んだ割合で溶かす (足の重心が止まる・脚が二重に見える)。
export type WalkCycleSmooth = 'lock' | 'fade'
export type WalkCycle = { keys: readonly SpriteKey[]; stridePx: number; smooth?: WalkCycleSmooth }
const WALK_CYCLE_OVERRIDE = new Map<string, WalkCycle>()
export function setWalkCycleOverride(bossId: string, cycle: WalkCycle | null): void {
  if (cycle === null) WALK_CYCLE_OVERRIDE.delete(bossId)
  else WALK_CYCLE_OVERRIDE.set(bossId, cycle)
}
// N コマの歩きを描く直前に、1コマの中の進んだ割合 (0..1) を渡す (2026-11-05・DEV の戴冠獣の足の滑りの比べ物・src/hound_walk16_1104.ts)。
const WALK_CYCLE_FRAC_HOOK = new Map<string, (frac: number) => void>()
export function setWalkCycleFracHook(bossId: string, hook: ((frac: number) => void) | null): void {
  if (hook === null) WALK_CYCLE_FRAC_HOOK.delete(bossId)
  else WALK_CYCLE_FRAC_HOOK.set(bossId, hook)
}
// 歩きのコマの溶かしを本当に描くボス (2026-11-05・既定オン・開発版 ?houndWalkFade=off で切る)。
//   ボスの絵の溶かしは全体で切ってある (BOSS_SPRITE_CROSSFADE = false) ので、歩きの fade も前のコマの濃さが 0 のままで、
//   11-04 から配布版の戴冠獣の歩きは溶かさずに描かれていた (記録 = docs/research/2026-11-05-hound-walk-slide.md)。
const WALK_FADE_FORCE = new Set<string>()
export function setWalkFadeForce(bossId: string, on: boolean): void {
  if (on) WALK_FADE_FORCE.add(bossId)
  else WALK_FADE_FORCE.delete(bossId)
}
let walkFadeCv: HTMLCanvasElement | null = null
function walkFadeCanvas(w: number, h: number): HTMLCanvasElement {
  walkFadeCv ??= document.createElement('canvas')
  if (walkFadeCv.width !== w) walkFadeCv.width = w
  if (walkFadeCv.height !== h) walkFadeCv.height = h
  return walkFadeCv
}
export function walkCycleKey(keys: readonly SpriteKey[], walkDist: number, stridePx: number, backward: boolean): SpriteKey {
  const tFwd = (((walkDist / stridePx) % 1) + 1) % 1
  const t = backward ? (1 - tFwd) % 1 : tFwd
  return keys[Math.min(keys.length - 1, Math.floor(t * keys.length))]!
}

// 開発版の一戦が待機の絵を差し替え、並びに出ない絵を読み込ませる (剣舞のアルレ版のワルツ・src/blade_dancer_waltz.ts)。
//   待機の絵は 'idle' を引いた所 (待機・戻りの最後の 'idle') で差し替わる。null で外す。
const IDLE_KEY_OVERRIDE = new Map<string, SpriteKey>()
const RUNTIME_EXTRA_POSE_KEYS = new Map<string, SpriteKey[]>()
export function setBossIdleOverride(bossId: string, idle: SpriteKey | null, extraKeys: SpriteKey[] = []): void {
  if (idle === null) { IDLE_KEY_OVERRIDE.delete(bossId); RUNTIME_EXTRA_POSE_KEYS.delete(bossId); return }
  IDLE_KEY_OVERRIDE.set(bossId, idle)
  RUNTIME_EXTRA_POSE_KEYS.set(bossId, [idle, ...extraKeys])
}
export function bossIdleKey(bossId: string): SpriteKey {
  return IDLE_KEY_OVERRIDE.get(bossId) ?? 'idle'
}

// ===== 接地点の比率 (2026-08-19) =====
//   rectFor は「不透明bboxの一番下」を床に置く。刃先・手・籠が靴底より下へ出る絵では、その一番下は
//   靴底ではないので体が浮く (倒れた絵で実寸 約19px)。**浮きは位置のズレなので倍率では直らない。**
//   ここは key → 「bbox 上端=0 / 下端=1 のどの高さを床に置くか」。載っていないボス・キーは従来どおり 1 (下端)。
//   値の出どころ = scripts/blade_measure_marks.py で「体重の乗っている接地点」を人が読んで確定した _marks.json
//   (票 = docs/candidates/2026-08-18-blade-measure-ground-and-head-ticket.md)。
//   POSE_SCALE_FIX と同じく variant 表があればそれだけを見る (平置き用の値を variant へ漏らさない)。
//   ⚠️ 横 (anchorX) は動かさない = 当たり判定との関係が変わるため (票 §やらないこと)。
export const POSE_GROUND_ANCHOR: Record<string, Record<string, number>> = {
  // 🔵 2026-08-19 (夜間便・Fable 5): 刃27キーのうち、接地点が bbox 下端より 1% 以上 上にある 6 キーだけ。
  //   他の21キーは 靴底 = bbox 下端 (比率 1.0) なので載せない。実寸の差 = 236px 換算。
  //   ⚠️ まだユーザーの実機検収前 (シート = _確認用/48_接地点と頭_今回分.html の赤い点)。
  'the-executioner#blade': {
    // 🔵 2026-08-25 夜: ~~morph_a: 0.9886~~ を削除。エンチャント版の変形は**武器を振り上げる**ので、
    //   3枚とも武器が靴底より下へ出ない (実測)。旧・機械的に畳まれる版は刃先が 15px 下へ出ていた。
    defeat: 0.8784,            // 床に着いた手の指先。下端は刃先 (113px元 ≒ 19px実寸 浮いていた)
    downswing_smear: 0.9753,   // 前足の靴底。下端は刃先 (26px元 ≒ 4px)
    exec_contact: 0.9812,      // 後ろ足の靴底。下端は刃先 (19px元 ≒ 3px)
    exec_contact_v2: 0.9782,   // 後ろ足の靴底 (y=1122)。bbox は y 2〜1147。前足の靴底 (y=1046) は後ろ足より約11px(画面) 浮いている
    overhead_contact: 0.9152,  // 右足の靴底。下端は床へ突き立てた刃先 (89px元 ≒ 12px)
    exec_key_settle_1022: 0.878, // 2026-10-22 描き直し版。後ろ足 (右) の靴底 y=1053。下端は床へ届いた刃先 y=1184 (bbox 110〜1184)。前足の靴底は 37px(画面) 上＝当たった絵 (42px) と同じ形
    staggered_enter: 0.968,    // 前足の靴底。下端は刃先 (32px元 ≒ 5px)
    staggered_hold: 0.9819,    // 右足の靴底。下端は刃先 (17px元 ≒ 3px)
  },
  // 🔵 2026-08-19: 砲 (新デザイン22枚)。砲口が靴底より下へ出る4キーだけ (他18キーは下端=靴底)。
  //   出どころ = そざい/_inbox/2026-08-18-cannon-measure/_calc_from_blade.json (接地点は人が列を選び機械が拾った)。
  'the-executioner#cannon': {
    // 🔵 2026-08-25 夜: ~~morph_a: 0.9886~~ を削除 (刃側と同じ理由 = 振り上げる絵なので下へ出ない)。
    defeat: 0.8788,            // 床に着いた手の指先。下端は砲口 (≒19px 実寸)
    set_place: 0.9523,         // 前足の靴底。下端は砲口 (≒7.5px)
    staggered_hold: 0.9595,    // 右足の靴底。下端は砲口 (≒6.5px)
    staggered_enter: 0.9813,   // 前足の靴底。下端は砲口 (≒3px)
  },
  // 鶏の崩れ・倒れ (2026-10-30・作者「地面から浮いているように見える」)。下端は手前へ垂れた翼の先で、足・体は上に浮いていた。
  //   値 = 512×768 の alpha>16 の列ごとの最下点から読んだ (docs/research/2026-10-30-rooster-ground-fix.md)。翼の先は床の手前へはみ出して描く。
  'inverted-jester': {
    rooster_stagger_1029: 0.8757, // 爪の先 y=640 (bbox 182〜705。下端は右の翼の先・浮き 65px元 ≒ 17px)
    rooster_defeat_1029: 0.671,   // 腹と房の下 y=560 (bbox 254〜710。下端は下の翼の先・浮き 150px元 ≒ 39px)
  },
}

export function resolveBossPoseGroundAnchor(bossId: string, variant: string | null, key: SpriteKey): number | undefined {
  key = awokenFangSourcePose(bossId, key) as SpriteKey
  const table = POSE_GROUND_ANCHOR[cacheKeyOf(bossId, variant)] ?? POSE_GROUND_ANCHOR[bossId]
  // ファイルの幹を差し替えている時 (比較版の exec_contact_v2 等) は、幹の名前の値を優先する (POSE_SCALE_FIX と同じ)。
  const stem = SPRITE_FILE_OVERRIDE.get(`${bossId}#${variant ?? ''}/${key}`)
  return (stem !== undefined ? table?.[stem] : undefined) ?? table?.[key]
}

export function resolveTimelineFaceAnchor(
  fallback: BossFaceAnchor,
  frame: TimelineFrame | null,
  poseAnchors: Readonly<Record<string, BossFaceAnchor>>,
  devOverrides?: Readonly<Record<string, BossFaceAnchor>>,
  currentImage?: string,
): BossFaceAnchor {
  const anchorFor = (image: string): BossFaceAnchor | undefined => devOverrides?.[image] ?? poseAnchors[image]
  if (!frame) return currentImage ? (anchorFor(currentImage) ?? fallback) : fallback

  const current = anchorFor(frame.current.image) ?? fallback
  if (!frame.next || frame.blend <= 0) return current
  const next = anchorFor(frame.next.image) ?? fallback
  return [
    current[0] + (next[0] - current[0]) * frame.blend,
    current[1] + (next[1] - current[1]) * frame.blend,
  ]
}

export function resolveBossFaceAnchor(
  bossId: string,
  fallback: BossFaceAnchor,
  frame: TimelineFrame | null,
  currentImage?: string,
): BossFaceAnchor {
  if (currentImage) currentImage = awokenFangSourcePose(bossId, currentImage)
  const poseAnchors = BOSS_FACE_ANCHOR_BY_POSE[bossId] ?? {}
  const devOverrides = import.meta.env.DEV
    ? (globalThis as { __faceFix?: Record<string, BossFaceAnchor> }).__faceFix
    : undefined
  return resolveTimelineFaceAnchor(fallback, frame, poseAnchors, devOverrides, currentImage)
}

// 実際に描く画像が variant 自身か平置きフォールバックかを含め、rectFor が使う尺度を一括解決する。
// 公開関数なのは「blade を持っていても、blade/ に無いキーは平置き尺度」という回帰を純粋関数で守るため。
export function resolveBossSpriteScaleParams(
  bossId: string,
  requestedVariant: string | null,
  key: SpriteKey,
  fellBackToFlat: boolean,
): { sourceVariant: string | null; refH: number | undefined; poseScaleFix: number; groundAnchorRatio: number | undefined } {
  const sourceVariant = requestedVariant !== null && fellBackToFlat ? null : requestedVariant
  return {
    sourceVariant,
    refH: bossId === 'crowned-king-awoken' && AWOKEN_POCKET_SPIN_ART[key]
      ? AWOKEN_POCKET_SPIN_ART[key]!.refH : resolveBossFixedScaleRefH(bossId, sourceVariant),
    poseScaleFix: resolveBossPoseScaleFix(bossId, sourceVariant, key),
    // 接地点も同じ variant 解決に乗せる (blade を持っていても平置きへ落ちたキーは平置きの値 = 無し)。
    groundAnchorRatio: resolveBossPoseGroundAnchor(bossId, sourceVariant, key),
  }
}

// Spin measurement uses bbox-normalized POSE_SCALE_FIX, unlike the old fixed 850px reference.
// refH = poseBBoxHeight * 850 / 852 converts that fix to idle's exact runtime pixel scale.
// anchorX = frontPawPivotX + (idleCenterX - idlePawPivotX) / sourceScaleToIdleTorso.
// Source art faces left, except sweep (intentionally right). Never flip that pose separately.
export const AWOKEN_POCKET_SPIN_ART: Readonly<Record<string, { pivotX: number; anchorX: number; refH: number }>> = {
  "pocket_spin_wind_1001": {
    "pivotX": 267.5,
    "anchorX": 678.3250905920477,
    "refH": 825.0586854460093
  },
  // rear / front (10-02): refH は 850 = 待機と同じ基準。pivotX は見本 spin_mock.py の値。
  "pocket_spin_rear_1002": {
    "pivotX": 1560.0,
    "anchorX": 1962.7599626612187,
    "refH": 850.0
  },
  "pocket_spin_sweep_1001": {
    "pivotX": 1553.5,
    "anchorX": 1971.6808649422476,
    "refH": 784.1549295774648
  },
  "pocket_spin_front_1002": {
    "pivotX": 420.0,
    "anchorX": 839.7598996462095,
    "refH": 850.0
  },
  "pocket_spin_settle_1001": {
    "pivotX": 298.0,
    "anchorX": 684.0400732824899,
    "refH": 861.9718309859155
  },
  // 顔の描き直し（案 C）。1001 と同じ寸法・体の位置なので前脚の軸 pivotX・refH は 1001 のまま、
  //   縮尺 fix が変わる分だけ anchorX = pivotX + 361*refH/(850*fix) を引き直した。
  "pocket_spin_settle_1002": {
    "pivotX": 298.0,
    "anchorX": 690.1463481113658,
    "refH": 861.9718309859155
  }
} as const

// Source-pixel pivots for the 10-01 back-hop art only. At displayHeight=186,
// face x stays about centerX-118 through all three swaps (idle: -120.66,
// following walk_passing: -117.01). Do not apply these to the temporary art.
const AWOKEN_BACK_HOP_ANCHOR_X: Record<string, number> = {
  back_hop_crouch_1001: 779,
  back_hop_air_1001: 666,
  back_hop_land_1001: 849,
}
// 10-09 夜 ?devourJaw=1 の顎を閉じた絵。旧の frenzy_bite2_fangs_1006 (不透明部分の中心 x=694) と体が同じ位置に立つ原画の x。
//   重ね合わせ (新の x → 旧の x = 0.8235 * x - 89.0) の逆は 950.8 (後ろ足の爪 +0.3px・前足の爪 -1.65px)。そこから 3.8 原画px 前へ寄せて、
//   後ろ足 +1.0px・前足 -1.0px に振り分けた (表示 186px・噛みの4つのキーの倍率と傾きで実測・tests/awoken_devour.test.ts)。
const AWOKEN_FLOORBITE_ANCHOR_X: Record<string, number> = {
  floorbite_closed_1009: 947.0,
  // 10-27 DEV ?awokenNeckBack の首を引いた予備動作 (幅 1449・倍率 0.85)。不透明部分の中心 (724.5) のままだと、待機から替わる時に
  //   後ろ足が 102・前足が 67 (待機の原画px) 頭の側へ跳ぶ (足先の金の爪の塊の中心で測った)。その真ん中の 85 を尾の側へ戻す = 85 ÷ 0.85 = 100 原画px。
  bite_windup_neckback_1027: 624.5,
}

// Rest-mid art (adopted 2026-10-08): source px (the art faces left) moving the figure toward its facing so that both
// in-between steps stay <=50px per tick. 99px x 0.182 = ~18 world px (measured 10-08).
//   10-08 later: shove/butt/stagger are shifted BACK (negative) to split each step's front/back-foot jump evenly
//   (butt 63px = ~11.5 world px, stagger 55px = ~10, shove 27px = ~5; measured before/after in measure/).
//   10-08 later: rest_from_stagger2 (the 2nd step after a stagger) -33px = ~6 world px back: back foot 53 -> 47 on stagger -> stagger2, front foot 46 -> 40 on stagger2 -> idle.
// 戴冠獣の駆け抜けの振り返り (2026-10-11・DEV ?houndPassThrough=1): 床に着いた足が絵の切り替わりで滑らないよう、boss.x に置く原画の x。
//   turnLook = 滑り止まり (skid・bbox 中心) と4本の足が同じ所に立つ (bbox 中心 826 のままだと前後の足とも +3px ずれる → 11 原画px 寄せる)。
//   turnFront = 次の待機 (向きが反転した idle) と足を合わせる。bbox 中心 (792.5) だと長い尾の分だけ体が前へずれ、
//   前足 +58px・後ろ足 +19px 跳ぶ → 154 原画px (= 38 画面px) 後ろへ置いて前後 ±20px に振り分けた (踏み込んだ前足を寄せる分は絵の差)。
//   測り方 = そざい/boss-study/hound-pass-1011/sheet.py (表示 186px・ゲームと同じ置き方)。
// 10-11 撮影で測り直し (C:/dev/LUNAIRE-notes/assets/2026-10-11-hound-pass-art/pass-art-measure.md → v2/)。
//   首の振り返り → 体の向き直り で前後の脚が入れ替わり (前脚が頭の側へ回る)、立つ幅も 257 → 220 → 待機 ~190 画面px と縮む (絵の差)。
//   床に着いた足 (最下端から 28 原画px 以内) の外側の端で測ると、旧の置き方は 振り返り→向き直り +44/-3・向き直り→待機 +13/+15
//   (走った向きを − とする画面px)。幅の縮み 37px の半分ずつが両端に出るのが下限なので、それに寄せて最大 44 → 24 にした:
//     skid と turnLook は 12 画面px (= 48.7 原画px) 尾の側へ (2枚の足は同じ所のまま)・turnFront は 8 画面px (= 32.5 原画px) 走った向きへ。
//   代わりに 走り(runExtend) → 滑り止まり の体の重心は -13 → -1 (止まり始めが滑らかになる)・頭は -1 → +11 画面px (尾の側へ)。
//   測り方 = scripts/analyze_hound_pass_art.py (床に着いた足の列を足した)。
// 剣舞の技ごとの絵 (2026-10-19・DEV ?bladeMoves=1) の横の基準。帯の結び目 (腰) を、idle の結び目と同じ画面の所へ置く。
//   idle は結び目 x≈385・不透明 bbox の中心 504 = 結び目が中心から 119 原画px 前 (左)。各絵の値 = 結び目の x + 119 ÷ 倍率。
//   足先でそろえると、踏み込みの絵で体が後ろへ跳ぶので腰にした。弾かれ・崩れ・撃破は幹の名前 (<key>_1019) で引く。
//   swap_draw だけは腰で合わせると前後の絵から頭が 39px 後ろへ跳んだので、画面で 20px 前へ寄せた (290 → 400)。
//   waltz_cut2 は剣が頭より上を通る旧の横薙ぎ (使っていない・残してある)。今の横薙ぎは胸の高さを薙ぐ waltz_cut2b。
export const BLADE_DANCER_ANCHOR_X: Partial<Record<string, number>> = {
  waltz_ready: 972,
  waltz_cut1: 752,
  waltz_cut2: 967,
  waltz_cut2b: 890,
  pirouette_wind: 630,
  pirouette_turn: 722,
  pirouette_thrust: 873,
  hem_sweep: 922,
  hem_rise: 883,
  thrust_recover: 823,
  glide: 443,
  exec_slash: 908,
  swap_draw: 400,
  parried_1019: 663,
  staggered_1019: 798,
  defeat_1019: 755,
  // 円舞 v2 (2026-10-20) = 帯の結び目の x (コマの中・方眼で目読み) + 119 ÷ 2.4。
  m1020_waltz_1: 165.6,
  m1020_waltz_2: 385.6,
  m1020_waltz_3: 467.6,
  m1020_waltz_4: 407.6,
  m1020_waltz_5: 234.6,
  m1020_waltz_6: 389.6,
  m1020_waltz_7: 489.6,
  m1020_waltz_8: 314.6,
  m1020_waltz_9: 444.6,
  m1020_waltz_10: 429.6,
  m1020_waltz_11: 394.6,
  m1020_waltz_12: 164.6,
  // 溜めの3コマ = 結び目 + 119 ÷ 1.776。
  m1020_waltz_w1: 205,
  m1020_waltz_w2: 174,
  m1020_waltz_w3: 203,
  m1020_waltz_s1: 233,
  m1020_waltz_s2: 287,
  m1020_waltz_c1: 175,   // 結び目 115 + 119 ÷ 1.97
  m1020_waltz_c2: 414.5,   // 結び目 355 + 119 ÷ 2.0
  m1020_waltz_c3: 199,     // 結び目 145 + 119 ÷ 2.2
  m1020_waltz_c4: 248.4,   // 結び目 180 + 119 ÷ 1.74
  m1020_waltz_c6: 293,     // 結び目 227 + 119 ÷ 1.64 = 299.6 から、s2 (294 → 287) と同じ分だけ戻した
  m1020_waltz_c5: 210,
  m1020_waltz_a1: 317,     // 帯の中ほど 261 + 119 ÷ 2.11
  m1020_waltz_b1: 334,     // 結び目 267 + 119 ÷ 1.776
  m1020_waltz_b2: 405,     // 結び目 338 + 119 ÷ 1.776
  m1020_waltz_b3: 390,     // 結び目 323 + 119 ÷ 1.776
  // v6 の 28コマ = 結び目 (帯の薄紫の上 35% の x の中央) + 119 ÷ 2.4。
  m1020_v6_a1: 200,   // 10-21 描き直し (紙 blade-dancer-motion-1021/a1.png を緑抜き・1/2.03 に縮めて他のコマと同じ 2.4 に)
  m1020_v6_a2: 307.6,
  m1020_v6_a3: 437.6,
  m1020_v6_a4: 240.6,
  m1020_v6_b1: 271.6,
  m1020_v6_b2: 275.6,
  m1020_v6_b3: 419.6,
  m1020_v6_b4: 131.6,
  m1020_v6_c1: 214.6,
  m1020_v6_c2: 387.6,
  m1020_v6_c3: 232.6,
  m1020_v6_c4: 172.6,
  m1020_v6_d1: 143.6,
  m1020_v6_d2: 321.6,
  m1020_v6_d3: 122.6,
  m1020_v6_d4: 381.6,
  m1020_v6_e1: 274.6,
  m1020_v6_e2: 191.6,
  m1020_v6_e3: 513.6,
  m1020_v6_e4: 215.6,
  m1020_v6_f1: 202.6,
  m1020_v6_f2: 363.6,
  m1020_v6_f3: 159.6,
  m1020_v6_f4: 147.6,
  m1020_v6_g1: 373.6,
  m1020_v6_g2: 425.6,
  m1020_v6_g3: 147.6,
  m1020_v6_g4: 227.6, // 10-21 礼を両手に刀の形で描き直し (紙 blade-dancer-motion-1021/g4.png を緑抜き・1/2.03)
  m1020_waltz_a2: 511,     // 帯の中ほど 455 + 119 ÷ 2.11     // 結び目 175 + 119 ÷ 2.3 = 226.7 から、体が左へ寄って見える分を目で 17 戻した
}
// 2026-10-22 致命を弾かれた絵 (src/exec_parry_recoil.ts)。2本の刀を前の地面に突き立てて踏ん張る絵へ描き直し (旧の半回転は _archived-2026-10-22-parried-half-spin/)。
//   腰 (帯の結び目) x≈667 + 119 ÷ 1.1。表の中へ書かず外で足す (剣舞の表は別の作業と並行で触られるため・衝突を避ける)。
BLADE_DANCER_ANCHOR_X[EXEC_PARRIED_ART_1021] = 775
POSE_SCALE_FIX['blade-dancer'][EXEC_PARRIED_ART_1021] = 1.1
// 10-22 円舞 v7 (?bladeWaltz=v7)。帯の結び目 (濃い薄紫の x の中央) + 55 (v6 の表と結び目の差の中央)。a4・b4 は後ろへ流れる帯を拾うので目で決めた。
BLADE_DANCER_ANCHOR_X.m1022_w7_a1 = 142
BLADE_DANCER_ANCHOR_X.m1022_w7_a2 = 224
BLADE_DANCER_ANCHOR_X.m1022_w7_a3 = 407   // 結び目なら 367。踏み込みの絵で剣先が v6 の当たりの前端 (133) に 21 足りないので、40 (画面で 12) 前へ出した
BLADE_DANCER_ANCHOR_X.m1022_w7_a4 = 165
BLADE_DANCER_ANCHOR_X.m1022_w7_b1 = 178
BLADE_DANCER_ANCHOR_X.m1022_w7_b2 = 187
BLADE_DANCER_ANCHOR_X.m1022_w7_b3 = 469
BLADE_DANCER_ANCHOR_X.m1022_w7_b4 = 165
BLADE_DANCER_ANCHOR_X.m1022_w7_c1 = 205
BLADE_DANCER_ANCHOR_X.m1022_w7_c2 = 163
BLADE_DANCER_ANCHOR_X.m1022_w7_c3 = 436
BLADE_DANCER_ANCHOR_X.m1022_w7_c4 = 165
// 10-25 旋回突き (アルレッキーノの4連・開発版 ?bladePirouette=b4・src/blade_dancer_pirouette.ts)。帯の結び目 + 119 ÷ 2.1。
//   倍率は体の大きさをそろえた w7 (blade_body_size.ts) と同じ 2.1 を直接入れる。
for (const [k, x] of [[1, 610], [2, 484], [3, 629], [4, 544], [5, 502], [6, 222], [7, 607], [8, 333]] as const) {
  BLADE_DANCER_ANCHOR_X[`m1025_p4_${k}`] = x
  POSE_SCALE_FIX['blade-dancer'][`m1025_p4_${k}`] = 2.1
}
// 10-25 遅れの一撃 (開発版 ?bladeGamma=1・src/blade_dancer_gamma.ts)。帯の結び目 + 57。
for (const [k, x] of [[1, 219], [2, 437], [3, 567], [4, 583]] as const) {
  BLADE_DANCER_ANCHOR_X[`m1025_g_${k}`] = x
  POSE_SCALE_FIX['blade-dancer'][`m1025_g_${k}`] = 2.1
}
// 10-25 引き寄せ (開発版 ?bladePull=1・src/blade_dancer_pull.ts)。帯の結び目 + 57。
for (const [k, x] of [[1, 370], [2, 460], [3, 478], [5, 703]] as const) {
  BLADE_DANCER_ANCHOR_X[`m1025_c_${k}`] = x
  POSE_SCALE_FIX['blade-dancer'][`m1025_c_${k}`] = 2.1
}
// 10-25 跳んで叩く (開発版 ?bladeDelta=1・src/blade_dancer_delta.ts)。帯の結び目 + 57。
for (const [k, x] of [[1, 418], [2, 396], [3, 275], [4, 406]] as const) {
  BLADE_DANCER_ANCHOR_X[`m1025_d_${k}`] = x
  POSE_SCALE_FIX['blade-dancer'][`m1025_d_${k}`] = 2.1
}
// 10-25 回って終わる連 (開発版 ?bladeSpin=1・src/blade_dancer_spin.ts)。帯の結び目 + 57。
for (const [k, x] of [[1, 563], [2, 603], [3, 568], [4, 612]] as const) {
  BLADE_DANCER_ANCHOR_X[`m1025_r_${k}`] = x
  POSE_SCALE_FIX['blade-dancer'][`m1025_r_${k}`] = 2.1
}
// 10-25 アルレ版の絵の大きさを立ち姿 (idle) にそろえる (作者「サイズのズレは人間の眼としてはすごく気になる」)。
//   値 = idle の体の芯の面積の平方根 ÷ その絵の値 (画面で実測・docs/research/2026-10-25-blade-arle-size-table.md の体基準)。
//   頭の幅で測る案は剣を掲げると狂うので使わない。m1025_* はアルレ版 (開発版) だけの絵。
const BLADE_ARLE_BODY_FIT: Readonly<Record<string, number>> = {
  m1025_c_1: 0.918, m1025_c_2: 0.823, m1025_c_3: 0.852, m1025_c_5: 0.715,
  m1025_d_1: 0.662, m1025_d_2: 0.712, m1025_d_3: 0.806, m1025_d_4: 0.688,
  m1025_g_1: 1.039, m1025_g_2: 0.864, m1025_g_3: 0.856, m1025_g_4: 0.906,
  m1025_p4_1: 0.905, m1025_p4_2: 1.084, m1025_p4_3: 0.935, m1025_p4_4: 1.057,
  m1025_p4_5: 1.099, m1025_p4_6: 1.125, m1025_p4_7: 1.25, m1025_p4_8: 1.222,
  m1025_r_1: 1.138, m1025_r_2: 0.985, m1025_r_3: 0.975, m1025_r_4: 0.92,
}
for (const [key, fit] of Object.entries(BLADE_ARLE_BODY_FIT)) {
  POSE_SCALE_FIX['blade-dancer'][key] = Math.round(2.1 * fit * 1000) / 1000
}
// 10-25 アルレ版の描き直し (投げ t・ピルエットの沈み p4_6b・回る連の出だし r_1b・ワルツ w・拍手 k)。帯の結び目 + 57、体の芯の面積を idle に合わせた倍率。
//   r_1b は同じ測り方で既存の r_1 を測ると 548 (登録 563) と 15 出るので、続く r_2〜4 とずれないよう +15 してある。
//   k_1 は k_2 と 200ms ごとに入れ替わるので、面積でなく頭から足までの高さを k_2 にそろえた。
for (const [name, x, scale] of [
  ['m1025_t_1', 438, 2.710956],
  ['m1025_t_2', 299, 2.610223],
  ['m1025_t_3', 367, 2.795904],
  ['m1025_p4_6b', 443, 2.720409],
  ['m1025_r_1b', 256, 2.523342],
  ['m1025_w_1', 429, 2.545398],
  ['m1025_w_2', 495, 2.544498],
  ['m1025_w_3', 474, 2.290042],
  ['m1025_w_4', 512, 2.484633],
  ['m1025_k_1', 229, 2.2975],
  ['m1025_k_2', 181, 2.797554],
  // 10-26 作者の答えで描き足した2枚 (暗器投げの振りかぶり t_4・旋回突きの④の前の溜め p4_6c)。測り方は同じ。
  ['m1026_t_4', 210, 2.138895],
  ['m1026_p4_6c', 383, 2.083154],
  // 10-28 回る連の向き違い (?bladeSpinTurn=1)。回るために描いた横・斜め後ろ・後ろ・正面と、その左右反転。足元は爪先、倍率は頭とスカートの大きさを r_1b に目で合わせた。
  ['m1028_s_side', 241, 2.27],
  ['m1028_s_side_m', 234, 2.27],
  ['m1028_s_back3q', 267, 2.27],
  ['m1028_s_back3q_m', 251, 2.27],
  ['m1028_s_back', 290, 2.27],
  ['m1028_s_back_m', 263, 2.27],
  ['m1028_s_front', 286, 2.27],
  ['m1028_s_front_m', 249, 2.27],
  // 10-28 夕 回る絵の2版目 (?bladeSpinTurn=2)。剣の長さを立ち絵と同じに描き直した。倍率は帯から爪先までの高さを1版目にそろえた (4枚とも同じ紙に同じ大きさで描いてあるので1つの値)。
  ['m1028_s2_side', 290, 2.17],
  ['m1028_s2_side_m', 245, 2.17],
  ['m1028_s2_back3q', 308, 2.17],
  ['m1028_s2_back3q_m', 244, 2.17],
  ['m1028_s2_back', 286, 2.17],
  ['m1028_s2_back_m', 345, 2.17],
  ['m1028_s2_front', 305, 2.17],
  ['m1028_s2_front_m', 273, 2.17],
] as const) {
  BLADE_DANCER_ANCHOR_X[name] = x
  POSE_SCALE_FIX['blade-dancer'][name] = scale
}
// 10-29 Codex 描きの3枚 (幕引きの礼・崩れの入り・拍手の入り)。倍率は胴の面積を元の絵 (glide・staggered_1019・m1025_k_1) に合わせ、
//   置き場所は帯の結び目 + 119 ÷ 倍率。
BLADE_DANCER_ANCHOR_X.m1029_exec_bow = 554
POSE_SCALE_FIX['blade-dancer'].m1029_exec_bow = 1.04
BLADE_DANCER_ANCHOR_X.m1029_defeat_fall = 534
POSE_SCALE_FIX['blade-dancer'].m1029_defeat_fall = 1.14
//   拍手の入りは m1025_k_1 と頭から足の高さをそろえた (1457px × 1.123 ≒ 712px × 2.2975)。
BLADE_DANCER_ANCHOR_X.m1029_clap_in = 480
POSE_SCALE_FIX['blade-dancer'].m1029_clap_in = 1.123
// 11-01 借りている絵を専用の絵へ (配布版の既定・前の絵は開発版 ?bladeOwnArt=old・src/blade_own_art.ts)。δ の低い払い v2 (前後へ開いた払い)。倍率は頭と裾の大きさを今の c_5 (2.1 × 0.715) に目で合わせ、
//   置き場所は帯の結び目 (810) + 119 ÷ 倍率。
BLADE_DANCER_ANCHOR_X.m1101_delta_sweep_v2 = 927
POSE_SCALE_FIX['blade-dancer'].m1101_delta_sweep_v2 = 1.02
// 11-02 γ の戻りの残心の専用の絵 (開発版 ?bladeGammaRest=v1・src/blade_own_art.ts)。倍率は頭から足までの高さを直前の g_4 (2.1 × 0.906) にそろえ、
//   置き場所は帯の結び目 (772) を g_4 と同じ画面の所へ (結び目 + 57 × 2.1 × 0.906 ÷ 倍率 = 772 + 99)。
BLADE_DANCER_ANCHOR_X.m1102_gamma_rest = 871
POSE_SCALE_FIX['blade-dancer'].m1102_gamma_rest = 1.1
// 11-03 大振り・速い連の型の1枚 (開発版 ?bladeGrandQuick=v1・src/blade_own_art.ts)。倍率は頭の大きさを前後のコマ (v6 の倍率 2.1 = 配布版の体の大きさ) にそろえ、
//   置き場所は帯の結び目 + 119 ÷ 倍率 (速い連 660・大振り 546)。
BLADE_DANCER_ANCHOR_X.m1103_quick_d2 = 773
POSE_SCALE_FIX['blade-dancer'].m1103_quick_d2 = 1.05
BLADE_DANCER_ANCHOR_X.m1103_grand_g1 = 647
POSE_SCALE_FIX['blade-dancer'].m1103_grand_g1 = 1.18
// 11-03 大振りの締めの戻り2枚 (開発版 ?bladeGrandQuick=v3)。倍率は頭から足までの高さを g1 にそろえて 1.18、
//   置き場所は帯の結び目の x − 168 (g1 と同じ差・結び目は目で読んだ)。
BLADE_DANCER_ANCHOR_X.m1103_grand_g2 = 526
POSE_SCALE_FIX['blade-dancer'].m1103_grand_g2 = 1.18
BLADE_DANCER_ANCHOR_X.m1103_grand_g3 = 542
POSE_SCALE_FIX['blade-dancer'].m1103_grand_g3 = 1.18
// 11-02 速い連の合図の構え (同じ旗)。同じ画風の d2 と比べて面と帯の結びが約 1.13 倍に描かれているので 1.05 ÷ 1.13、置き場所は結び目 570 + 119 ÷ 倍率。
BLADE_DANCER_ANCHOR_X.m1102_quick_sig = 698
POSE_SCALE_FIX['blade-dancer'].m1102_quick_sig = 0.93
// 11-04 速い連の専用の11枚 (開発版 ?bladeQuick=v1104・src/blade_own_art.ts)。1024×1536 に同じ大きさで描いてあるので倍率は1つ。
//   倍率は頭 (面の縞) の幅と高さを配布版の m1103_quick_d2 (1.05) と比べて 1.11〜1.19 倍に描かれているので 1.05 ÷ 1.15 ≒ 0.9。
//   置き場所は帯の結び目 (目で読んだ x) を m1103_quick_d2 と同じ画面の所へ (結び目 + 87 ÷ 0.9)。
for (const [k, knot] of [['d1', 410], ['d2c', 495], ['d3', 460], ['d4', 520], ['e1', 420], ['e2', 530], ['e3b', 600], ['e4', 610], ['f1', 550], ['f2', 635], ['f3', 400]] as const) {
  BLADE_DANCER_ANCHOR_X[`m1104_quick_${k}`] = knot + 97
  POSE_SCALE_FIX['blade-dancer'][`m1104_quick_${k}`] = 0.9
}
// 11-03 速い連の回る型 (開発版 ?bladeQuick=spin1・src/blade_own_art.ts)。倍率は首から足先までと頭の高さを d2c (1.029) と比べ、
//   どれも約 0.88 倍に描かれているので 1.029 ÷ 描かれた大きさ。置き場所は回る3枚が立つつま先 (絵の一番下) を足元の 8px 後ろへ
//   (d2c の2つの足先の間・つま先 + 8 ÷ (0.12944 × 倍率))。h2 は前と後ろの足先の真ん中 (560) を同じ所へ。
//   h4 (spin4 の4発目) はそろえた2つの足先の真ん中 (442)・頭の高さが s1 と同じなので倍率も s1 並み。
for (const [k, x, fix] of [['s1', 515, 1.143], ['s2', 542, 1.183], ['s3', 477, 1.169], ['h2', 560, 1.17], ['h4', 442, 1.15]] as const) {
  BLADE_DANCER_ANCHOR_X[`m1103_qspin_${k}`] = Math.round(x + 8 / (0.12944 * fix))
  POSE_SCALE_FIX['blade-dancer'][`m1103_qspin_${k}`] = fix
}
// 11-04 速い連の交互打ちの型 (開発版 ?bladeQuick=alt1・src/blade_own_art.ts)。頭のてっぺんから足先までと頭の幅が d2c の約 0.92 倍に
//   描かれているので倍率は 1.029 ÷ 0.92 ≒ 1.12。置き場所はそろえた足先の真ん中 (495) を d2c の足先と同じ画面の所へ (495 + 15.4 ÷ (0.12944 × 1.12))。
BLADE_DANCER_ANCHOR_X.m1104_qalt_h2 = 601
POSE_SCALE_FIX['blade-dancer'].m1104_qalt_h2 = 1.12
// 花嫁の 10-23 描き直し (開発版 ?brideRedraw・src/bride_redraw_1023.ts) の横の置き場所 (絵の画素)。
//   腰の黒い薔薇が元の絵と同じ所に来るように測った (薔薇の新しい x + (元の置き場所 − 元の薔薇の x) ÷ 倍率の比)。キーはファイルの幹。
export const BRIDE_REDRAW_ANCHOR_X: Readonly<Record<string, number>> = {
  embrace_hug_1023: 454,
  flurry_swing_right_floor_1023: 478, flurry_swing_cross_floor_1023: 481, flurry_swing_left_floor_1023: 552,
  flurry_slam_windup_floor_1023: 465,
  exec_found_1023: 571, exec_glide_1023: 810, exec_coil_1023: 867, exec_okaeri_1023: 995,
  // 10-25 立ち姿の描き直しの見本 (開発版 ?brideArt・src/bride_art_sample_1025.ts)。元の idle は置き場所 462・薔薇 376。
  idle_art_1025_a: 535, idle_art_1025_b: 537, idle_art_1025_c: 535,
  // 10-25 剣舞の描き直し (案A)。薔薇を元の絵と同じ所に。charge・slam の元は置き場所の指定なし (縁の中央 542.5・737.5)。
  flurry_charge_art_1025_a: 508, flurry_swing_right_art_1025_a: 484, flurry_swing_cross_art_1025_a: 492,
  flurry_swing_left_art_1025_a: 555, flurry_slam_windup_art_1025_a: 465, flurry_slam_art_1025_a: 726,
  // 10-25 状態の絵の描き直し (案A)。元の4枚は置き場所の指定なし (縁の中央)。薔薇は元 active 444・parried 500・staggered 597・defeat 650 (目で取った)。
  active_art_1025_a: 496, parried_art_1025_a: 568, staggered_art_1025_a: 634, defeat_art_1025_a: 608,
  // 10-25 技ごとの絵の描き直し (案A)。ヴェール・招待・茨の道の元は置き場所の指定なし (縁の中央)。とどめは元の _1023 の置き場所から。
  veil_coil_art_1025_a: 512, veil_twist_art_1025_a: 763, veil_sweep_contact_art_1025_a: 653,
  veil_follow_art_1025_a: 589, veil_open_art_1025_a: 731,
  invitation_prep_art_1025_a: 504, invitation_reach_art_1025_a: 563, invitation_full_art_1025_a: 589,
  aisle_rise_art_1025_a: 522, aisle_kneel_art_1025_a: 758,
  exec_found_art_1025_a: 559, exec_glide_art_1025_a: 810, exec_coil_art_1025_a: 870, exec_okaeri_art_1025_a: 996,
  exec_parried_snap_art_1025_a: 1280, exec_cover_art_1025_a: 854,
  // 10-26 残りの技の絵 (案A)。元の4枚は置き場所の指定なし (不透明部分の中央)。薔薇は元 side_crouch 500・triple 423・open 621・close 454 (close は目で取った)。
  side_crouch_art_1025_a: 506, blink_triple_charge_1021_art_1025_a: 477, embrace_open_art_1025_a: 606, embrace_close_art_1025_a: 500,
  // 10-27 返しの一閃の間の絵3枚と抱く絵 (案A)。元の置き場所は rise 507・reach 621・settle 557 (BRIDE_ISSEN_ANCHOR_X)・抱く絵 454。
  //   薔薇は元 rise 599・reach 857・settle 860・抱く絵 540、新 623・879・891・568 (目で取った)。
  kaeshi_tween_rise_1023_art_1025_a: 526, kaeshi_tween_reach_1023_art_1025_a: 637,
  kaeshi_tween_settle_1023_art_1025_a: 573, embrace_hug_1023_art_1025_a: 477,
}
function brideRedrawAnchorX(key: SpriteKey): number | undefined {
  return BRIDE_REDRAW_ANCHOR_X[SPRITE_FILE_OVERRIDE.get(`bride#/${key}`) ?? key]
}
function bladeDancerAnchorX(key: SpriteKey): number | undefined {
  // 描き直しの比較版 (repaint-1023/<元の名前>・src/blade_art_repaint.ts) は元と同じ置き方なので、フォルダを外した名前で引く。
  const stem = SPRITE_FILE_OVERRIDE.get(`blade-dancer#/${key}`)
  return BLADE_DANCER_ANCHOR_X[stem?.replace(/^repaint-1023\//, '') ?? key]
}
// 剣舞の絵の倍率を名前の頭ごとに替える (10-23 作者「攻撃中だけ体が大きい」・src/blade_body_size.ts)。
//   足元は腰の結び目を同じ所に保つ (結び目の x + 119 ÷ 倍率 の決まりのまま倍率だけ替える)。元の値を控えるので何度呼んでも同じ。
const BLADE_BODY_FIX_ORIG = new Map<string, { fix: number; anchor: number | undefined }>()
export function setBladeDancerBodyFix(prefixes: readonly string[], fix: number | null): void {
  const fixes = POSE_SCALE_FIX['blade-dancer']!
  for (const name of Object.keys(fixes)) {
    if (!prefixes.some(p => name.startsWith(p))) continue
    if (!BLADE_BODY_FIX_ORIG.has(name)) BLADE_BODY_FIX_ORIG.set(name, { fix: fixes[name]!, anchor: BLADE_DANCER_ANCHOR_X[name] })
    const o = BLADE_BODY_FIX_ORIG.get(name)!
    const f = fix ?? o.fix
    fixes[name] = f
    if (o.anchor !== undefined) BLADE_DANCER_ANCHOR_X[name] = o.anchor + 119 * (1 / f - 1 / o.fix)
  }
}

// 剣舞の絵の倍率を1枚ごとに替える (11-04 速い連の大きさそろえ・src/blade_own_art.ts の v1104b)。
//   結び目の画面の所は保つ (置き場所 = 結び目 + lead ÷ 倍率 の決まりのまま倍率だけ替える)。元の値は上と同じ控えに置く。
export function setBladeDancerPoseFix(fixes: Readonly<Record<string, number>>, lead: number): void {
  const all = POSE_SCALE_FIX['blade-dancer']!
  for (const [name, f] of Object.entries(fixes)) {
    if (!BLADE_BODY_FIX_ORIG.has(name)) BLADE_BODY_FIX_ORIG.set(name, { fix: all[name]!, anchor: BLADE_DANCER_ANCHOR_X[name] })
    const o = BLADE_BODY_FIX_ORIG.get(name)!
    all[name] = f
    if (o.anchor !== undefined) BLADE_DANCER_ANCHOR_X[name] = o.anchor + lead * (1 / f - 1 / o.fix)
  }
}

export const HOUND_PASS_ANCHOR_X: Partial<Record<string, number>> = {
  houndPass_skid: 788,
  houndPass_turnLook: 788,
  houndPass_turnFront: 979,
  // 8コマの歩き (2026-11-04・DEV ?houndWalk=8)。床に着いた足がコマの間で止まるよう最小二乗で解いた横の置き場所に、
  //   1周の冠の位置の平均が待機と同じ所へ来る分を足した値 (tests/hound_walk8_1104.test.ts が足と待機⇔歩きを見る)。
  houndWalk8_t000: 751.5,
  houndWalk8_t125: 712.7,
  houndWalk8_t250: 705.1,
  houndWalk8_t375: 797.3,
  houndWalk8_t500: 818.7,
  houndWalk8_t625: 790.4,
  houndWalk8_t750: 705.0,
  houndWalk8_t875: 772.2,
  // 16コマの歩き (2026-11-04・DEV ?houndWalk=16)。4枚目の色直しは形が同じなので置き場所も同じ。
  //   間の8枚は、前後のコマと同じ足が床で止まる所と、胴が前後のコマの真ん中に来る所の間で、ずれの大きい方が最も小さくなる値。
  //   間の絵は胴を前のコマのまま描いたので、両方は同時に満たせない (tests/hound_walk16_1104.test.ts が足の滑りを見る)。
  houndWalk16_t062: 726.0,
  houndWalk16_t188: 693.7,
  houndWalk16_t312: 693.1,
  houndWalk16_t375: 797.3,
  houndWalk16_t438: 735.3,
  houndWalk16_t562: 793.7,
  houndWalk16_t688: 729.4,
  houndWalk16_t812: 744.5,
  houndWalk16_t938: 763.7,
}

const KNIGHT_REST_ANCHOR_SHIFT_PX: Partial<Record<string, number>> = {
  rest_from_thrust: 99, rest_from_shove: -27, rest_from_butt: -63, rest_from_stagger: -55, rest_from_stagger2: -33,
  // 構え直しの後の間の絵 (2026-10-27): 前後の切り替えで後ろ足の跳びが等しく割れる置き位置 (実戦の記録から解いた)。
  rest_from_rearm_a: -85, rest_from_rearm_b: 19,
  // 石突を前へ運ぶ2枚 (2026-10-29): 後ろ足のかかとが rest_from_scrape と rest_from_butt の間に来る置き位置。
  rest_rise_55: 60, rest_rise_70: -35,
}

/** Same area scale, ground anchor and authored shift used by drawBossSprite's rectFor. */
export function resolveBossSpriteArtPlacement(
  bossId: string, key: SpriteKey, meta: SpriteMeta,
  centerX: number, footY: number, displayHeight: number, facingMul: 1 | -1,
  options: {
    variant?: string | null; fellBackToFlat?: boolean;
    scaleX?: number; scaleY?: number; offsetX?: number; offsetY?: number;
    rotationRad?: number; horizontalAnchorX?: number;
    // 足に合わせる置き方 (致命の4枚の比較版)。footX の靴を、bbox 中心から fromCenterPx (画面px・倍率1の時) の所に置く。
    footAnchor?: { footX: number; fromCenterPx: number };
  } = {},
): BossSpritePlacement {
  if (bossId === 'vigil-knight' && isKnightRearmKey(key)) {
    const contactImage = cache.get(bossId)?.get('lanceSweep_contact')
    const contactMeta = contactImage?.complete && contactImage.naturalWidth
      ? getSpriteMeta(bossId, null, 'lanceSweep_contact', contactImage) ?? KNIGHT_REARM_CONTACT_META
      : KNIGHT_REARM_CONTACT_META
    const contact = resolveBossSpriteArtPlacement(bossId, 'lanceSweep_contact', contactMeta,
      centerX, footY, displayHeight, facingMul, options)
    const sx = contact.imageRect.width / contactMeta.w
    const sy = contact.imageRect.height / contactMeta.h
    const imageRect = {
      x: contact.imageRect.x - KNIGHT_REARM_PADDING.x * sx,
      y: contact.imageRect.y - KNIGHT_REARM_PADDING.y * sy,
      width: meta.w * sx, height: meta.h * sy,
    }
    return { ...contact, imageKey: key, sourceSize: { width: meta.w, height: meta.h }, imageRect,
      opaqueRect: { x: imageRect.x + meta.x0 * sx, y: imageRect.y + meta.y0 * sy,
        width: Math.max(1, meta.x1 - meta.x0) * sx, height: Math.max(1, meta.y1 - meta.y0) * sy } }
  }
  const params = resolveBossSpriteScaleParams(bossId, options.variant ?? null, key, options.fellBackToFlat ?? false)
  const devFix = import.meta.env.DEV
    ? (globalThis as { __poseFix?: Record<string, number> }).__poseFix?.[key] : undefined
  const devGround = import.meta.env.DEV
    ? (globalThis as { __groundFix?: Record<string, number> }).__groundFix?.[key] : undefined
  const fix = (devFix ?? params.poseScaleFix)
    * (bossId === 'vigil-knight' ? knightHelmetScaleFactor(key, knightHelmetScaleEnabled()) : 1)
  const base = params.refH !== undefined ? displayHeight * fix / params.refH
    : displayHeight * (AREA_SCALE_K_BY_BOSS[bossId] ?? AREA_SCALE_K) * fix / Math.sqrt(meta.area)
  // The two suri canvases share the upper body. Keep one pixel scale and one floor/hip anchor;
  // area normalization would magnify the narrower drawn-in leg and make the helmet jump.
  const suri = bossId === 'vigil-knight' && (key === 'suri_open' || key === 'suri_draw')
  // 騎士の固有の致命の新しい3枚 (DEV ?execUnique=1・src/exec_unique.ts): 兜の大きさで合わせた固定の倍率・体の中心の画素をボスの x に置く。
  const uniqueArt = bossId === 'vigil-knight' ? KNIGHT_EXEC_ART_PLACEMENT[key] : undefined
  const fixedBase = uniqueArt ? displayHeight * uniqueArt.pxScale / 216
    : suri ? displayHeight * 0.124 / 216
    : bossId === 'vigil-knight' && key === 'lanceSweep_reset' ? displayHeight * 0.163 / 216 : base
  // Rest-mid recovery art is registered to idle's helmet pixel scale and ground.
  // Area normalization would enlarge a narrower stance. Use idle's pixel scale.
  let restBase = fixedBase
  // 崩れから立ち上がる2枚 (rise1/rise2・src/boss_rise.ts) も、構えと同じ画素の倍率で描く (絵は構えの倍率に合わせて縮めて置いた)。
  if ((bossId === 'vigil-knight' && (key.startsWith('rest_from_') || key.startsWith('rest_rise_'))) || key === 'rise1' || key === 'rise2') {
    // 介錯は刃・砲のフォルダごとに読む (cache は 'the-executioner#blade' 等にしか無い)。
    const variant = options.variant ?? null
    const idleImage = (cache.get(cacheKeyOf(bossId, variant)) ?? cache.get(bossId))?.get('idle')
    const idleMeta = idleImage?.complete ? getSpriteMeta(bossId, variant, 'idle', idleImage) : null
    if (idleMeta) {
      const idle = resolveBossSpriteArtPlacement(bossId, 'idle', idleMeta, centerX, footY, displayHeight, facingMul, { variant })
      restBase = idle.imageRect.width / idleMeta.w
      // 溶け合う相手 (崩れ・構え) より一回り大きく描かれている物は、相手の大きさへ縮める (10-30・重ね絵で同じ姿勢と確かめた)。
      if (bossRiseFitOn()) restBase *= BOSS_RISE_SCALE[bossId]?.[key as 'rise1' | 'rise2'] ?? 1
    }
  }
  return resolveBossSpritePlacement({
    imageKey: key, imageWidth: meta.w, imageHeight: meta.h, opaqueBounds: meta,
    pivotX: centerX, pivotY: footY + 12,
    scaleX: restBase * (options.scaleX ?? 1), scaleY: restBase * (options.scaleY ?? 1),
    offsetX: (options.offsetX ?? 0) + (bossId === 'vigil-knight' ? (KNIGHT_SWEEP_ART[key]?.shiftX ?? 0) * displayHeight / 216 : 0),
    offsetY: options.offsetY ?? 0, facingMul, rotationRad: options.rotationRad ?? 0,
    // Front sole ends at source y=863 in both files; bbox bottoms differ with the rear leg.
    groundAnchorRatio: suri ? (863 - meta.y0) / (meta.y1 - meta.y0) : devGround ?? params.groundAnchorRatio,
    horizontalAnchorX: suri ? 826 : uniqueArt ? uniqueArt.anchorX
      : bossId === 'bride' && brideRedrawAnchorX(key) !== undefined ? brideRedrawAnchorX(key)
      : bossId === 'bride' && BRIDE_EXEC_ART_PLACEMENT[key] ? BRIDE_EXEC_ART_PLACEMENT[key]!.anchorX
      : bossId === 'bride' && BRIDE_ISSEN_ANCHOR_X[key] !== undefined ? BRIDE_ISSEN_ANCHOR_X[key] : options.horizontalAnchorX
      ?? (options.footAnchor ? options.footAnchor.footX - options.footAnchor.fromCenterPx / restBase : undefined)
      ?? (bossId === 'vigil-knight' && key in KNIGHT_REST_ANCHOR_SHIFT_PX
        ? (meta.x0 + meta.x1) / 2 + (knightSmoothRestShift(key) ?? KNIGHT_REST_ANCHOR_SHIFT_PX[key]!) : undefined)
      ?? (bossId === 'crowned-king-awoken' ? AWOKEN_POCKET_SPIN_ART[key]?.anchorX ?? AWOKEN_BACK_HOP_ANCHOR_X[key] ?? AWOKEN_FLOORBITE_ANCHOR_X[key] : undefined)
      ?? (bossId === 'dream-hound' ? HOUND_PASS_ANCHOR_X[key] : undefined)
      ?? (bossId === 'blade-dancer' ? bladeDancerAnchorX(key) : undefined),
  })
}

/** Fixed-pose world FX use the loaded image's cached alpha>16 metadata, never proxy dimensions. */
export function loadedBossSpriteArtPlacement(
  bossId: string, key: SpriteKey, x: number, footY: number, height: number, facing: Boss['facing'],
): BossSpritePlacement | null {
  const img = cache.get(bossId)?.get(key)
  if (!img?.complete || !img.naturalWidth) return null
  const meta = getSpriteMeta(bossId, null, key, img)
  if (!meta) return null
  return resolveBossSpriteArtPlacement(bossId, key, meta, x, footY, height,
    facing === (SOURCE_FACING[bossId] ?? 'right') ? 1 : -1)
}

// ===== 芯 (BOSS_CORE) の実寸計測ツール (2026-09-06 便1) =====
//   docs/candidates/2026-09-06-boss-core-real-size-fable-design.md §1-2。
//   🔴 scripts/measure_boss_body.py のように定数を写さない = getSpriteMeta / resolveBossSpriteScaleParams を
//   **呼ぶ**ことで、本体の描画式 (rectFor の base) と1画素もズレない値を出す。DEV 専用 (__measureBossCore)。
export type BossCoreBandName = 'head' | 'chest' | 'waist' | 'hip' | 'knee'
// 帯の高さ範囲 (不透明 bbox の上端=0 / 下端=1)。 人型は「尻」帯・四足は「胸」帯が正 (§1-1)。
const BOSS_CORE_BANDS: Record<BossCoreBandName, [number, number]> = {
  head: [0, 0.15],
  chest: [0.25, 0.45],
  waist: [0.45, 0.60],
  hip: [0.60, 0.75],
  knee: [0.75, 0.88],
}

export type BossCoreBandMeasurement = {
  band: BossCoreBandName
  rangeFrac: [number, number]
  rows: number
  // 以下は画面 px (描画倍率を掛けた後)。 行が1つも無い/全透明なら null。
  wideWMed: number | null
  wideWMin: number | null
  wideWMax: number | null
  // そのランの中心が bbox 中心 (= boss.x) からどれだけずれているか (右が+)。
  offsetMed: number | null
  // bbox 中心の列がそのランに入っている行の割合 (0..1)。
  centerInRunFrac: number | null
  // 行全体 (最初〜最後の不透明画素) の幅の中央値。 widestRun と大きく離れていたら
  //   「腕と体で細切れ」(騎士のように機械で測れない体型) のサイン。
  rowFullWMed: number | null
}

export type BossCoreMeasurement = {
  bossId: string
  variant: string | null
  key: SpriteKey
  displayHeight: number
  scale: number       // 画面px / 画像px (idle等・手続き変位なしの base。rectFor の base と同じ式)
  bboxWidthPx: number  // 画面px
  bboxHeightPx: number
  bodyHalfWidthPx: number // 画面px。 bbox 半幅 (reach 系の別道具・§1-2 item4)
  bands: BossCoreBandMeasurement[]
}

function median(xs: number[]): number {
  if (xs.length === 0) return NaN
  const s = [...xs].sort((a, b) => a - b)
  const n = s.length
  const m = Math.floor(n / 2)
  return n % 2 ? s[m]! : (s[m - 1]! + s[m]!) / 2
}

// 1行 [x0,x1] の中で最も広い連続した不透明 (alpha>16) 区間。 無ければ null。
function widestRunInRow(data: Uint8ClampedArray, w: number, y: number, x0: number, x1: number): { start: number; end: number } | null {
  let bestStart = -1, bestLen = 0, curStart = -1
  for (let x = x0; x <= x1; x++) {
    const alpha = data[(y * w + x) * 4 + 3]!
    if (alpha > 16) {
      if (curStart < 0) curStart = x
      const len = x - curStart + 1
      if (len > bestLen) { bestLen = len; bestStart = curStart }
    } else {
      curStart = -1
    }
  }
  return bestStart < 0 ? null : { start: bestStart, end: bestStart + bestLen - 1 }
}

// 1行 [x0,x1] の中の最初〜最後の不透明画素 (=行全幅)。 無ければ null。
function rowFullSpan(data: Uint8ClampedArray, w: number, y: number, x0: number, x1: number): { start: number; end: number } | null {
  let s = -1, e = -1
  for (let x = x0; x <= x1; x++) {
    if (data[(y * w + x) * 4 + 3]! > 16) { if (s < 0) s = x; e = x }
  }
  return s < 0 ? null : { start: s, end: e }
}

// ゲーム本体と同じ描画式で「芯」の帯を測る (idle 等・手続き変位のない画像専用)。
//   displayHeight は呼び出し側 (main.ts) が render.ts の bossDisplayHeightFor(bossId) から渡す
//   (sprites.ts は render.ts を import しない = 循環importを避ける・定数はコピーしない)。
export function measureBossCoreBands(
  bossId: string, variant: string | null, key: SpriteKey, displayHeight: number,
): BossCoreMeasurement | null {
  if (typeof document === 'undefined') return null
  const ck = cacheKeyOf(bossId, variant)
  const variantMap = cache.get(ck)
  const img = variantMap?.get(key) ?? cache.get(bossId)?.get(key)
  if (!img || !img.complete) return null
  const meta = getSpriteMeta(bossId, variant, key, img)
  if (!meta) return null

  const fellBackToFlat = variant !== null && !variantMap?.get(key)
  const scaleParams = resolveBossSpriteScaleParams(bossId, variant, key, fellBackToFlat)
  const fix = scaleParams.poseScaleFix
  const refH = scaleParams.refH
  // rectFor の base と同じ式 (scaleK=bobK=atkScaleX=atkScaleY=1・idle/手続き変位なし前提)。
  const scale = refH !== undefined
    ? displayHeight * fix / refH
    : displayHeight * (AREA_SCALE_K_BY_BOSS[bossId] ?? AREA_SCALE_K) * fix / Math.sqrt(meta.area)

  if (!metaScratch) metaScratch = document.createElement('canvas')
  metaScratch.width = meta.w
  metaScratch.height = meta.h
  const c = metaScratch.getContext('2d', { willReadFrequently: true })
  if (!c) return null
  c.clearRect(0, 0, meta.w, meta.h)
  c.drawImage(img, 0, 0)
  let data: Uint8ClampedArray
  try { data = c.getImageData(0, 0, meta.w, meta.h).data } catch { return null }

  const bboxCenterX = meta.cx
  const bboxH = meta.y1 - meta.y0
  const bands: BossCoreBandMeasurement[] = (Object.keys(BOSS_CORE_BANDS) as BossCoreBandName[]).map((band) => {
    const [f0, f1] = BOSS_CORE_BANDS[band]!
    const yFrom = Math.round(meta.y0 + f0 * bboxH)
    const yTo = Math.round(meta.y0 + f1 * bboxH)
    const wideWs: number[] = []
    const offsets: number[] = []
    const fullWs: number[] = []
    let centerInRun = 0
    let rows = 0
    for (let y = yFrom; y <= yTo; y++) {
      const run = widestRunInRow(data, meta.w, y, meta.x0, meta.x1)
      const full = rowFullSpan(data, meta.w, y, meta.x0, meta.x1)
      if (!run || !full) continue
      rows += 1
      wideWs.push(run.end - run.start + 1)
      offsets.push((run.start + run.end) / 2 - bboxCenterX)
      fullWs.push(full.end - full.start + 1)
      if (bboxCenterX >= run.start && bboxCenterX <= run.end) centerInRun += 1
    }
    return {
      band,
      rangeFrac: [f0, f1],
      rows,
      wideWMed: rows ? median(wideWs) * scale : null,
      wideWMin: rows ? Math.min(...wideWs) * scale : null,
      wideWMax: rows ? Math.max(...wideWs) * scale : null,
      offsetMed: rows ? median(offsets) * scale : null,
      centerInRunFrac: rows ? centerInRun / rows : null,
      rowFullWMed: rows ? median(fullWs) * scale : null,
    }
  })

  return {
    bossId, variant, key, displayHeight, scale,
    bboxWidthPx: (meta.x1 - meta.x0) * scale,
    bboxHeightPx: (meta.y1 - meta.y0) * scale,
    bodyHalfWidthPx: (meta.x1 - meta.x0) * scale / 2,
    bands,
  }
}

// ===== per-boss 描画設定フラグ (1枚絵方式 §0.2 / 2026-06-15 vigil-knight パイロット) =====
//   「crossfade は輪郭一致差分だけ / 速い動きは離散スナップ / 半透明overlay禁止」の共通方針をボス別に宣言。
//   適用は1体ずつ (他ボスを巻き込まない)。
export const PARRIED_OVERLAY_DISABLED = new Set(['vigil-knight', 'bride', 'the-executioner', 'crowned-king-awoken'])  // 半透明 parried overlay スキップ → recoil+FXで出す (花嫁=騎士方式へ揃え 2026-06-26 / 処刑人=parried絵を廃止し recoil 方式へ 2026-07-05 / 覚醒王=弾き主体ボスの二重写し回避 2026-07-07)
export const IDLE_BREATH_DISABLED = new Set(['vigil-knight'])      // idle↔idle_breath crossfade スキップ → 1枚固定
export const WALK_DISCRETE_SNAP = new Set(['vigil-knight', 'the-executioner', 'crowned-king-awoken', 'dream-hound']) // walk blend → 2値ハードカット。処刑人=重量級2枚歩行を溶かさない
// 王 (dream-hound) の経緯 (2026-08-11): 一度ここから外して crossfade を試した。
//   きっかけ = ユーザー実プレイ「絵がかくかく動いてる感じ」。だが原因は切り替え方ではなく
//   **2枚の中身**だった (「足を閉じた絵」が閉じておらず、立ち姿と大股開きの2枚だった)。
//   足元の幅 = 覚醒王 18%⇄114% に対し、当時の王は 94%⇄122%。
//   覚醒王も切り替え式で自然に見えている以上、方式は揃える。crossfade へは戻さない。
// 2枚を体ごと滑らせて歩く2体だけ (騎士=段の歩み・介錯=踏み込みは別の作り)。
const WALK_SURGE_BOSSES = new Set(['crowned-king-awoken', 'dream-hound'])
export const IDLE_BOB_DISABLED = new Set(['vigil-knight'])         // procedural bob 無効化 → 重い静止感

function computeSpriteMeta(img: HTMLImageElement): SpriteMeta | null {
  const w = img.naturalWidth, h = img.naturalHeight
  if (!w || !h || typeof document === 'undefined') return null
  if (!metaScratch) metaScratch = document.createElement('canvas')
  metaScratch.width = w; metaScratch.height = h
  const c = metaScratch.getContext('2d', { willReadFrequently: true })
  if (!c) return null
  c.clearRect(0, 0, w, h); c.drawImage(img, 0, 0)
  let data: Uint8ClampedArray
  try { data = c.getImageData(0, 0, w, h).data } catch { return null }
  let x0 = w, y0 = h, x1 = -1, y1 = -1, area = 0
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      if (data[(y * w + x) * 4 + 3]! > 16) {
        area++
        if (x < x0) x0 = x; if (x > x1) x1 = x
        if (y < y0) y0 = y; if (y > y1) y1 = y
      }
    }
  }
  if (x1 < 0) return null
  return { x0, y0, x1, y1, cx: (x0 + x1) / 2, w, h, area }
}
function getSpriteMeta(
  bossId: string,
  variant: string | null,
  key: SpriteKey,
  img: HTMLImageElement,
): SpriteMeta | null {
  const ck = cacheKeyOf(bossId, variant)
  let m = spriteMeta.get(ck)
  if (!m) { m = new Map(); spriteMeta.set(ck, m) }
  const cached = m.get(key)
  if (cached) return cached
  const computed = computeSpriteMeta(img)
  // 武器だけ直した絵 (_wfix) は、置き位置を決める範囲を元の絵のものに固定する (体の位置・接地の比率をそのまま使う)。
  const body = EXEC_WEAPON_FIX_BODY_BOUNDS[SPRITE_FILE_OVERRIDE.get(`${bossId}#${variant ?? ''}/${key}`) ?? '']
  if (computed && body && body.w === computed.w && body.h === computed.h) {
    const [x0, y0, x1, y1] = body.bounds
    Object.assign(computed, { x0, y0, x1, y1, cx: (x0 + x1) / 2, area: body.area })
  }
  if (computed) m.set(key, computed)
  return computed
}

/**
 * 初めて描く時に重い下ごしらえ (画像の展開と、不透明範囲を測る画素の読み取り) を、手すきの時間に前もって済ませる (11-04)。
 *   測る値は描く時と同じ getSpriteMeta なので、見た目もタイミングも変わらない。1回の手すきに1枚ずつ。
 *   剣舞の致命の入りで、初めての1回だけ1コマ 40〜100ms 止まっていた分 (docs/research/2026-11-04-blade-first-execution-hitch.md)。
 */
export function warmBossSpriteMeta(bossId: string, keys: readonly string[]): void {
  if (typeof document === 'undefined') return
  // 11-03: 致命の外でも、初めて描く1コマが 50〜95ms になっていた (全ボス・docs/research/2026-11-03-first-draw-warm-all-bosses.md)。
  //   一覧の絵を先に、残りの読んだ絵 (見た目の版ごと) も全部、手すきに1枚ずつ測る。
  const queue: Array<[string | null, SpriteKey, HTMLImageElement]> = []
  const seen = new Set<string>()
  for (const variant of [null, ...(BOSS_SPRITE_VARIANTS[bossId] ?? [])]) {
    const map = cache.get(cacheKeyOf(bossId, variant))
    if (!map) continue
    for (const k of [...keys.filter((k) => map.has(k as SpriteKey)) as SpriteKey[], ...map.keys()]) {
      const id = `${variant ?? ''}/${k}`
      if (seen.has(id)) continue
      seen.add(id)
      queue.push([variant, k, map.get(k)!])
    }
  }
  const later = (fn: () => void): void => {
    if (typeof requestIdleCallback === 'function') requestIdleCallback(fn, { timeout: 1500 })
    else setTimeout(fn, 50)
  }
  const next = (): void => {
    const item = queue.shift()
    if (item === undefined) return
    const [variant, key, img] = item
    const measure = (): void => {
      try { if (img.naturalWidth) getSpriteMeta(bossId, variant, key, img) } catch { /* 描く時に測り直す */ }
      later(next)
    }
    img.decode().then(() => later(measure), () => later(next))
  }
  later(next)
}

// 介錯の武器を直した8枚の、元の絵の不透明範囲 (alpha>16) を新しい絵の座標へ移したもの (2026-10-19)。
//   キー = ファイルの幹。w/h = 新しい絵の寸法 (違う絵が来たら使わない)。area = 元の絵の面積。
//   出どころ = 元の webp を computeSpriteMeta と同じ閾値で測り、武器を伸ばした分の枠のずれを足した値
//   (記録 = docs/research/2026-10-19-executioner-weapon-fix.md)。
//   origin = 元の絵の (0,0) が新しい絵のどこにあるか (枠を左・上へ広げた絵だけ)。元の絵の画素で持つ表
//   (致命の足の位置 EXEC_FINISHER_REAR_BOOT_X など) は、この分を足して読む = execWeaponFixOriginX。
export const EXEC_WEAPON_FIX_BODY_BOUNDS: Record<string, { w: number; h: number; bounds: [number, number, number, number]; area: number; origin?: [number, number] }> = {
  slam_windup_wfix: { w: 1181, h: 1092, bounds: [39, 4, 1176, 1012], area: 523545 },
  slam_hold_wfix: { w: 1201, h: 1027, bounds: [43, 4, 1196, 945], area: 494863 },
  overhead_recover_wfix: { w: 963, h: 1273, bounds: [2, 4, 958, 1269], area: 568363 },
  quake_contact_wfix: { w: 1254, h: 1254, bounds: [101, 104, 1215, 1197], area: 657952 },
  overhead_contact_wfix: { w: 1186, h: 1137, bounds: [4, 4, 1181, 1054], area: 537445 },
  sweep_coil_wfix: { w: 1503, h: 1103, bounds: [285, 28, 1398, 1073], area: 451712 },
  mortar_aim_wfix: { w: 1254, h: 1387, bounds: [218, 149, 1236, 1368], area: 499906 },
  mortar_fire_wfix: { w: 1280, h: 1254, bounds: [193, 204, 1262, 1235], area: 493329 },
  // 2026-10-31 砲の口の襟を描き足した3枚 (docs/research/2026-10-31-cannon-collar.md)。棘が左・上へ出た分だけ枠を広げた。
  overhead_raise_wfix: { w: 895, h: 1674, bounds: [4, 38, 890, 1667], area: 640540, origin: [2, 34] },
  downswing_smear_wfix: { w: 1305, h: 1180, bounds: [37, 5, 1302, 1176], area: 757703, origin: [34, 2] },
  exec_contact_wfix: { w: 1361, h: 1130, bounds: [33, 3, 1356, 1125], area: 767313, origin: [30, 2] },
}
/** 元の絵の画素 x で持つ値を、いま読んでいる絵 (_wfix で枠を左へ広げた絵) の画素 x へ移す分。違う寸法の絵なら 0。 */
export function execWeaponFixOriginX(bossId: string, variant: string | null, key: string, meta: { w: number; h: number }): number {
  const body = EXEC_WEAPON_FIX_BODY_BOUNDS[SPRITE_FILE_OVERRIDE.get(`${bossId}#${variant ?? ''}/${key}`) ?? '']
  return body?.origin && body.w === meta.w && body.h === meta.h ? body.origin[0] : 0
}
function shiftFootAnchorX<T extends { footX: number }>(anchor: T | undefined, dx: number): T | undefined {
  return anchor && dx ? { ...anchor, footX: anchor.footX + dx } : anchor
}

// dream-hound の動画クリップキー (Phase 1)。 未配置のキーはロード失敗 → 1枚絵にフォールバック。
// 2026-06-09: dream-hound クリップを戦闘から退役 (archive済)。 clipCache はロードせず、
//   タイムライン/1枚絵経路を有効化する。 将来のシネマティクス用に clip 処理コードは保持。
const BOSS_CLIP_KEYS: Record<string, string[]> = {}
// DEV ?execVideoMix=1 (2026-10-21): 介錯の振り下ろしを、動画AIのためと決め＋ゲームの振りの絵を混ぜた48コマで見る比較版。
//   絵 = public/sprites/bosses/the-executioner/clips/attack_exec_maulDrop/・記録 = docs/research/2026-10-21-video-ai-trial-result.md。配布版は URL を読まない。
if (import.meta.env.DEV && typeof location !== 'undefined'
  && new URLSearchParams(location.search).get('execVideoMix') === '1') {
  BOSS_CLIP_KEYS['the-executioner'] = ['attack:exec_maulDrop']
}

export function preloadBossSprites(bossId: string): void {
  if (bossId === 'crowned-king-awoken') preloadWallHuntArt()
  if (!SPRITE_BOSSES.has(bossId)) return
  // 動画クリップ (Phase 1): atlas を非同期ロード。 cache.has の早期 return より前に行う。
  if (!clipCache.has(bossId)) {
    const cm = new Map<string, LoadedBossClip>()
    clipCache.set(bossId, cm)
    for (const key of BOSS_CLIP_KEYS[bossId] ?? []) {
      void loadBossClip(bossId, key).then((c) => { if (c) cm.set(key, c) })
    }
  }
  // バリアントを持つボスのキャッシュの鍵は `${bossId}#${variant}` なので、bossId だけを見ると毎回読み直していた。
  //   先読み (src/prefetch_next.ts) の後にボスが替わった時にもう一度呼ばれ、読めた絵を読み込み中の絵で上書きして
  //   前のポーズの絵が一瞬出ていた (11-01)。最初のバリアントの鍵で「読み始め済み」を見る。
  const firstVariant = BOSS_SPRITE_VARIANTS[bossId]?.[0] ?? null
  if (cache.has(bossId) || cache.has(cacheKeyOf(bossId, firstVariant))) return
  if (typeof Image === 'undefined') return
  const tlImageKeys = new Set<string>()
  for (const t of Object.values(BOSS_ATTACK_TIMELINES[bossId] ?? {})) {
    for (const k of t) tlImageKeys.add(k.image)
  }
  const omitted = new Set(STATE_KEYS_OMITTED_BY_BOSS[bossId] ?? [])
  const keysToLoad = new Set<SpriteKey>([
    ...STATE_KEYS.filter((k) => !omitted.has(k)),
    ...([...tlImageKeys] as SpriteKey[]),
    ...(EXTRA_POSE_KEYS_BY_BOSS[bossId] ?? []),
    ...(RUNTIME_EXTRA_POSE_KEYS.get(bossId) ?? []),
    ...(WALK_CYCLE_OVERRIDE.get(bossId)?.keys ?? []),
    // 致命を弾かれた時だけの絵 (10-21・src/exec_parry_recoil.ts)。並びに出ないのでここで読む。
    ...(EXEC_PARRIED_ART_1021_BOSSES.includes(bossId) ? [EXEC_PARRIED_ART_1021 as SpriteKey] : []),
    // ?awokenParried=c|d|e の時だけ読む (10-31 の描き直し3案)。
    ...(bossId === 'crowned-king-awoken' && awokenParriedArtKey() ? [awokenParriedArtKey() as SpriteKey] : []),
    // ?devourJaw=1 の時だけ読む (配布版は読まない = 通信が増えない)。
    ...(bossId === 'crowned-king-awoken' && awokenDevourJawEnabled() ? [AWOKEN_DEVOUR_JAW_IMAGE as SpriteKey] : []),
    // ?awokenDevour=gladius の時だけ読む (宙で口を開けて首を伸ばす絵・src/awoken_devour_pounce.ts)。
    ...(bossId === 'crowned-king-awoken' && awokenDevourMode() === 'pounce' ? [POUNCE_REACH_IMAGE as SpriteKey, POUNCE_LAUNCH_IMAGE as SpriteKey] : []),
    // 騎士の固有の致命の新しい3枚 (DEV ?execUnique=1 の時だけ読む = 配布版は通信が増えない・src/exec_unique.ts)。
    ...(bossId === 'vigil-knight' && execUniqueOn('vigil-knight') ? Object.values(KNIGHT_EXEC_ART) as unknown as SpriteKey[] : []),
    ...(bossId === 'bride' && execUniqueOn('bride') ? Object.values(BRIDE_EXEC_ART) as unknown as SpriteKey[] : []),
  ])
  // 登録されたバリアントぶんを、それぞれ独立にロードする。
  //   🔵2026-08-20: バリアントを持つボスは**平置き (variant=null) を読まない**。
  //     介錯の平置きは旧デザインで、_archived-2026-08-20-maul-olddesign/ へ退避したため、
  //     読みに行くと 54件が空振りする (実測)。bossSpriteVariant も null を返さなくなったので
  //     平置きのキャッシュは誰も引かない。バリアントを持たないボスは従来どおり平置き1本。
  const variants = BOSS_SPRITE_VARIANTS[bossId]
  const passes: (string | null)[] = variants && variants.length > 0 ? [...variants] : [null]
  for (const variant of passes) {
    loadVariant(bossId, variant, keysToLoad)
  }
}

// 1バリアントぶんのプリロード。 variant=null は従来の平置きパス。
// DEV 用のファイル差し替え (2026-09-03 第8便): 同じ SpriteKey のまま、実際に読みに行くファイルの幹だけ
//   差し替える (候補比較用。sweep_windup_low_a / _low_b / _twist_a / _twist_b の切替に使う)。
//   キー = `${bossId}#${variant}/${key}` / 値 = ファイル名の幹 (拡張子・パスは付けない)。null で解除。
// 2026-09-12 ユーザー検収・main統合承認。旧資産は保持し、論理キーを変えずに採用絵へ配線。
const SPRITE_FILE_OVERRIDE = new Map<string, string>([
  // 剣舞 (2026-10-19): 配布版は5キーとも B の立ち絵 (idle) を読む。旧は DEV ?bladeArt=old。
  //   技ごとの描き直し (構え・振り・弾かれ・崩れ・撃破) は DEV ?bladeMoves=1 の比較版 (src/blade_dancer_art.ts)。
  ['blade-dancer#/telegraph', 'idle'],
  ['blade-dancer#/active', 'idle'],
  ['blade-dancer#/parried', 'idle'],
  ['blade-dancer#/staggered', 'idle'],
  ['blade-dancer#/defeat', 'idle'],
  ['crowned-king-awoken#/bite_contact', 'bite_contact_closed_0912'],
  ['crowned-king-awoken#/bite_step', 'bite_step_closed_0912'],
  ['crowned-king-awoken#/bite_follow', 'bite_follow_0910'],
  ['crowned-king-awoken#/bite_recover', 'bite_recover_0910'],
  ['crowned-king-awoken#/dash_crouch', 'dash_crouch_fangs_0912'],
  ['crowned-king-awoken#/dash_form', 'dash_form_fangs_0912'],
  ['crowned-king-awoken#/wall_kick', 'wall_kick_fangs_0912'],
  ['crowned-king-awoken#/rush_overextend', 'rush_overextend_fangs_0912'],
  // 大暴れ第5案の移動中に使う「牙を伏せた」キー = 2026-09-12 に差し替える前の原画そのもの。
  //   ファイルは増やさず、別キーから同じ原画を読む (2026-09-20 ユーザー「移動は牙を出さない」)。
  ['crowned-king-awoken#/wall_kick_plain_0920', 'wall_kick'],
  ['crowned-king-awoken#/dash_crouch_plain_0920', 'dash_crouch'],
  ['crowned-king-awoken#/dash_form_plain_0920', 'dash_form'],
  // 介錯の致命「執行」の刃の打点 = 前足のブーツが見える新しい絵 (2026-10-09 ユーザー採用 exec.contactArt「使う」)。
  //   旧 exec_contact.png/.webp は同じ場所に残してある (DEV ?execFinisherFoot=old|foot で読む・src/executioner_finisher_foot.ts)。
  ['the-executioner#blade/exec_contact', 'exec_contact_v2'],
  // 介錯の武器の長さと太さを待機の絵に揃えた8枚 (2026-10-19)。体の画素は元の絵のまま・武器だけ伸ばし/細めた。
  //   旧の絵は同じ場所に残してある (DEV ?execWeapon=old で読む・src/executioner_weapon_fix.ts)。
  //   置き位置は EXEC_WEAPON_FIX_BODY_BOUNDS で元の絵の範囲に固定する (武器が伸びても体が動かない)。
  ...EXEC_WEAPON_FIX_KEYS.map(([v, key]) => [`the-executioner#${v}/${key}`, `${key}_wfix`] as [string, string]),
])
/** そのキーで実際に読むファイルの幹 (差し替え適用後)。差し替えが無ければキー名そのもの。 */
export function bossSpriteFileStem(bossId: string, key: string, variant: string | null = null): string {
  return SPRITE_FILE_OVERRIDE.get(`${bossId}#${variant ?? ''}/${key}`)
    ?? (bossId === 'vigil-knight' && variant === null && key === 'lanceSweep_reset' ? 'prev-reset-0930/lanceSweep_reset'
      // 足を寄せる絵 (knight_rest.ts・既定 more) は別フォルダ (直下の webp・グレード済みの揃いのガードに入れない)。
      : bossId === 'vigil-knight' && variant === null && (key.startsWith('rest_from_') || key.startsWith('rest_rise_')) ? `rest-mid-1008/${key}`
      // 固有の致命の新しい3枚 (DEV ?execUnique=1) は 10-09 に色合わせして直下＋graded/ へ移した (キー名そのまま = graded/ を読む)。
      : key)
}
export function setSpriteFileOverride(bossId: string, variant: string, key: SpriteKey, stem: string | null): void {
  const ck = `${bossId}#${variant}/${key}`
  if (stem === null) {
    SPRITE_FILE_OVERRIDE.delete(ck)
  } else {
    SPRITE_FILE_OVERRIDE.set(ck, stem)
  }
}

function loadVariant(bossId: string, variant: string | null, keysToLoad: Set<SpriteKey>): void {
  const map = new Map<SpriteKey, HTMLImageElement>()
  const loadedMap = new Map<SpriteKey, boolean>()
  const variantFileMap = new Map<SpriteKey, boolean>()
  const sourceFileMap = new Map<SpriteKey, boolean>()
  const ext = SPRITE_EXT_BY_BOSS[bossId] ?? 'png'
  // 平置き経路にも SPRITE_FILE_OVERRIDE を効かせる (2026-09-07)。
  //   キーは variant='' として登録した時と同じ書式 `${bossId}#/${key}` に揃える。
  //   誰も登録していなければ従来どおりキー名がそのままファイル名になる (挙動不変)。
  const flatPath = (key: SpriteKey) => knightSpriteSource(bossId, key, bossSpriteFileStem(bossId, key), ext, knightColorGradedEnabled())
  for (const key of keysToLoad) {
    const img = new Image()
    let fellBack = false
    img.onload = () => {
      loadedMap.set(key, true)
      variantFileMap.set(key, variant === null || !fellBack)
      sourceFileMap.set(key, proxyUrl === null && (variant === null || !fellBack))
    }
    // 読めなかった絵は、ずっと「読み込み中」の代わりの絵になって気づけなかった (11-01・洗い出しの D)。開発版で知らせる。
    img.onerror = () => {
      if (import.meta.env.DEV) console.warn(`[sprites] ${bossId}${variant ? `#${variant}` : ''}/${key} を読めなかった: ${img.src}`)
    }
    // 全ボス DEVプロキシ (bossId ごと・src/boss_proxy.ts): 実行時生成の仮モデル (dataURL) を
    //   ファイルの代わりに流す。 既存の面積正規化/接地/crossfade/共通演出は全部そのまま通る。
    //   最終絵の差し替え = PNG 配置 + boss_proxy.ts のボス別フラグ off (このループは不変)。
    const proxyUrl = getBossProxyDataUrl(bossId, key, SOURCE_FACING[bossId] ?? 'right')
    if (proxyUrl) {
      img.src = proxyUrl
    } else if (variant) {
      // 🔴 2026-08-20 廃止: ~~バリアント画像がまだ無い間は平置きへ落とす (1回だけ)~~。
      //   ~~これが「武器1種ずつ絵を入れられる」根拠~~ だったが、**落ちる先が旧デザイン (別人) だった**。
      //   介錯の平置きは 2026-08-12 に退役したフードの巨躯+大槌で、
      //   _archived-2026-08-20-maul-olddesign/ へ退避した。落とすと「1枚だけ別人が出る」事故になる
      //   (tests/executioner_sprite_wiring.test.ts の 2026-08-05 の注記が警告していたとおり)。
      //   → 落とさない。絵が無ければ**出ない**。どのキーが欠けてよいかは
      //     tests/boss_sprite_variant_completeness.test.ts が宣言つきで機械ガードしている。
      //   ⚠️ 将来「平置きがそのボスの正しい絵」である新しいバリアントボスを足す時は、
      //     ここを戻すのではなく、そのボスの全キーをバリアントフォルダへ置く。
      void fellBack
      const stem = SPRITE_FILE_OVERRIDE.get(`${bossId}#${variant}/${key}`) ?? key
      // 形態別フォルダに実在しない絵は取りに行かない (一覧 = src/sprite_manifest.ts)。空振りは soft404 の無駄な通信だった。
      //   src を付けない Image は「画像として読めない」(complete=true・naturalWidth=0) で、以前の空振りと描く側から見て同じ。
      if (variantSpriteFileListed(bossId, variant, stem)) img.src = `/sprites/bosses/${bossId}/${variant}/${stem}.${ext}`
    } else {
      img.src = flatPath(key)
    }
    map.set(key, img)
    loadedMap.set(key, false)
    variantFileMap.set(key, false)
    sourceFileMap.set(key, false)
  }
  const ck = cacheKeyOf(bossId, variant)
  cache.set(ck, map)
  loaded.set(ck, loadedMap)
  variantFileLoaded.set(ck, variantFileMap)
  sourceFileLoaded.set(ck, sourceFileMap)
}

// DEV 用: 指定ボスのスプライトキャッシュを破棄して再ロードさせる (プロキシ⇔実絵の切替に使う)。
//   次の preloadBossSprites 呼び出し (main.ts のボス切替検出) で再充填される。
export function clearBossSpriteCache(bossId: string): void {
  cache.delete(bossId)
  loaded.delete(bossId)
  variantFileLoaded.delete(bossId)
  sourceFileLoaded.delete(bossId)
  lastDrawableKeyByVariant.delete(bossId)
  // バリアントぶんも一緒に捨てる (捨て漏れると DEV の差し替えが片方だけ古いまま残る)。
  for (const v of BOSS_SPRITE_VARIANTS[bossId] ?? []) {
    const ck = cacheKeyOf(bossId, v)
    cache.delete(ck)
    loaded.delete(ck)
    variantFileLoaded.delete(ck)
    sourceFileLoaded.delete(ck)
    lastDrawableKeyByVariant.delete(ck)
    spriteMeta.delete(ck)
  }
  spriteMeta.delete(bossId)
  clipCache.delete(bossId)
}

/**
 * そのボスの絵を全部取りに行き終えたか (読めた・読めなかったの両方を終わりと数える)。
 * 戦いへ抜ける暗転を延ばす判定に使う (src/battle_asset_gate.ts)。絵を持たないボスは待つ物が無いので true。
 */
export function bossSpritesSettled(bossId: string): boolean {
  if (!SPRITE_BOSSES.has(bossId) || typeof Image === 'undefined') return true
  const variants = BOSS_SPRITE_VARIANTS[bossId]
  const passes: (string | null)[] = variants && variants.length > 0 ? [...variants] : [null]
  for (const variant of passes) {
    const map = cache.get(cacheKeyOf(bossId, variant))
    if (!map) return false
    for (const img of map.values()) if (!img.complete) return false
  }
  return true
}

export function hasBossSprite(bossId: string): boolean {
  return SPRITE_BOSSES.has(bossId)
}

// このボスの絵がキャッシュに載りうるキーの一覧 (先頭 = 平置き)。
//   🔴 バリアントを持つボス (介錯) は **平置きキーでは絶対にキャッシュされない**。
//   preload は cacheKeyOf(id, variant) = 'the-executioner#blade' 等でしか cache.set しない
//   (この下の loadBossSprites / 上の cacheKeyOf を参照)。 平置きだけを見る判定は必ず false になる。
export function spriteCacheKeyCandidates(bossId: string): readonly string[] {
  const variants = BOSS_SPRITE_VARIANTS[bossId]
  if (!variants || variants.length === 0) return [bossId]
  return [bossId, ...variants.map((v) => cacheKeyOf(bossId, v))]
}

// 当該ボスのスプライト(既定=idle)がロード済みか。 hollow-actor の前の版の「借り衣装を本物で着る」フォールバック判定用 (E-7-B)。
//   🔵2026-08-27: バリアント込みで見る。 それ以前は cache.get(bossId) だけを見ていたため、
//     **介錯だけが常に「未ロード」判定**になり、呪いオーラが本体シルエットを焼かず
//     楕円フォールバック (＝実機で「赤い丸」) に落ちていた (render.ts の curseUsesSilhouette)。
//     どれか1つのバリアントが使えれば true = 実際に焼く drawBossSprite 側が変形状態から
//     正しいバリアントを引き直すので、ここでは「絵の経路が生きているか」だけを問えばよい。
export function bossSpriteReady(bossId: string, key: SpriteKey = 'idle'): boolean {
  for (const ck of spriteCacheKeyCandidates(bossId)) {
    const img = cache.get(ck)?.get(key)
    if (img && img.complete && img.naturalWidth > 0) return true
  }
  return false
}

// boss.state を SpriteKey にマッピング
// recovery 中は直前の攻撃ポーズを残す → 'active' を引き続き表示
// hp 0 + defeat phase 用に 'defeat' を別途呼び出し
function stateToKey(state: BossState): SpriteKey {
  switch (state) {
    case 'idle':       return 'idle'
    case 'telegraph':  return 'telegraph'
    case 'active':     return 'active'
    case 'comboGap':   return 'active'   // 連撃の間も攻撃姿勢を維持
    case 'beatGap':    return 'active'   // P3: 連の拍と拍の間も攻撃姿勢
    case 'recovery':   return 'active'   // 振り抜き後の余韻
    case 'staggered':  return 'staggered'
    default:           return 'idle'
  }
}

// 宵渡りは reappear の時点で次の連が予約済み。攻撃telegraphを待たず、姿が結び直される
// 最初のフレームから「細い1撃 / 広い3撃」の身体シルエットを出す。
export function brideBlinkBranchPoseKey(boss: Boss): SpriteKey | null {
  if (boss.id !== 'bride' || (boss.state !== 'reappear' && boss.state !== 'telegraph')) return null
  const old = getBrideMotionArt() === 'old'
  if (boss.currentString?.id === 'bride-blink-single') return brideKaeshiOn() ? 'kaeshi_pray' : brideKaeshiBladeOn() ? 'issen_raise_1023' : old ? 'blink_single_charge' : 'side_crouch'
  if (boss.currentString?.id === 'bride-blink-triple') return old ? 'blink_triple_charge' : 'blink_triple_charge_1021'
  return null
}

// 花嫁の分岐予告3枚は、直後の接触絵を巻き戻したキーポーズとして同じ実寸を保つ。
// ★このフラグが止めるのは「汎用の均等+5%拡大」だけ (2026-07-19 に意味を限定)。
//   花嫁固有の縦寄り緊張ストレッチ(+6%)は telegraph 中のみ効かせる = 他の花嫁攻撃と同じ語彙。
//   以前 reappear開始→接触で約11%伸びて「別の絵へ飛んだ」ように見えたのは、
//   この2つ (均等+5% と 縦寄り+6%) の二重がけが原因だった。
export function brideBranchCueKeepsFixedScale(bossId: string, poseKey: string): boolean {
  return bossId === 'bride' && poseKey in BRIDE_BRANCH_CUE_SCALE_FIX
}

// ===== 分岐予告の予備動作 (2026-07-19 監修裁定) =====
//   問題: 固定スケール化でサイズジャンプは止まったが、代わりに reappear 470ms + telegraph 350ms =
//     約820ms のあいだ絵も変位も動かず、「固まってから急に殴られる」 (ユーザー実機指摘) になった。
//     ✅コード上の裏取り: idle bob は state==='idle' 限定、 telegraph の微振動は Math.random() の
//       ±0.6px のみ。 reappear はどちらの分岐にも入らない = 完全静止。
//   方針: 乱数の微振動は「意図」に読めないので予備動作にならない → 決定論的な「引き→解放」へ置換。
//     読ませるのは振幅ではなく速度の変化 (ゆっくり引いて、最後に鞭のように戻る)。
//   ★倍率は一切触らない。 均一な拡大縮小は身体的な意味を持たない唯一の動きで、11%成長で失敗済。
//   ★両端は必ず 0 に着地する。 telegraph 開始=直前の絵から飛ばない / 終端=接触絵へ 0 で受け渡す。
//     (解放が +方向へ突き抜けると、 active へ切り替わった瞬間に変位 0 へスナップして逆に跳ねる)
//   ✅当たり判定への影響なし: 返り値は描画オフセット専用で boss.x を触らない。 同ボスは既にパリィ
//     recoil で 16px 動いている (下の recoilAmp) ため、 最大7px は確立済みの許容内。
const BRIDE_BRANCH_CUE_ANTICIPATION: Record<string, {
  drop: number; sway: number; swayCycles: number; pull: number; sink: number; driveFrac: number
}> = {
  // C08 返しの一閃: reappear を経由しない (通常連携の中で telegraph だけ) → drop=0。
  veil_link_snap_windup: { drop: 0, sway: 0,   swayCycles: 0, pull: 7, sink: 2,   driveFrac: 0.26 },
  // C02 ワープ後1撃: 着地して据わる → 深く落ちて静かに収まる。 telegraph は C08 と同一。
  //   ★C08 と同じ「弾く」が答えなので、意図的に同じ動きにする (見た目を分けると読みのノイズになる)。
  blink_single_charge:   { drop: 6, sway: 1.5, swayCycles: 2, pull: 7, sink: 2,   driveFrac: 0.26 },
  // C03 ワープ後3撃: 浅く速く落ち、揺れが収まりきらない → 引きも浅く、解放は短く鋭い。
  //   振幅ではなく「性格」で1撃と差をつける (溜めて重い / 浅くて忙しない)。
  blink_triple_charge:   { drop: 4, sway: 2.2, swayCycles: 4, pull: 5, sink: 1.5, driveFrac: 0.20 },
  // 2026-10-21 手本合わせの絵。返しの一閃と瞬き一打は同じ横向きの縮み (side_crouch) を使い、動きも C02 と同じ。
  //   drop は reappear の時だけ効くので、reappear を通らない返しの一閃では C08 と同じ動きになる。
  side_crouch:              { drop: 6, sway: 1.5, swayCycles: 2, pull: 7, sink: 2,   driveFrac: 0.26 },
  blink_triple_charge_1021: { drop: 4, sway: 2.2, swayCycles: 4, pull: 5, sink: 1.5, driveFrac: 0.20 },
}

const clamp01 = (v: number) => (v < 0 ? 0 : v > 1 ? 1 : v)

/**
 * 花嫁の分岐予告3枚だけに効く決定論的な予備動作。 純関数 = Canvas 無しで検証できる。
 * @returns 描画オフセット px。 対象外・両端では必ず { x: 0, y: 0 }。
 */
export function resolveBrideBranchCueMotion(
  bossId: string, poseKey: string, state: BossState,
  stateElapsedMs: number, chargeMs: number, facing: 'left' | 'right', facingMul: 1 | -1,
): { x: number; y: number } {
  if (bossId !== 'bride') return { x: 0, y: 0 }
  const cfg = BRIDE_BRANCH_CUE_ANTICIPATION[poseKey]
  if (!cfg) return { x: 0, y: 0 }
  // 向きの定義は既存 recoil と揃える: 前=facing 方向 → 背中= その逆。
  const back = (facing === 'left' ? 1 : -1) * facingMul

  if (state === 'reappear') {
    if (cfg.drop <= 0) return { x: 0, y: 0 }   // C08 は reappear を通らない
    const t = clamp01(stateElapsedMs / BLINK_REAPPEAR_MS)
    // ★等速で沈める (2026-07-19 実測で修正)。 easeOutCubic は動きを前半へ集中させ、
    //   470ms のうち最初の150msで落下の68%が終わり、残り320msが実質静止していた
    //   (= 直そうとした「固まってから急に殴られる」 をそのまま再現していた)。
    const settle = t
    const damp = (1 - t) * (1 - t)             // 揺れは t=1 で必ず 0
    return {
      x: back * cfg.sway * Math.sin(t * Math.PI * cfg.swayCycles) * damp,
      y: -cfg.drop * (1 - settle),             // 負 = 上。 t=0 で最も浮き、 t=1 で接地
    }
  }
  if (state === 'telegraph') {
    const p = chargeMs > 0 ? clamp01(stateElapsedMs / chargeMs) : 1
    const pDrive = 1 - cfg.driveFrac
    if (p <= pDrive) {
      const u = pDrive > 0 ? clamp01(p / pDrive) : 1
      // ★等速で引く (2026-07-19 実測で修正)。 easeOutCubic だと 259ms の引きのうち
      //   90msで72%が終わり、 180ms以降は30msあたり0.3px以下 = 目には止まって見えた。
      //   等速なら30msごとに常に0.81px動き続け、 予告の最後まで「溜めている」 が見える。
      //   解放側 (下の easeInCubic) はそのまま = 等速の溜めとの対比で鞭が際立つ。
      const load = u
      return { x: back * cfg.pull * load, y: cfg.sink * load }
    }
    const v = clamp01((p - pDrive) / cfg.driveFrac)
    const release = 1 - v * v * v              // 溜めを保ってから終盤で一気に 0 へ = 鞭
    return { x: back * cfg.pull * release, y: cfg.sink * release }
  }
  return { x: 0, y: 0 }
}

// ボス描画状態 (前ステート保持でクロスフェードを実現) は types.ts の BossSpriteRuntime と一致
export function initBossSpriteRuntime(): BossSpriteRuntime {
  return {
    prevKey: 'idle',
    currKey: 'idle',
    transitionRemainingSec: 0,
    parryOverlayRemainingSec: 0,
    guardReactRemainingSec: 0,
    shallowHitReactRemainingSec: 0,
    deepHitReactRemainingSec: 0,
    playerHitReactionDir: 0,
    impactFlashRemainingSec: 0,
    impactFlashIntensity: 0.55,
    defeatElapsedSec: 0,
    clipKey: 'idle',
    prevClipKey: 'idle',
    clipElapsedMs: 0,
    parriedClipRemainingMs: 0,
    idleAnimSec: 0,
    prevX: 0,
    walkDist: 0,
    walkBurstPx: 0,
    sinceMoveMs: 999,
    turnRemainingMs: 0,
    prevFacing: 'left',
  }
}

const TRANSITION_DURATION_SEC = 0.15      // 150ms crossfade
// 絵の切り替えを溶かすか (2026-08-05 ユーザー裁定「消してほしい」)。
//   false = 全部ハードカット。輪郭の違う2枚が重なる「モヤ/残影」が消える。
//   戻す時はここを true にするだけ。DEV では globalThis.__crossfade = true で実行時に切替可。
//   消えるのは画像の重なりだけ。offset/scale/rot の手続きチャンネルは従来どおり滑らかに動く。
export const BOSS_SPRITE_CROSSFADE = false

export function resolveBossSpriteTransitionAlphas(
  crossfadeEnabled: boolean,
  transitionProgress: number,
): { curr: number; prev: number } {
  return crossfadeEnabled
    ? { curr: transitionProgress, prev: 1 - transitionProgress }
    : { curr: 1, prev: 0 }
}

function bossSpriteCrossfadeEnabled(bossId?: string): boolean {
  return BOSS_SPRITE_CROSSFADE
    || (bossId !== undefined && CROSSFADE_BOSSES.has(bossId))
    || (import.meta.env.DEV && (globalThis as { __crossfade?: boolean }).__crossfade === true)
}
// タイムラインの hold なしのキーを溶かして替えるボス (全ボスの既定はハードカット = BOSS_SPRITE_CROSSFADE)。
//   いまは DEV ?bladeMotion=v4 の剣舞だけ (構えに入る所・戻る所の絵の替わり目を短く溶かす・src/blade_dancer_art.ts)。
const CROSSFADE_BOSSES = new Set<string>()
export function setBossTimelineCrossfade(bossId: string, on: boolean): void {
  if (on) CROSSFADE_BOSSES.add(bossId)
  else CROSSFADE_BOSSES.delete(bossId)
}
export function isBossTimelineCrossfade(bossId: string): boolean { return CROSSFADE_BOSSES.has(bossId) }

export const PARRY_OVERLAY_DURATION_SEC = 0.32   // 320ms (パリィ後の怯み=parriedスプライト重なり+のけぞりrecoilの尺。手応え重視で延長 2026-06-11)。 render.ts の亡霊 recoil も同じ時計を使う
// 弾かれの見せ方 (2026-10-19 作者の答え「A 重ねない」「自然さを表すとA＞B＞＞＞＞今のまま」・検収パック kensyu-blade-dancer-moves-1020 C-2/C-3)。
//   既定 = 'none': 弾かれの絵を重ねず、振りの絵のまま押し戻す (重ね無しボスと同じのけぞり 20px)。
//   効くのは前に重ねていたボスだけ (PARRIED_OVERLAY_DISABLED の外 = 戴冠獣・剣舞・元王女・逆さ道化)。
//   比べ (DEV ?parriedPose=・src/main.ts):
//   'overlay' = 前の版 (弾かれの絵を 0.85→0 の薄さで PARRY_OVERLAY_DURATION_SEC 重ねる。 作者「一瞬薄くちらつくような見せ方は変」10-19)。
//   'cut'     = B (弾かれの絵へ重ねずにぱっと替え、PARRY_CUT_HOLD_SEC だけ止めてから元の絵へ戻す)。
export type ParriedPoseTrial = 'none' | 'cut' | 'overlay'
export const PARRY_CUT_HOLD_SEC = 0.14
let parriedPoseTrial: ParriedPoseTrial | null = null
export function setParriedPoseTrial(trial: ParriedPoseTrial | null): void { parriedPoseTrial = trial }
export function parseParriedPoseTrial(raw: string | null): ParriedPoseTrial | null {
  return raw === 'none' || raw === 'cut' || raw === 'overlay' ? raw : null
}
// 弾かれてもひるまないボス (2026-10-19 作者「しっかりしたボスは弾かれてもひるまない・連の間に弾かれた絵を挟むと変」)。
//   弾かれの絵を重ねず・替えず、NO_FLINCH_RECOIL_PX だけ押し戻す。いまは DEV ?bladeMotion=v3 の剣舞だけ (src/blade_dancer_art.ts)。
const NO_FLINCH_BOSSES = new Set<string>()
export const NO_FLINCH_RECOIL_PX = 6
export function setBossNoFlinch(bossId: string, on: boolean): void {
  if (on) NO_FLINCH_BOSSES.add(bossId)
  else NO_FLINCH_BOSSES.delete(bossId)
}
export interface ParriedPoseLook {
  overlayAlpha: number      // 弾かれの絵を重ねる薄さ (0 = 重ねない)
  cutToParried: boolean     // 今の絵の代わりに弾かれの絵を描く
  recoilAmp: number         // のけぞりの最大 px
}
export function resolveParriedPoseLook(
  bossId: string,
  remainingSec: number,
  trial: ParriedPoseTrial | null = parriedPoseTrial,
): ParriedPoseLook {
  const noFlinch = NO_FLINCH_BOSSES.has(bossId)
  const mode = noFlinch || PARRIED_OVERLAY_DISABLED.has(bossId) ? 'none' : trial ?? 'none'
  const active = remainingSec > 0
  const p = Math.min(1, Math.max(0, remainingSec / PARRY_OVERLAY_DURATION_SEC))
  return {
    overlayAlpha: active && mode === 'overlay' ? p * 0.85 : 0,
    cutToParried: active && mode === 'cut' && PARRY_OVERLAY_DURATION_SEC - remainingSec < PARRY_CUT_HOLD_SEC,
    // 騎士は壁流れ防止で弱め・花嫁は中量級で16・重ね無しは recoil で手応えを補う
    recoilAmp: noFlinch ? NO_FLINCH_RECOIL_PX : bossId === 'vigil-knight' ? 11 : bossId === 'bride' ? 16 : mode === 'none' ? 20 : 14,
  }
}
const GUARD_REACT_DURATION_SEC = 0.14     // 140ms (受けの brace=槍を寄せて受け止める短い反応。 parry recoil より小さく短い=動じない)
export const SHALLOW_HIT_REACT_DURATION_SEC = 0.09 // 90ms: 表面が短く詰まる。全身怯みにはしない
export const DEEP_HIT_REACT_DURATION_SEC = 0.18    // 180ms: 開いた隙へ刺さり、全身へ抵抗が通って戻る
const IMPACT_FLASH_DURATION_SEC = 0.12    // 120ms 攻撃命中時の白フラッシュ
const PARRIED_INTERRUPT_MS = 400          // 弾かれクリップを見せる割り込み窓 (短く固定。 clip尺に縛らない)
const IDLE_MOVE_DEBOUNCE_MS = 120         // この時間 動いていなければ「静止」とみなす (歩き→呼吸の切替デバウンス)
const WALK_STRIDE_PX = 130                // 未指定ボスの歩き1サイクル(passing↔reach↔passing)
const WALK_STRIDE_PX_BY_BOSS: Record<string, number> = {
  'the-executioner': 170,                 // 重量級の一歩=85px。速度は変えず、移動距離だけで絵を同期
}
// 歩きの絵へ切り替える下限 = 歩き1サイクル分 (stride ×1.0) 進んでから (2026-09-09 実測で決めた値)。
//   症状: 攻撃後に間合いを詰め直す 0.1〜0.3秒だけ 立ち絵→歩き→立ち絵 が出入りし、明滅して見えた
//     (覚醒王・25秒で6回。WALK_DISCRETE_SNAP のボスはハードカットなのでそのまま瞬きに見える)。
//   実測: 0.5 では 191ms の短い出入りが残り、1.0 で 0 回になった。長い接近 (1500px) では 1.0 でも
//     歩きが5コマ切り替わって出るので、2026-08-11 に直した「待機の絵のまま滑って寄る」へは戻らない。
const WALK_MIN_BURST_RATIO = 1.0

// 歩きの絵 (2枚ペア) へ切り替えてよいか。 1サイクル未満の短い burst では立ち絵を保つ。
export function walkPoseGateOpen(walkBurstPx: number, stridePx: number): boolean {
  return walkBurstPx >= stridePx * WALK_MIN_BURST_RATIO
}
const WALK_4_CYCLE: SpriteKey[] = ['walk_passing', 'walk_cross', 'walk_contact_b', 'walk_cross']
// 歩きの体の上下動 (2026-08-23)。絵は4コマだが、体の上下は**絵と切り離して連続**で動かす。
//   足が揃う瞬間 (t=0.25/0.75) が一番高く、接地 (t=0/0.5) が一番低い = 1歩に1回。
//   🔴 平行移動ではなく倍率で作る = 接地点を固定したまま伸び上がるので、床に着いた足が浮かない。
//   登録の無いボスは 0 = 何も変わらない (4コマ経路のボスだけに効く)。
const WALK_BODY_BOB_BY_BOSS: Record<string, number> = {
  // 🔵 2026-08-23 夕: 介錯は **0** = 上下動を絵の中へ移した。接地の絵が前傾して靴底→頭が 95% になり、
  //   足が揃う絵で 100% へ戻る (完成ボスの騎士と同じ作り = コード側の上下動はゼロで絵が 3.2% 持っている)。
  //   ここに値を入れると絵の沈みと二重にかかる。仕組みは残す = 絵に上下が無いボスのための逃げ道。
  'the-executioner': 0,
}
// 🔴 2026-08-22 (ユーザー裁定): 向きが変わった瞬間の turn 1拍を出さないボス。
//   介錯 = turn の絵が**他の48枚と逆を向いて描かれている**（ユーザーが元絵54枚を1枚ずつ判定）。
//   反転して向きを揃えると刃が反対の腕へ移り、腕を戻そうとすると向きが戻る（生成側が
//   「体はそのまま刃だけ付け替える」をできない＝2026-08-22 に3通りの言い方で実測）。
//   → 出さない。**拍・判定・当たり方は一切変わらない（描画だけ）。**
//   ⚠️ 変形 (morph) の中で使う turn は別経路 (resolveBossStateOverrideKey) なので生きている。
//     そちらは**ボスが実際に回っている最中**なので、逆向きの絵で辻褄が合う。
//   戻し方 = 向きも腕も正しい turn を描けたら、この Set から id を外す。
const TURN_FLIP_BEAT_DISABLED = new Set<string>(['the-executioner'])
const TURN_INTERRUPT_MS = 180             // 未指定ボスの向き替え1拍
const TURN_INTERRUPT_MS_BY_BOSS: Record<string, number> = {
  // 2026-07-20 是正: 120ms は標準180msより「速い」= 一番重いボスが一番軽い、で論理が逆だった。
  //   実機では turn 絵が約1コマの点滅になり、「槌を杭にして体を回す」良いポーズが認識前に消える。
  //   200ms へ (標準180より重い側)。判定・不感帯・facing 確定タイミングは不変 = 描画側の値のみ。
  'the-executioner': 200,
}

export function updateBossSpriteRuntime(
  ss: BossSpriteRuntime,
  boss: Boss,
  parryJustHappened: boolean,
  contact: PlayerAttackContact | null,
  dt: number,
): BossSpriteRuntime {
  const targetKey = stateToKey(boss.state)
  let next = { ...ss }
  if (boss.id === 'vigil-knight') {
    const tlImage = boss.state === 'recovery' || boss.state === 'telegraph'
      ? resolveBossTimelinePose(boss.id, boss)?.frame?.current.image ?? null : null
    const recoveryImage = boss.state === 'recovery' ? tlImage : null
    // DEV ?knightScrapeTurn=stand (旧の比較): turn の拍の後の scrape_stop を直立で描くので、足を寄せる拍の元の絵も直立として覚える。
    Object.assign(next, advanceKnightScrapeTurn(ss, boss, recoveryImage))
    Object.assign(next, advanceKnightRestMid(ss, boss, knightScrapeTurnHistoryImage(boss, ss, recoveryImage), dt,
      boss.state === 'telegraph' ? tlImage : null))
    Object.assign(next, advanceKnightWalkBridge(ss, boss, dt, boss.state === 'telegraph' ? tlImage : null))
  }

  if (targetKey !== ss.currKey) {
    next = {
      ...next,
      prevKey: ss.currKey,
      currKey: targetKey,
      transitionRemainingSec: TRANSITION_DURATION_SEC,
    }
  }

  if (next.transitionRemainingSec > 0) {
    next.transitionRemainingSec = Math.max(0, next.transitionRemainingSec - dt)
  }

  if (parryJustHappened) {
    next.parryOverlayRemainingSec = PARRY_OVERLAY_DURATION_SEC
  } else if (next.parryOverlayRemainingSec > 0) {
    next.parryOverlayRemainingSec = Math.max(0, next.parryOverlayRemainingSec - dt)
  }

  // 受け (ガード) の brace 反応 (③⑤): 弾かれた tick に立てて、 以降は減衰。
  if (contact?.kind === 'guarded') {
    next.guardReactRemainingSec = GUARD_REACT_DURATION_SEC
  } else if (next.guardReactRemainingSec > 0) {
    next.guardReactRemainingSec = Math.max(0, next.guardReactRemainingSec - dt)
  }

  if (contact?.kind === 'shallow') {
    next.shallowHitReactRemainingSec = SHALLOW_HIT_REACT_DURATION_SEC
    next.playerHitReactionDir = contact.dir
  } else if (next.shallowHitReactRemainingSec > 0) {
    next.shallowHitReactRemainingSec = Math.max(0, next.shallowHitReactRemainingSec - dt)
  }

  if (contact?.kind === 'deep') {
    next.deepHitReactRemainingSec = DEEP_HIT_REACT_DURATION_SEC
    next.playerHitReactionDir = contact.dir
  } else if (next.deepHitReactRemainingSec > 0) {
    next.deepHitReactRemainingSec = Math.max(0, next.deepHitReactRemainingSec - dt)
  }

  if (contact !== null && contact.kind !== 'guarded') {
    next.impactFlashRemainingSec = IMPACT_FLASH_DURATION_SEC
    next.impactFlashIntensity = contact.kind === 'shallow' ? 0.30
      : contact.kind === 'deep' ? 0.48
      : 0.55
  } else if (next.impactFlashRemainingSec > 0) {
    next.impactFlashRemainingSec = Math.max(0, next.impactFlashRemainingSec - dt)
  }

  if (next.shallowHitReactRemainingSec <= 0 && next.deepHitReactRemainingSec <= 0) {
    next.playerHitReactionDir = 0
  }

  // defeat フェード用タイマー (hp 0 中は加算、hp 残ってる間は 0 リセット)
  if (boss.hp <= 0) {
    next.defeatElapsedSec = ss.defeatElapsedSec + dt
  } else {
    next.defeatElapsedSec = 0
  }

  // --- 動画クリップ進行 (Phase 1) ---
  // parried 割り込みは「短い固定窓」。 ⚠️ clip 総尺由来にしてはいけない:
  //   現状クリップは全長 ~10s (大半が棒立ち) ゆえ、 尺由来だと 1 パリィで 10s 間 idle/攻撃が
  //   全て parried に上書きされ静止する。 窓内で parried クリップをスクラブして見せる (drawBossClipFrame)。
  const parriedRemain = parryJustHappened
    ? PARRIED_INTERRUPT_MS
    : Math.max(0, ss.parriedClipRemainingMs - dt * 1000)
  const baseKey = resolveBossClipKey({
    state: boss.state, currentAttackId: boss.currentAttack?.id ?? null, hp: boss.hp,
  })
  const activeKey = parriedRemain > 0 ? 'parried' : baseKey
  const keyChanged = activeKey !== ss.clipKey
  next.prevClipKey = ss.clipKey
  next.clipKey = activeKey
  next.clipElapsedMs = keyChanged ? 0 : ss.clipElapsedMs + dt * 1000
  next.parriedClipRemainingMs = parriedRemain

  // --- idle サブアニメ用の移動/クロック更新 (STAGE B) ---
  //   idle 中に boss.x がスライドしている=「歩いて詰めている」(boss.ts dist>preferredRange)。
  //   移動量を積算して歩幅サイクルへ同期 (時間でなく距離駆動=足滑り防止)。
  const dx = boss.x - ss.prevX
  const movedPx = Math.abs(dx)
  // 2026-10-05 (ユーザー「後ろ飛び後に他の絵が混ざっている」): 覚醒王の後ろ跳びの移動は歩きに数えない。
  //   数えると跳んだ距離 (110px＋滑り) が歩きの下限の門を開け、着地の絵が終わった直後の約0.12秒 walk_reach が出た。
  const hopMove = boss.state === 'idle' && boss.spacing?.action === 'hop'
  const isMoving = boss.state === 'idle' && movedPx > 0.02 && !hopMove
  next.prevX = boss.x
  next.idleAnimSec = boss.state === 'idle' ? ss.idleAnimSec + dt : 0
  next.walkDist = isMoving ? ss.walkDist + movedPx : ss.walkDist
  // 進む向き (DEV ?walkSurge=1 の見た目のずらしが読む・src/boss_walk_surge.ts)。止まっている間は前の向きのまま。
  if (isMoving) next.walkDir = dx < 0 ? -1 : 1
  next.sinceMoveMs = isMoving ? 0 : hopMove ? Math.max(IDLE_MOVE_DEBOUNCE_MS, ss.sinceMoveMs + dt * 1000) : ss.sinceMoveMs + dt * 1000
  // walkBurstPx: 「今回の移動」だけの積算 (walkDist と違い静止で 0 へ戻る)。
  //   動いている間は加算、静止 (デバウンス経過) したら 0 へ、デバウンス待ち中は据え置き。
  next.walkBurstPx = isMoving
    ? ss.walkBurstPx + movedPx
    : (next.sinceMoveMs >= IDLE_MOVE_DEBOUNCE_MS ? 0 : ss.walkBurstPx)

  // 向き替え (B-4): facing が反転した tick に turn 窓を立て、 以降は減衰。
  if (boss.facing !== ss.prevFacing) {
    next.turnRemainingMs = TURN_INTERRUPT_MS_BY_BOSS[boss.id] ?? TURN_INTERRUPT_MS
  } else if (next.turnRemainingMs > 0) {
    next.turnRemainingMs = Math.max(0, next.turnRemainingMs - dt * 1000)
  }
  next.prevFacing = boss.facing

  return next
}

// 表示専用の接触抵抗。座標・AI・戦闘stateは変えず、描画矩形だけを押して戻す。
// contact.dir は実際の攻撃者→ボス方向。ボスの向きが固定される背面窓でも因果を反転させない。
// facingMulを掛けるのは、スプライトの左右反転後も画面上のworld方向を保つため。
export function playerHitReactionOffset(
  ss: BossSpriteRuntime, facingMul: 1 | -1,
): { x: number; y: number } {
  const awayScreen = ss.playerHitReactionDir
  if (ss.deepHitReactRemainingSec > 0) {
    const p = ss.deepHitReactRemainingSec / DEEP_HIT_REACT_DURATION_SEC
    const settle = p * p
    return { x: awayScreen * facingMul * 6 * settle, y: 2.5 * settle }
  }
  if (ss.shallowHitReactRemainingSec > 0) {
    const p = ss.shallowHitReactRemainingSec / SHALLOW_HIT_REACT_DURATION_SEC
    const settle = p * p
    return { x: awayScreen * facingMul * 2 * settle, y: 1 * settle }
  }
  return { x: 0, y: 0 }
}

// HP 0 のときは defeat スプライトに上書き。 BOSS_DEATH_COLLAPSE 登録ボスは専用の崩れ絵 (death_collapse) を出す。
function effectiveKey(boss: Boss, key: SpriteKey): SpriteKey {
  if (boss.hp <= 0) {
    const dc = BOSS_DEATH_COLLAPSE[boss.id]
    if (dc) return dc.key
    return 'defeat'
  }
  if (key === 'idle') return bossIdleKey(boss.id)
  return key
}

// 動画クリップの 1 フレームを描く (root-locked: 移動は boss.x が担う)。
//   - セルはフルキャンバス。 charH を displayHeight に合わせる固定スケールで「呼吸サイズ変動」を防ぐ。
//   - 足元 (footX,footY) を (centerX,centerY) に合わせて接地を安定させる。
//   - 擬似モーション (bob/scale/tremble/recoil) は一切足さない (実フレームが本物の動きを持つ)。
//   - impactFlash (命中白) のみ残す。 顔の虚無発光は drawBossFaceGlow が別途描く。
function drawBossClipFrame(
  ctx: CanvasRenderingContext2D,
  clip: LoadedBossClip,
  boss: Boss,
  ss: BossSpriteRuntime,
  centerX: number,
  centerY: number,
  displayHeight: number,
  facingMul: 1 | -1,
): void {
  const m = clip.meta
  const [cw, ch] = m.canvas
  let cur: ClipCursor
  if (ss.clipKey.startsWith('attack:')) {
    const atk = boss.currentAttack
    const elapMs = boss.stateElapsed * 1000
    if (boss.state === 'telegraph') {
      cur = { mode: 'segment', seg: 'telegraph', progress: elapMs / (atk?.chargeMs ?? 1) }
    } else if (boss.state === 'active') {
      cur = { mode: 'segment', seg: 'active', progress: elapMs / (atk?.activeMs ?? 1) }
    } else if (boss.state === 'comboGap') {
      cur = { mode: 'segment', seg: 'comboGap', progress: 0 }
    } else if (boss.state === 'beatGap') {
      cur = { mode: 'segment', seg: 'comboGap', progress: 0 }   // comboGap と同じ繋ぎ姿勢
    } else {
      cur = { mode: 'segment', seg: 'recovery', progress: elapMs / (atk?.recoveryMs ?? 1) }
    }
  } else if (ss.clipKey === 'parried') {
    // 割り込み窓の進捗でクリップ全体をスクラブ (全長10sを実時間でなく窓内に圧縮して見せる)
    const prog = 1 - Math.max(0, Math.min(1, ss.parriedClipRemainingMs / PARRIED_INTERRUPT_MS))
    cur = { mode: 'oneshot', elapsedMs: prog * m.frames.length * (1000 / m.outFps) }
  } else if (m.loop !== 'none') {
    cur = { mode: 'loop', elapsedMs: ss.clipElapsedMs }
  } else {
    cur = { mode: 'oneshot', elapsedMs: ss.clipElapsedMs }
  }
  const fr = m.frames[clipFrameIndex(m, cur)]
  if (!fr) return

  const scale = displayHeight / clip.charH
  const dw = cw * scale
  const dh = ch * scale
  const hitReact = playerHitReactionOffset(ss, facingMul)
  const dx = centerX - fr.footX * scale + hitReact.x
  const dy = centerY - fr.footY * scale + hitReact.y

  // defeat fade: 撃破後 1.0s で完全に消える (0.35 残しは「薄く残ってる」誤読を生むため廃止 / 2026-06-24)。
  //   騎士の「頽れて残る」設計は drawKnightDefeat で procedural に表現するもので、 スプライト経路は
  //   崩れポーズを持たないため停止フレームが半透明で立ち続けて違和感を出していた。 fbeat=silence で
  //   本体は既に dissolve 済 (drawBossFromSprite) なので、 ここは単純に追加保険のフェードのみ。
  let defeatAlpha = 1
  if (boss.hp <= 0) defeatAlpha = Math.max(0, 1 - ss.defeatElapsedSec / 1.0)

  ctx.save()
  ctx.translate(centerX, centerY)
  ctx.scale(facingMul, 1)
  ctx.translate(-centerX, -centerY)
  ctx.globalAlpha = defeatAlpha
  ctx.drawImage(clip.image, fr.x, fr.y, cw, ch, dx, dy, dw, dh)
  if (ss.impactFlashRemainingSec > 0) {
    const a = ss.impactFlashRemainingSec / IMPACT_FLASH_DURATION_SEC
    ctx.globalCompositeOperation = 'lighter'
    ctx.globalAlpha = a * ss.impactFlashIntensity * defeatAlpha
    ctx.drawImage(clip.image, fr.x, fr.y, cw, ch, dx, dy, dw, dh)
  }
  ctx.restore()
}

export type ResolvedBossTimelinePose = {
  attackId: string
  timeline: BossAttackTimeline
  phaseProgress: ReturnType<typeof phaseProgressOf>
  frame: TimelineFrame | null
  motion: BossPoseMotion
}

// いま出ている技の絵の並び。覚醒王の致命「灰を食む」の比較版 (DEV ?awokenDevour) は、その回の段取りから作った並び
//   (active と recovery は今の暴噛・終のまま・src/awoken_devour.ts)。それ以外は登録された並びそのもの。
export function bossAttackTimelineOf(bossId: string, boss: Boss): BossAttackTimeline | undefined {
  const attackId = boss.currentAttack?.id
  if (!attackId) return undefined
  // 介錯の突き当ての比較版 (DEV ?jabFix=charge|early・src/exec_jab_fix.ts): 振り下ろし×2→突き当ての3拍目と、その前のワンテンポだけ。配布版は常に undefined。
  // 介錯の突き当ての踏み込みの版 (配布版・src/exec_jab_tremble_1028.ts): 連の2拍目以降は溜めから入る並び。上の比較版の上書きより先に聞く。
  const jabLink = bossId === 'the-executioner' ? execJabLinkTimeline(boss) : undefined
  if (jabLink) return jabLink
  // 騎士の突き・薙ぎの足を留める版 (配布版 1028・src/knight_smooth_1028.ts): 連の2拍目以降は構えから入る並び。
  const knightLink = bossId === 'vigil-knight' ? knightSmoothLinkTimeline(boss) : undefined
  if (knightLink) return knightLink
  const jabFix = bossId === 'the-executioner' ? jabFixTimeline(boss) : undefined
  if (jabFix) return jabFix
  const authored = BOSS_ATTACK_TIMELINES[bossId]?.[attackId]
  // 騎士と花嫁の固有の致命 (DEV ?execUnique=1・src/exec_unique.ts): その回の段取りから作った並び。
  const unique = boss.currentAttack?.uniqueExec
  if (unique && authored) return execUniqueTimeline(unique, authored)
  const plan = boss.currentAttack?.devour
  // 剣舞の幕引きの一閃の作り直し (src/blade_finisher_v2.ts・既定・前の版は DEV ?bladeFinisher=v1): その回の手拍子から作った並び。
  const clap = boss.currentAttack?.bladeClap
  const base = clap ? bladeClapTimeline(clap) : plan && authored ? awokenDevourTimeline(plan, authored) : authored
  // 致命技を弾かれた (src/exec_parry_recoil.ts): 弾いたコマから弾かれの姿 (騎士・花嫁は上の固有の並びの中で替える)。旧は DEV ?execParryRecoil=old。
  if (boss.currentAttack?.execParried && execParryRecoilNow()) {
    // 執行が砲を持っている間は据え斬りの絵・弾かれの絵 (刃だけ) を使えないので、前の並びの当たりの絵で反らす。
    const cannon = bossId === 'the-executioner' && boss.exec?.held === 'cannon'
    const cannonSue = cannon && base === EXEC_FINISHER_TIMELINE_SUE
    return execParriedTimeline(bossId, cannonSue ? EXEC_FINISHER_TIMELINE_OLD : base, cannon)
  }
  return base
}

// 本体と付随描画が同じフレーム・手続き変位を読むための共通入口。
export function resolveBossTimelinePose(bossId: string, boss: Boss): ResolvedBossTimelinePose | null {
  const attackId = boss.currentAttack?.id
  if (!attackId) return null
  const authored = bossAttackTimelineOf(bossId, boss)
  // 突き当て A-1 の絵 (jab_coil / jab_lunge) は刃の絵しか無い。突き当ては居座りへの応手として砲形態でも出るので、
  //   砲を持っている間だけ旧の絵 (kick_windup → shove_contact・砲の絵あり) へ落とす (2026-09-22)。
  //   見分けは「jab_coil を使う並びか」。段の比較版 (?execJabSteps=1028) も同じく落とす (10-28)。
  //   致命「執行」の据え斬り (sue_*) も刃の絵しか無いので、砲の間は前の並びへ落とす (2026-10-20)。
  const cannonHeld = bossId === 'the-executioner' && boss.exec?.held === 'cannon'
  const weaponTimeline = cannonHeld && attackId === 'exec_maulJab' && authored?.some(k => k.image === 'jab_coil')
    ? EXEC_JAB_TIMELINE_VARIANTS.old
    : cannonHeld && attackId === 'executioner_finisher' && authored === EXEC_FINISHER_TIMELINE_SUE
      ? EXEC_FINISHER_TIMELINE_OLD
      : authored
  // 執行の寄りの一歩 (src/exec_finisher_step.ts): 溜めの頭に沈み → 踏み込み → 着地の絵を足し、掲げる絵を残りへ詰める。
  const finisherStep = boss.currentAttack?.finisherStep
  const timeline = finisherStep && weaponTimeline
    ? finisherStepTimeline(weaponTimeline, finisherStep, boss.currentAttack!.chargeMs)
    : weaponTimeline
  const inAttack = boss.state === 'telegraph' || boss.state === 'active'
    || boss.state === 'comboGap' || boss.state === 'beatGap' || boss.state === 'recovery'
  if (!timeline || !inAttack || boss.hp <= 0) return null

  const phaseProgress = phaseProgressOf(boss)
  const { phase, progress, hitIndex } = phaseProgress
  const motion = resolveTimelineMotion(timeline, phase, progress, hitIndex)
  const cannonMs = boss.state === 'telegraph' ? boss.stateElapsed * 1000
    : boss.state === 'active' ? boss.currentAttack!.chargeMs + boss.stateElapsed * 1000 : 935
  return {
    attackId,
    timeline,
    phaseProgress,
    frame: (() => {
      const frame = selectTimelineFrame(timeline, phase, progress, hitIndex)
      if (bossId === 'vigil-knight' && boss.state === 'recovery') {
        const image = knightResetRecoveryImage(attackId, boss.stateElapsed * 1000)
        if (image && frame) return { current: { ...frame.current, image, hold: true }, next: undefined, blend: 0 }
      }
      if (!frame || bossId !== 'vigil-knight' || attackId !== 'knightWallCharge' || boss.state !== 'active') return frame
      const traveled = Math.abs(boss.x - boss.actionAnchorX)
      return { current: { ...frame.current, image: Math.floor(traveled / KNIGHT_CHARGE_STRIDE) % 2 === 0
        ? 'charge_run1' as const : 'charge_run2' as const }, next: undefined, blend: 0 }
    })(),
    motion: bossId === 'the-executioner' && attackId === 'exec_cannonFajin'
      ? cannonFajinBodyMotion(motion, cannonMs) : motion,
  }
}

/** 壁蹴りの絵は「足裏を壁に固定」して描く (拡大・震えで滑らせない)。旧＝壁反射の溜め中だけ。 */
const wallKickFootPinned = (boss: Boss): boolean =>
  !!boss.currentAttack?.reboundFromWall && boss.state === 'telegraph'

export function drawBossSprite(
  ctx: CanvasRenderingContext2D,
  bossId: string,
  boss: Boss,
  ss: BossSpriteRuntime,
  centerX: number,
  centerY: number,
  displayHeight: number,
  time: number,
  playerGrabbed: boolean = false,
  playing: boolean = false,
): BossSpritePlacement | null {
  {
    const hunt = wallHuntGameSample(boss)
    if (hunt) {
      const { frame, art, placement: at } = hunt
      const image = wallHuntImage(art.file)
      if (!image) return null
      const key = art.file.replace('.webp', '')
      // Same placement as the contact test, including rotation and the wall pulse.
      ctx.save()
      ctx.translate(at.x, at.y)
      ctx.scale(frame.facing, 1)
      ctx.rotate(at.rotation)
      ctx.scale(at.scaleX, at.scaleY)
      ctx.drawImage(image, -at.pivotX, -at.pivotY)
      ctx.restore()
      bossSpriteDrawProbe = { bossId, key, previousKey: null, substitutedFrom: null,
        variant: null, variantFileExists: true, fellBackToFlat: false, facing: boss.facing }
      const [x0, y0, x1, y1] = art.bounds
      const face = wallHuntFacePoint(art.file)
      const faceAnchorInArt: BossFaceAnchor | undefined = face
        ? [(face[0] - x0) / (x1 - x0), (face[1] - y0) / (y1 - y0)]
        : resolveBossFaceAnchorInArt(bossId, key === 'dash_crouch_fangs_0912' ? 'dash_crouch' : key)
      const imageRect = { x: at.x - at.pivotX * at.scaleX, y: at.y - at.pivotY * at.scaleY,
        width: art.width * at.scaleX, height: art.height * at.scaleY }
      return { imageKey: key, imageRect, faceAnchorInArt,
        opaqueRect: { x: imageRect.x + x0 * at.scaleX, y: imageRect.y + y0 * at.scaleY,
          width: (x1 - x0) * at.scaleX, height: (y1 - y0) * at.scaleY },
        pivot: { x: at.x, y: at.y }, facingMul: frame.facing, rotationRad: at.rotation }
    }
  }
  // 見た目バリアント (刑吏の腕＝武器)。 未登録ボスと未配置バリアントは従来の平置きへ落ちる。
  const variant = bossSpriteVariant(boss)
  const variantCacheKey = cacheKeyOf(bossId, variant)
  const variantMap = cache.get(variantCacheKey)
  const map = variantMap ?? cache.get(bossId)
  // 通常は key 単位の onerror フォールバック。キャッシュ自体がまだ無い場合も平置き map を使う。
  const mapFellBackToFlat = variant !== null && !variantMap
  if (!map) return null

  // ソース向きと boss.facing が一致するなら flip 無し、不一致なら水平反転
  const sourceFacing = SOURCE_FACING[bossId] ?? 'right'
  // 覚醒王の後ろ歩きの比較版 (DEV ?awokenBackWalk=turn・src/awoken_back_walk.ts): 下がる間だけ絵を反転する。配布版はいつも boss.facing。
  const facingMul: 1 | -1 = awokenBackWalkDrawFacing(boss) === sourceFacing ? 1 : -1

  // ===== 動画クリップ経路 (Phase 1) =====
  // 当該クリップがロード済なら実フレームを描いて return (擬似モーションは通さない = root-locked)。
  // 未配置/未ロードは下の従来 1枚絵 + 擬似モーション経路にフォールバック。
  const clip = clipCache.get(bossId)?.get(ss.clipKey)
  if (clip && clip.image.complete && clip.meta.frames.length > 0) {
    drawBossClipFrame(ctx, clip, boss, ss, centerX, centerY, displayHeight, facingMul)
    return null
  }

  // ===== キーポーズ・タイムライン経路 (2026-06-09) =====
  // 攻撃中 (telegraph/active/comboGap/recovery) かつ当該 attackId のタイムラインがあれば、
  // 現在キー画像を currKey に解決して既存の1枚絵+手続き+FXブロックを流用する (ハードカット)。
  // ※ 動画クリップがロード済の間はクリップ経路が先に return するため、本経路はクリップ退役後に有効化される。
  const timelinePose = resolveBossTimelinePose(bossId, boss)
  // ⚠️ tl は「この攻撃にタイムラインが存在するか」だけを表す (下の !tl 分岐が読む)。
  //   timelinePose?.timeline で代用すると、hp<=0 や非攻撃 state で undefined に落ちて
  //   攻撃専用 squash (line ~1439) が復活してしまう。存在判定は従来どおり素で引く。
  const tl = bossAttackTimelineOf(bossId, boss)
  const crossfadeEnabled = bossSpriteCrossfadeEnabled(bossId)
  let tlCurrKey: SpriteKey | null = null
  let tlPrevKey: SpriteKey | null = null   // クロスフェード時の outgoing キー (非接触キー間のみ)
  let tlBlend = 0
  let rushFangRelease = false
  // 手続きチャンネル (2026-07-29): タイムラインが持つ offset/scale/rot を ease 補間で解決。
  //   motion を1つも書いていないタイムライン (既存の全ボス) は NO_POSE_MOTION = 無変位。
  let tlMotion: BossPoseMotion = NO_POSE_MOTION
  if (timelinePose && tl) {
    tlMotion = timelinePose.motion
    const f = timelinePose.frame
    if (f) {
      // ★Codex 2026-06-09: 一律ハードカットは過剰。 selectTimelineFrame の next/blend を実際に使う
      //   (= resolveTimelineDraw)。 非接触キー間だけディゾルブ・接触周辺は死守。判定は純関数でテスト済。
      const layers = resolveTimelineDraw(f, (img) => map.get(img as SpriteKey)?.complete === true)
      if (layers.mode === 'crossfade') {
        // Only the adopted rush releases these matching-pose fangs gradually.
        // Keep the global hard-cut policy and every other attack unchanged.
        rushFangRelease = isAwokenRushRelease(boss)
          && layers.from === 'rush_windup_fangs_0917' && layers.to === 'rush_windup'
        if (crossfadeEnabled || rushFangRelease) {
          // 牙ありの旧キー(rush_windup_fangs_0917)は 2026-10-07 の描き直し版へ読み替える。
          tlPrevKey = (rushFangRelease ? awokenFangPose(boss, layers.from) : layers.from) as SpriteKey
          tlCurrKey = layers.to as SpriteKey
          tlBlend = layers.blend
        } else {
          // 画像は現キーを保持し、次のキー到達時にハードカットする。手続き motion は別経路。
          tlCurrKey = layers.from as SpriteKey
          tlPrevKey = null
          tlBlend = 0
        }
      } else {
        tlCurrKey = layers.current as SpriteKey
      }
      // 連中はニュートラルに戻さない (K4 / design_combo_no_neutral_pose): 連の2発目以降の telegraph 冒頭で
      //   idle 絵が出るのを抑止し、その技の構え(anticipation)絵へ差し替える。既存絵の使い回し=新規絵不要。
      const noNeutral = comboNoNeutralImage(tl, tlCurrKey ?? '', boss)
      if (noNeutral && map.get(noNeutral as SpriteKey)?.complete === true) {
        tlCurrKey = noNeutral as SpriteKey
        tlPrevKey = null
        tlBlend = 0
      }
      // 騎士の中間拍は技ごとの「張ったまま」絵を優先する。
      // 最終拍は beatGap に入らず recovery の緩みへ進むため、攻め時を消さない。
      const continuation = knightComboContinuationImage(boss)
      if (continuation && map.get(continuation as SpriteKey)?.complete === true) {
        tlCurrKey = continuation as SpriteKey
        tlPrevKey = null
        tlBlend = 0
        const gapOffX = knightSmoothGapOffsetX(continuation)
        if (gapOffX !== undefined) tlMotion = { ...NO_POSE_MOTION, offsetX: gapOffX }
      }
      // 拍の間に決まった絵があれば、その間ずっと出す (剣舞のアルレ版の見得・StringBeat.gapImage)。
      const gapImage = boss.state === 'beatGap' ? boss.currentString?.beats[boss.beatIndex + 1]?.gapImage : undefined
      if (gapImage && map.get(gapImage as SpriteKey)?.complete === true) {
        tlCurrKey = gapImage as SpriteKey
        tlPrevKey = null
        tlBlend = 0
      }
      // An actual recovery releases the fangs; comboGap/beatGap keep them.
      // Apply after no-neutral selection so a linked attack cannot briefly lose its cue.
      if (tlCurrKey) {
        const fangKey = isAwokenRushRelease(boss) && tlCurrKey === ('rush_windup' as SpriteKey)
          ? tlCurrKey : awokenFangPose(boss, tlCurrKey) as SpriteKey
        if (fangKey !== tlCurrKey && map.get(fangKey)?.complete === true) {
          tlCurrKey = fangKey
          tlPrevKey = null
          tlBlend = 0
        }
      }
      // 配布版の既定 (more) も: 薙ぎの recovery が長く続き、構え直しの絵が idle へ替わる所に足を寄せる絵を 120ms (描画のみ・state は recovery のまま)。
      {
        const rearmKey = knightRestMidRearmKey(boss, tlCurrKey)
        if (rearmKey && map.get(rearmKey)?.complete === true) {
          tlCurrKey = rearmKey
          tlPrevKey = null
          tlBlend = 0
        }
      }
      // 配布版の既定 (more): recovery から idle を通らず直接 telegraph へ移る回 (壁ぎわの突進 → 薙ぎ等) は、telegraph の最初の絵が idle (直立) で
      //   低い構え → 直立が1コマで跳ぶ。その idle の間だけ、最後の recovery の絵に応じた中間の絵を最初の 120ms 描く (構えの絵に替わったら出さない)。
      {
        const teleKey = knightRestMidTeleKey(boss, ss, tlCurrKey, playing)
        if (teleKey && map.get(teleKey)?.complete === true) {
          tlCurrKey = teleKey
          tlPrevKey = null
          tlBlend = 0
        }
      }
      // 既定 (10-15・旧 = DEV ?knightWalkBridge=0): 歩きの広い構えから直接溜めへ移った回、溜めの頭の立ち姿の間だけ最初の 100ms を揃えかけの絵へ (描画のみ)。
      {
        const bridgeKey = knightWalkBridgeKey(boss, ss, tlCurrKey, playing)
        if (bridgeKey && map.get(bridgeKey as SpriteKey)?.complete === true) {
          tlCurrKey = bridgeKey as SpriteKey
          tlPrevKey = null
          tlBlend = 0
        }
      }
      // 配布版の既定 (stand・2026-10-29 作者): recovery の途中の向き替え (turn) の前後の scrape_stop を待機の絵へ。前の版は DEV ?knightScrapeTurn=rest (半分起きた構え) / now。
      {
        const scrapeTurnKey = knightScrapeTurnKey(boss, ss, tlCurrKey)
        if (scrapeTurnKey && map.get(scrapeTurnKey)?.complete === true) {
          tlCurrKey = scrapeTurnKey
          tlPrevKey = null
          tlBlend = 0
        }
      }
      // 配布版の既定 (more・2026-10-12 ユーザー採用): 柄の押し返しの recovery で idle の絵へ替わる所に中間の絵を 120ms (state は recovery のまま)。旧は DEV ?knightRest=mid。
      {
        const shoveKey = knightRestMidShoveKey(boss, tlCurrKey)
        if (shoveKey && map.get(shoveKey)?.complete === true) {
          tlCurrKey = shoveKey
          tlPrevKey = null
          tlBlend = 0
        }
      }
      // DEV: 「今どのキーポーズ絵が出てるか」を stash (render の drawHitboxesDev が読んで画面表示)。
      //   攻撃の判定帯とキーポーズ絵名を突き合わせ「攻撃とキーポーズのズレ」を正確に指させる道具。
      if (import.meta.env.DEV) {
        ;(globalThis as { __poseNow?: unknown }).__poseNow = {
          image: tlCurrKey, prev: tlPrevKey, blend: Math.round(tlBlend * 100) / 100,
          hitIndex: timelinePose.phaseProgress.hitIndex,
          phase: timelinePose.phaseProgress.phase,
          progress: Math.round(timelinePose.phaseProgress.progress * 100) / 100,
          attack: timelinePose.attackId,
        }
      }
    }
  }

  // ===== idle 状態のサブアニメ override (STAGE B) =====
  //   攻撃タイムライン外・idle・hp>0・遷移が落ち着いた後だけ、 idle 専用キー絵を周期スワップする。
  //   - 静止中 (sinceMoveMs 大): idle ↔ idle_breath を呼吸の正弦でクロスフェード (B-2)。
  //     IDLE_BREATH_DISABLED のボスはスキップ → 1枚固定 (1枚絵方式 §0.2)。
  //   - 移動中 (boss.ts で間合い詰め=x スライド): walk_passing ↔ walk_reach を移動距離でスクラブ (B-1)。
  //     WALK_DISCRETE_SNAP のボスは blend を 0.5 閾値で2値化しハードカット (溶かさない離散スナップ)。
  //   - 介錯は処刑台型ステップへ移行。4コマ機構と絵は他ボス用・ロールバック用に温存する。
  //     正本 = docs/candidates/2026-08-23-executioner-step-movement-design.md
  //     ★歩幅サイクルを時間でなく walkDist (実移動量) に同期 = walkSpeed に自動追従して足滑りを防ぐ。
  //   キー絵が未ロードのボスは null = 従来の単一 idle + procedural bob にフォールバック (= 無害)。
  const isLoadedKey = (k: string) => { const im = map.get(k as SpriteKey); return !!im && im.complete }
  const requestedBrideBlinkBranchKey = brideBlinkBranchPoseKey(boss)
  const brideBlinkBranchKey = requestedBrideBlinkBranchKey && isLoadedKey(requestedBrideBlinkBranchKey)
    ? requestedBrideBlinkBranchKey : null
  let idleOverride: { a: SpriteKey; b: SpriteKey; blend: number } | null = null
  let walkSurgeScreenPx = 0
  let walkFade = false
  let bobK = 1
  const executionerStepKey = boss.exec
    ? resolveExecutionerStepPoseKey(boss.exec, (key) => isLoadedKey(key))
    : null
  // 介錯の立て直し・歩き (?execRegain=walk・2026-09-18 比較): 温存中の4コマ歩きを使う。
  //   下がる距離は 100px 前後で歩き1サイクル (170px) に届かないので、下限の門は通さない。
  //   技の直後に始まるので、150ms の遷移待ちも step と同じく飛ばす (待つと立ち絵のまま滑る)。
  const execRegainWalk = isExecutionerRegainWalk(boss)
  // 覚醒王の間合いの一手 (09-26 比較 → 09-27 採用・旧は DEV `?spacing=old`): 後ろ跳びの仮組みの絵と、歩いて詰める一手の歩きの絵。
  //   どちらも技の直後に始まりうるので、150ms の遷移待ちを飛ばす (待つと立ち絵のまま動く)。
  //   歩きは始めから止まるまで歩きの絵 = 130px の下限の門を通さない。他ボス・DEV `?spacing=old` では両方 null/false。
  const spacingHopKeyRaw = awokenSpacingPoseKey(boss) as SpriteKey | null
  const spacingHopKey = spacingHopKeyRaw && isLoadedKey(spacingHopKeyRaw) ? spacingHopKeyRaw : null
  // 鶏の跳ね (DEV ?roosterMove=hop・src/rooster_hop_move.ts): 宙の絵と着地の1枚。技の直後に跳ぶこともあるので遷移待ちを飛ばす。
  const roosterHopKeyRaw = roosterHopPoseKey(boss) as SpriteKey | null
  const roosterHopKey = roosterHopKeyRaw && isLoadedKey(roosterHopKeyRaw) ? roosterHopKeyRaw : null
  // 覚醒王 (旧の動き = DEV `?spacing=old` だけ・2026-09-26「懐では噛まない」): 懐から歩いて下がる間も、始めから止まるまで歩きの絵 (後ろ向きに回す)。
  const pocketRetreatWalk = boss.state === 'idle' && boss.pocketRetreat !== undefined
  const spacingWalk = isAwokenSpacingWalk(boss) || pocketRetreatWalk
  // 剣舞の後ろへ下がる所の比較版 (DEV ?bladeBackStep=walk・src/blade_back_step.ts): 動いている間だけ門を通さず1歩踏む。配布版は false。
  const bladeBackStep = isBladeBackStepWalk(boss) && ss.sinceMoveMs < IDLE_MOVE_DEBOUNCE_MS
  // 同じ比較版の 'lock' (DEV ?bladeBackStep=lock): 足を床に留めた1歩の絵。体は絵が替わる瞬間だけ動く (boss.ts)。
  //   下がり終えた直後も、跳んだ移動量で歩きの門が開かないよう構えのまま。
  const bladeLockPose = bladeBackStepLockPose(boss)
    ?? (boss.state === 'idle' && bladeBackStepSettled(boss) && ss.sinceMoveMs < IDLE_MOVE_DEBOUNCE_MS ? 'hold' : null)
  // 騎士の足運び (2026-09-27・src/knight_gait.ts): 歩いている間ずっと歩きの絵 (130px の下限の門・150ms の遷移待ちを通さない)。
  //   DEV `?knightMove=old` では null = 従来の経路。
  const knightGaitKeyRaw = knightRestMidPoseKey(boss, ss, playing) ?? knightGaitPoseKey(boss)
  const knightGaitKey = knightGaitKeyRaw && isLoadedKey(knightGaitKeyRaw) ? knightGaitKeyRaw as SpriteKey : null
  // 短い travel は通常の150ms遷移待ちに隠れるため、stepだけ即時表示する。
  if (!tlCurrKey && boss.state === 'idle' && boss.hp > 0
      && (ss.transitionRemainingSec <= 0 || executionerStepKey !== null || execRegainWalk
        || spacingHopKey !== null || roosterHopKey !== null || spacingWalk || bladeBackStep || bladeLockPose !== null || knightGaitKey !== null)) {
    const stationary = ss.sinceMoveMs >= IDLE_MOVE_DEBOUNCE_MS
    // idle_breath crossfade は §0.2 の1枚絵方式適用で全ボス停止 (2026-06-15)。
    //   「輪郭が一致しない2枚の crossfade = 幽霊絵」を避ける。
    //   生きてる感は外部FX (霧/光/火花) で出す (隠れコスト: FX工数増)。
    //   将来「輪郭が画素単位でロックされた差分ペア」が得られたボスのみ再有効化を検討する。
    // 歩き2枚ペア経路の stride は else-if の下限判定でも要るので先に引く (executionerStepKey/4コマの
    //   分岐には影響しない・単なる値の前出し)。
    const walkCycle = WALK_CYCLE_OVERRIDE.get(bossId)
    const stridePx = walkCycle?.stridePx ?? WALK_STRIDE_PX_BY_BOSS[bossId] ?? WALK_STRIDE_PX
    if (executionerStepKey) {
      idleOverride = { a: executionerStepKey, b: executionerStepKey, blend: 1 }
    } else if (knightGaitKey) {
      idleOverride = { a: knightGaitKey, b: knightGaitKey, blend: 1 }
    } else if (spacingHopKey) {
      idleOverride = { a: spacingHopKey, b: spacingHopKey, blend: 1 }
    } else if (bladeLockPose !== null && isLoadedKey(resolveWalkPair(bossId)[0]) && isLoadedKey(resolveWalkPair(bossId)[1])) {
      const key = resolveWalkPair(bossId)[bladeLockPose === 'step' ? 1 : 0]
      idleOverride = { a: key, b: key, blend: 1 }
    } else if (roosterHopKey) {
      idleOverride = { a: roosterHopKey, b: roosterHopKey, blend: 1 }
    } else if (bladeBackStep && isLoadedKey(resolveWalkPair(bossId)[0]) && isLoadedKey(resolveWalkPair(bossId)[1])) {
      const [hold, step] = resolveWalkPair(bossId)
      const key = bladeBackStepIsStep(ss.walkBurstPx) ? step : hold
      idleOverride = { a: key, b: key, blend: 1 }
    } else if ((!boss.exec || execRegainWalk) && (!stationary || spacingWalk) && isLoadedKey(resolveWalkPair(bossId)[0]) && isLoadedKey(resolveWalkPair(bossId)[1])
        && (execRegainWalk || spacingWalk || walkPoseGateOpen(ss.walkBurstPx, stridePx))) {
      const [walkA, walkB] = resolveWalkPair(bossId)   // 既定 = walk_passing / walk_reach (DEV の試験だけ差し替わる)
      if (walkCycle && walkCycle.keys.every(isLoadedKey)) {
        // N コマの1周 (DEV)。顔の向きと逆へ進む時 (下がる歩き) は脚を逆に回す。
        const facingDir = boss.facing === 'left' ? -1 : 1
        const backward = execRegainWalk || pocketRetreatWalk || (ss.walkDir !== undefined && ss.walkDir !== facingDir)
        const key = walkCycleKey(walkCycle.keys, ss.walkDist, stridePx, backward)
        idleOverride = { a: key, b: key, blend: 1 }
        const framePx = stridePx / walkCycle.keys.length
        const frac = (((ss.walkDist / framePx) % 1) + 1) % 1
        WALK_CYCLE_FRAC_HOOK.get(bossId)?.(frac)
        if (walkCycle.smooth === 'lock') {
          walkSurgeScreenPx = -(frac - 0.5) * framePx * (ss.walkDir ?? (boss.facing === 'left' ? -1 : 1))
        } else if (walkCycle.smooth === 'fade') {
          idleOverride = { a: key, b: walkCycleKey(walkCycle.keys, ss.walkDist + framePx, stridePx, backward), blend: frac }
          walkFade = true
        }
      } else if (isLoadedKey('walk_cross') && isLoadedKey('walk_contact_b')) {
        // 温存中の4コマ歩行。介錯は上の step 分岐へ移ったため入らないが、
        // 絵を4枚 + EXTRA_POSE_KEYS_BY_BOSS へ登録した他ボスはこの経路をそのまま使える。
        // 🔴 walk_reach は「接地Aとほぼ同じ絵」(足の開き 40% 対 44%・前に出ている脚も同じ) なので
        //   4コマの並びから外している。ファイルは残してある = 戻す時は WALK_4_CYCLE へ入れるだけ。
        // 立て直しの歩きは後ろ向きに進むので、サイクルを逆に回す (前向きの歩きのまま滑らせない)。
        const tFwd = (((ss.walkDist / stridePx) % 1) + 1) % 1
        const t = execRegainWalk || pocketRetreatWalk ? (1 - tFwd) % 1 : tFwd
        const amp = WALK_BODY_BOB_BY_BOSS[bossId] ?? 0
        bobK = 1 + amp * (0.5 - 0.5 * Math.cos(t * Math.PI * 4))
        const key = WALK_4_CYCLE[Math.min(3, Math.floor(t * 4))]
        idleOverride = { a: key, b: key, blend: 1 }
      } else {
        const phase = (ss.walkDist / stridePx) * Math.PI * 2
        const blend = 0.5 - 0.5 * Math.cos(phase)   // passing↔reach を移動量でスクラブ (脚のシザー)
        if (!crossfadeEnabled || WALK_DISCRETE_SNAP.has(bossId)) {
          // 離散スナップ: blend を 0.5 閾値で2値化 → 幽霊 crossfade でなくパッと踏み替え。
          //   a === b にすることで prevImg 描画を自動スキップし、 currAlpha=1 で1枚をフル表示する。
          const snapKey: SpriteKey = blend < 0.5 ? walkA : walkB
          idleOverride = { a: snapKey, b: snapKey, blend: 1 }
          // DEV ?walkSurge=1: 絵が替わる瞬間に速く・1枚の真ん中でほぼ止まる見た目の速さ (src/boss_walk_surge.ts)。
          if (WALK_SURGE_BOSSES.has(boss.id)) walkSurgeScreenPx = bossWalkSurgeOffsetPx(ss.walkDist, stridePx) * (ss.walkDir ?? (boss.facing === 'left' ? -1 : 1))
        } else {
          idleOverride = { a: walkA, b: walkB, blend }
        }
      }
    }
  }

  // 剣舞のアルレ版の拍手 (src/blade_dancer_clap.ts): 待機・歩きの絵より先に出す。
  const clapKey = tlCurrKey ? null : bladeClapKey(boss) as SpriteKey | null
  const clapShown = !!clapKey && isLoadedKey(clapKey)
  if (clapShown) idleOverride = { a: clapKey!, b: clapKey!, blend: 1 }

  // ===== 向き替え (turn) override (STAGE B-4) =====
  //   facing 反転直後の短い窓だけ turn 絵を1拍挟む (即ミラー反転の代わりに「向き直る」体)。 idle 限定。
  const turnActive = ss.turnRemainingMs > 0 && boss.hp > 0
    && boss.state === 'idle' && isLoadedKey('turn')
    && !TURN_FLIP_BEAT_DISABLED.has(bossId)
    // 覚醒王の間合いの一手: 一手 (跳び・歩き) の絵を向き替えの1拍で上書きしない (動いている間に別の絵を挟まない)。
    && spacingHopKey === null && roosterHopKey === null && !spacingWalk && !bladeBackStep && bladeLockPose === null
    // 騎士の足運び: 歩いている間は向き替えの絵で足運びを上書きしない。
    && knightGaitKey === null
    && !clapShown

  // ===== 掴み保持 (grabbed_hold) override (処刑人 D8/D9・2026-07-05) =====
  //   プレイヤーが 'grabbed' の間だけ、 ボスの攻撃タイムライン (recovery=grab_whiff) より優先して
  //   grabbed_hold を表示する。 grab.holdMs (1150ms) < 攻撃の recoveryMs (1400/1500ms) なので、
  //   解放後は自然にタイムラインの grab_whiff (「今殴れ」の隙) へ戻る = 追加の終了処理不要。
  // ===== 掴み保持 / staggered二段 / 二形態変形 override =====
  // 専用の変形絵はフェーズ2。フェーズ1は idle 中だけ既存ポーズを保持し、掴み・崩れを上書きしない。
  const stateOverrideKey = resolveBossStateOverrideKey(boss, playerGrabbed, isLoadedKey)

  const currKey = stateOverrideKey ? effectiveKey(boss, stateOverrideKey)
    : brideBlinkBranchKey ? effectiveKey(boss, brideBlinkBranchKey)
    : turnActive ? effectiveKey(boss, 'turn')
    : idleOverride ? effectiveKey(boss, idleOverride.b)
    : tlCurrKey ? effectiveKey(boss, tlCurrKey)
    : effectiveKey(boss, ss.currKey)
  // 立ち上がりの溶け替わり (DEV ?bossRiseSmooth=blend|both・src/boss_rise.ts): 切り替えの頭だけ前の段の絵を下に描く。
  const riseBlendRaw = boss.hp > 0 && !playerGrabbed ? bossRiseBlend() : null
  const riseBlendFrom = riseBlendRaw === null ? null
    : riseBlendRaw.from === 'staggered' && isLoadedKey('staggered_hold') ? 'staggered_hold' as SpriteKey
    : riseBlendRaw.from
  const riseBlend = riseBlendRaw && riseBlendFrom && isLoadedKey(riseBlendFrom) ? { from: riseBlendFrom, t: riseBlendRaw.t } : null
  const prevKey = riseBlend ? effectiveKey(boss, riseBlend.from)
    : stateOverrideKey ? effectiveKey(boss, stateOverrideKey)
    : brideBlinkBranchKey ? effectiveKey(boss, brideBlinkBranchKey)
    : turnActive ? effectiveKey(boss, 'turn')
    : idleOverride ? effectiveKey(boss, idleOverride.a)
    : tlPrevKey ? effectiveKey(boss, tlPrevKey)
    : effectiveKey(boss, ss.prevKey)

  const prevImg = map.get(prevKey)
  const isDrawableKey = (key: SpriteKey): boolean => {
    const img = map.get(key)
    return !!img && img.complete && img.naturalWidth > 0
  }
  const drawKey = pickDrawableKey(
    currKey,
    [prevKey, lastDrawableKeyByVariant.get(variantCacheKey) ?? null, 'idle'],
    isDrawableKey,
  )
  // どの候補も未ロードなら描けない。呼び出し側に存在しない procedural fallback は期待しない。
  if (drawKey === null) return null
  const drawImg = map.get(drawKey)!
  const substitutedFrom = drawKey === currKey ? null : currKey

  if (import.meta.env.DEV) {
    const fromVariantFile = variantFileLoaded.get(variantCacheKey)?.get(drawKey) === true
    bossSpriteDrawProbe = {
      bossId,
      key: drawKey,
      previousKey: substitutedFrom === null && prevKey !== drawKey ? prevKey : null,
      substitutedFrom,
      variant,
      variantFileExists: variant === null ? drawImg.naturalWidth > 0 : fromVariantFile,
      fellBackToFlat: variant !== null && !fromVariantFile,
      facing: boss.facing,
    }
  }

  const transitionProgress = 1 - Math.min(1, ss.transitionRemainingSec / TRANSITION_DURATION_SEC)
  const baseAlphas = resolveBossSpriteTransitionAlphas(crossfadeEnabled, transitionProgress)
  let currAlpha = baseAlphas.curr
  let prevAlpha = baseAlphas.prev
  if (riseBlend) {
    // 2枚を重ねた所の濃さが1枚の時とほぼ同じになる割合 (亡霊の斬りの戻り 1029 と同じ・src/shade_slash_smooth_1028.ts)。
    currAlpha = Math.min(1, 1.4 * riseBlend.t)
    prevAlpha = Math.min(1, 1.4 * (1 - riseBlend.t))
  } else if (!crossfadeEnabled && !rushFangRelease && !(walkFade && WALK_FADE_FORCE.has(bossId))) {
    // 3系統とも常に現在の1枚だけを描く。下の従来分岐は再有効化時だけ使う。
  } else if (boss.hp <= 0) {
    // 撃破死: 攻撃中に力尽きると curr/prev が両方 defeat へ畳まれ、 currAlpha が途中のクロスフェード値
    //   (例 0.2) のまま finisher で凍結 → 死のポーズが半透明で固着 (花嫁の "薄い" バグ 2026-06-29)。
    //   死のポーズは遷移でなく確定の一枚なのでハードカット (solid) で見せる。 灰化は render 側が担う。
    //   全ボス共通 (死のポーズを一拍見せる設計に揃える)。
    currAlpha = 1
    prevAlpha = 0
  } else if (stateOverrideKey) {
    // 掴み保持・体幹崩れ・変形: 即フル表示でハードカットする確定の一枚。
    currAlpha = 1
    prevAlpha = 0
  } else if (brideBlinkBranchKey) {
    // ワープ分岐は読みの開始点。旧idleとのghost crossfadeを作らず、構えを一枚で見せる。
    currAlpha = 1
    prevAlpha = 0
  } else if (turnActive) {
    // 向き替え: 即フル表示でハードカット。
    currAlpha = 1
    prevAlpha = 0
  } else if (idleOverride) {
    // idle サブアニメ: a(prev)→b(curr) を blend でクロスフェード。
    currAlpha = idleOverride.blend
    prevAlpha = 1 - idleOverride.blend
  } else if (tlPrevKey) {
    // タイムライン・クロスフェード: 現キー(prev)→次キー(curr)を blend で溶かす (短い in-between 補完)。
    currAlpha = tlBlend
    prevAlpha = 1 - tlBlend
  } else if (tlCurrKey) {
    // タイムライン・ハードカット: 次キーを即フル表示 (接触の読みを濁らせない・Codex)。
    currAlpha = 1
    prevAlpha = 0
  } else if (prevKey === 'active' && (currKey === 'idle' || currKey === 'staggered')
    && BOSS_ATTACK_TIMELINES[bossId]) {
    // timeline ボスの攻撃離脱遷移: 汎用 active.png が 150ms 溶けて混入するのを防ぐ。
    // timeline 攻撃中は専用ポーズで表示されるが ss.currKey は 'active' のまま残るため、
    // idle / staggered 移行時に prevKey='active' (汎用 active.png=画面に出ていなかった絵) が
    // ghosting する。 timeline ボス限定で hardcut。
    //   - →idle: recovery 明けの復帰 (2026-06 修正済)
    //   - →staggered: ジャストパリィで崩した瞬間 (P1 スイープ 2026-07-03: idle と同型の穴)
    // ★idle 呼吸/walk は idleOverride で制御されるため、この分岐は触れない。
    currAlpha = 1
    prevAlpha = 0
  }
  if (substitutedFrom !== null) {
    // 未ロード絵との crossfade は半透明の幽霊になるため、代替の1枚だけをフル表示する。
    currAlpha = 1
    prevAlpha = 0
  }

  // ===== Case A: Canvas 側で「生きてる」感を足す =====
  //   1. idle bob: ゆっくり呼吸 (sin 1.4Hz, ±2.5px 上下)
  //   2. telegraph tension: 蓄積でじわっと拡大 + 微振動
  //   3. impact flash: 攻撃ヒット瞬間に白で光らせる
  //   4. defeat fade: hp 0 で徐々に透過
  let bobY = 0
  let scaleK = 1
  let shakeX = 0
  let shakeY = 0
  const fixedScaleBrideBranchCue = brideBranchCueKeepsFixedScale(bossId, currKey)
  if (boss.state === 'idle' && boss.hp > 0) {
    // IDLE_BOB_DISABLED のボスは bob を 0 にして重い静止感を出す (騎士: 動かない圧)。
    const bobAmp = IDLE_BOB_DISABLED.has(bossId) ? 0 : 2.5
    bobY = Math.sin(time * 1.4 * Math.PI * 2) * bobAmp
  }
  if (boss.state === 'telegraph' && boss.currentAttack) {
    // 連の拍の溜めの上書きがあればそれで割る (定義の値だと短縮した拍で膨らみ・震えが 6割で止まる・2026-10-10)。
    const prog = Math.min(1, (boss.stateElapsed * 1000) / Math.max(1, effectiveChargeMs(boss)))
    // 介錯の突き当ての踏み込みの版 (1028d・配布版の 1028e) は膨らませない (src/exec_jab_tremble_1028.ts)。
    // 騎士の突き・薙ぎの足を留める版 (配布版 1028) も膨らませず、震えも足さない (src/knight_smooth_flags_1028.ts)。
    const knightSmoothCalm = knightSmoothSkipsSwell(bossId, boss.currentAttack.id)
    if (!fixedScaleBrideBranchCue && !execJabSkipsSwell(boss.currentAttack.id) && !knightSmoothCalm) scaleK = 1 + prog * 0.05  // 最大 5% 拡大
    if (fixedScaleBrideBranchCue) {
      // 分岐予告3枚: 乱数の微振動では 820ms の静止感を消せない → 決定論的な引き→解放へ置換 (2026-07-19)。
      const m = resolveBrideBranchCueMotion(bossId, currKey, boss.state,
        boss.stateElapsed * 1000, boss.currentAttack.chargeMs, boss.facing, facingMul)
      shakeX = m.x
      shakeY = m.y
    } else {
      // 介錯の突き当ての震えを切った版 (DEV ?execJabSteps=1028c・src/exec_jab_tremble_1028.ts) では足さない。
      const trembleAmp = execJabSkipsTremble(boss.currentAttack.id) || knightSmoothCalm ? 0 : prog * 1.2
      shakeX = (Math.random() - 0.5) * trembleAmp
      shakeY = (Math.random() - 0.5) * trembleAmp
    }
  }
  // Trial anticipation uses foot-pivot rotation only; do not stack generic grow/jitter.
  // 「灰を食む」(DEV ?awokenDevour) も同じ: 溜めが長く、最後の 120ms は完全に止める作りなので、溜めの共通の膨らみ・震えを足さない。
  if (isAwokenRushOpening(boss) || (bossId === 'crowned-king-awoken' && (boss.currentAttack?.id === 'feralPocketSpin' || !!boss.currentAttack?.devour))
    // 騎士と花嫁の固有の致命 (DEV ?execUnique=1): 溜めが長く、止まりの間は静かに止める作りなので、溜めの共通の膨らみ・震えを足さない。
    || !!boss.currentAttack?.uniqueExec) {
    scaleK = 1
    shakeX = 0
    shakeY = 0
  }
  // 回転（5枚版）の着地の揺れ。見本どおり体の絵だけを 820〜900ms の間ぶらす (src/awoken_spin_fx.ts)。
  const spinMs = bossId === 'crowned-king-awoken' ? awokenSpinMs(boss) : null
  if (spinMs !== null) shakeX += awokenSpinShakePx(spinMs, displayHeight)
  // 壁に置いた足は拡大・震えで滑らせない。蹴り出すまでは同じ接触点を保つ。
  if (currKey === ('wall_kick' as SpriteKey) && (wallKickFootPinned(boss) || boss.currentAttack?.choreography === 'awokenRampageV5')) {
    scaleK = 1
    shakeX = 0
    shakeY = 0
  }
  if (boss.state === 'reappear' && fixedScaleBrideBranchCue) {
    // ワープ後の 470ms は従来どの分岐にも入らず完全静止だった。 着地の沈み込みだけ足す。
    const m = resolveBrideBranchCueMotion(bossId, currKey, boss.state,
      boss.stateElapsed * 1000, boss.currentAttack?.chargeMs ?? 0, boss.facing, facingMul)
    shakeX = m.x
    shakeY = m.y
  }
  // dev 計測フック: 「今どの状態の何msで、描画オフセットがいくつか」を外へ出す。
  //   モーションGIFの撮影 (scripts/capture_bride_branch_gif.mjs) が、実時間ではなく
  //   ゲーム内時刻でフレームを揃えるために読む。 揃えないと変更前後で別の瞬間が並ぶ。
  //   ★headless の requestAnimationFrame は 60fps より速く回るため、 rAF 回数で撮ると尺がズレる。
  if (bossShakeOverride) {
    // 一突きの版 (DEV ?lunaExec=pierce): 刺した瞬間だけ揺らし、その後は止める (作者 10-30「震え続けてるから止めて」)
    shakeX = bossShakeOverride.x
    shakeY = bossShakeOverride.y
  } else if (boss.state === 'staggered') {
    // よろけ揺れ
    shakeX = Math.sin(time * 25) * 1.5
    shakeY = Math.cos(time * 28) * 1.2
  }
  // パリィで弾かれた怯み (recoil): 後方へのけぞって戻る。 「弾いた手応え」の物理。
  //   parryOverlayRemainingSec を再利用 (impact で最大→0 へ減衰)。 away = ボスの背中側 (facing の逆)。
  //   PARRIED_OVERLAY_DISABLED のボスは overlay が出ない分 recoil を強め、体感を補う (14px→20px)。
  if (ss.parryOverlayRemainingSec > 0) {
    const p = ss.parryOverlayRemainingSec / PARRY_OVERLAY_DURATION_SEC  // 1=impact → 0
    const recoil = p * p                                                // 鋭く入って戻る
    const awayScreen = boss.facing === 'left' ? 1 : -1                  // 前=facing方向 → 背中=逆
    const recoilAmp = resolveParriedPoseLook(bossId, ss.parryOverlayRemainingSec).recoilAmp   // 騎士 11・花嫁 16・重ね無し 20・ほか 14
    shakeX += awayScreen * facingMul * recoilAmp * recoil              // 後方へ (flip 補正)
    shakeY += -5 * recoil                                               // 軽く浮く
  }
  // 受け (ガード) の brace (③⑤): 弾かれた瞬間に槍を寄せて受け止めるように小さく後方へ press して戻る。
  //   parry recoil (14px・浮く) より小さく短い (5px・沈む) = 「受け止めた=動じない」手応え。
  if (ss.guardReactRemainingSec > 0) {
    const g = ss.guardReactRemainingSec / GUARD_REACT_DURATION_SEC      // 1=impact → 0
    const brace = g * g
    const awayScreen = boss.facing === 'left' ? 1 : -1
    shakeX += awayScreen * facingMul * 5 * brace                       // 小さく後方へ press
    shakeY += 2 * brace                                                 // わずかに沈む (踏ん張り)
  }
  const playerHitReact = playerHitReactionOffset(ss, facingMul)
  shakeX += playerHitReact.x
  shakeY += playerHitReact.y

  // defeat fade: 1.0 秒かけて 1 → 0 (完全消滅)。 旧 0.35 残しは「ボスが薄く残る」誤読を生むため撤去 (2026-06-24)。
  //   ※ BOSS_DEATH_COLLAPSE 登録ボスは「死のポーズを holdSec だけ solid → fadeSec でフェード」= 死を見せてから消す。
  let defeatAlpha = 1
  if (boss.hp <= 0) {
    const dc = BOSS_DEATH_COLLAPSE[boss.id]
    if (dc) {
      defeatAlpha = ss.defeatElapsedSec < dc.holdSec
        ? 1
        : Math.max(0, 1 - (ss.defeatElapsedSec - dc.holdSec) / dc.fadeSec)
    } else {
      defeatAlpha = Math.max(0, 1 - ss.defeatElapsedSec / 1.0)
    }
  }

  // ===== 攻撃ごとの軌道差 (見た目で攻撃を判別させる = CLAUDE.md「予兆は体で見せる」) =====
  //   突進牙 (houndLunge): 沈み込む(crouch) → 弧を描いて飛びかかる(leap, 空中)。
  //   噛みつき (houndBite): その場で素早く前へ噛む(接地, 縦の動きは小さい)。
  //   軌道 (跳ぶ vs 接地) が全く違うので同じスプライトでも別の攻撃と即読める。
  let atkBobY = 0
  let atkScaleX = 1
  let atkScaleY = 1
  const atk = boss.currentAttack
  // ★「キーが主、 手続きが従」(Codex 2026-06-09): タイムラインがある攻撃は跳躍・着地・接地をキー絵が持つ。
  //   攻撃専用の大きな atkBobY (突進の -54px 弧) / squash は無効化する (キー絵と綱引きして「ペタン」を生む)。
  //   手続き層は呼吸 bob / telegraph 緊張 scaleK / パリィ recoil / 命中フラッシュ のみ (=味付け) に限定。
  if (atk && !tl && (boss.state === 'active' || boss.state === 'telegraph')) {
    // phase: telegraph 0→1 (easeIn 溜め) → active 1→2 (easeOut 解放)
    let phase = 0
    if (boss.state === 'telegraph') {
      const l = Math.min(1, (boss.stateElapsed * 1000) / Math.max(1, effectiveChargeMs(boss)))
      phase = l * l
    } else {
      const l = Math.min(1, (boss.stateElapsed * 1000) / atk.activeMs)
      phase = 1 + (1 - (1 - l) * (1 - l))
    }
    if (atk.id === 'houndLunge') {
      if (phase <= 1) {
        // 溜め: 沈んで潰れる (獣が脚を畳む)
        atkScaleY = 1 - 0.12 * phase
        atkScaleX = 1 + 0.10 * phase
        atkBobY = 8 * phase
      } else {
        // 解放: 弧を描いて跳ぶ → 着地
        const ap = phase - 1
        const arc = Math.sin(ap * Math.PI)
        atkScaleY = 1 + 0.06 * arc
        atkScaleX = 1 - 0.04 * arc
        atkBobY = 8 * (1 - ap) - 54 * arc   // 沈み解除しつつ大きく上へ弧 → 戻る
      }
    } else if (atk.id === 'houndBite') {
      // 接地のまま素早い前噛み。 縦移動は控えめ、 噛む瞬間に横へ伸びる。
      if (phase <= 1) {
        atkBobY = -3 * phase                // わずかに伸び上がって溜める
      } else {
        const ap = phase - 1
        atkBobY = -3 * (1 - ap)
        atkScaleX = 1 + 0.06 * Math.sin(ap * Math.PI)  // 噛みつきで前へ伸びる
      }
    }
  }

  // ===== 花嫁 procedural 味付け (2026-07-11 ユーザー要望): タイムラインボスは上の !tl squash が効かないので
  //   ここで足す。 ① 溜め(telegraph) ② 振りかぶり(hit7) = 「縦に伸びる」緊張ストレッチ (騎士式の予兆)。
  //   ③ 旋回連(hit0-6 active) = 体を左右に絞る turnaround = 回り込みアークと同位相で「体も回る」錯覚
  //   (アークは回るのに体が正面で切替わるだけ=「あと一息」の正体への手当て)。 amplitude 控えめ=破綻回避・⬜微調整。
  if (bossId === 'bride' && boss.currentAttack) {
    const cn = boss.currentAttack
    let windY = 0   // 0..1 溜め度 (縦伸び)
    if (boss.state === 'telegraph') {
      windY = Math.min(1, (boss.stateElapsed * 1000) / Math.max(1, effectiveChargeMs(boss)))
    } else if (cn.id === 'veilFlurry' && boss.comboHitIndex === 7
               && (boss.state === 'active' || boss.state === 'comboGap')) {
      // 振りかぶり(hit7): active→comboGap を通しでじわっと最大へ (頂点で最大の伸び→だん)
      const ms = (boss.state === 'active' ? 0 : (cn.activeMs ?? 160)) + boss.stateElapsed * 1000
      windY = Math.min(1, ms / 700)
    }
    if (windY > 0) {
      const e = windY * windY                 // ease-in (じわっと)
      // 2026-07-11 ユーザー修正: 「縦だけ伸ばす」でなく「少し拡大」= ほぼ均等スケールアップ。
      // 2026-07-19: 分岐予告3枚もこの緊張ストレッチへ戻す (ユーザー指摘「他の攻撃では大きくなる
      //   仕組みを組んでるのに、あれじゃダメなのか」)。
      //   ・以前 +11% 伸びた原因は 汎用の均等+5% と この縦寄り+6% の二重がけ。 汎用側は
      //     fixedScaleBrideBranchCue で切ったままなので、 二重がけは復活しない。
      //   ・windY は telegraph 中だけ立つ → reappear 470ms は固定スケールのまま。
      //     「再出現開始から接触まで伸び続けて別の絵に飛んで見える」 旧問題は再発しない。
      //   ・12原則が言うのは一様拡大ではなく非一様な潰し/伸ばし。 縦寄り(×1.12)の こちらが
      //     正典に近く、 汎用の均等拡大より筋が良い (2026-07-19 他ゲーム調査)。
      const grow = 0.06 * e
      atkScaleX *= 1 + grow
      atkScaleY *= 1 + grow * 1.12            // ごく僅かに縦寄り (伸びすぎない)
      // 分岐予告3枚は決定論的な引き→解放で予備動作を作るため、乱数の微振動を重ねない
      // (乱数は「意図」に読めず、 せっかくの等速の引きを濁らせる)。
      const tr = fixedScaleBrideBranchCue ? 0 : e * 1.0
      shakeX += (Math.random() - 0.5) * tr    // 緊張の微振動
      shakeY += (Math.random() - 0.5) * tr
    }
    // ③ 旋回 turnaround: active 中の各薙ぎで、 回り込みアークと同じ角度位相で体幅を絞る
    //   (cos≈±1=edge-on で細く / cos≈0=broadside で通常) = 紙が回るような回転の錯覚 (控えめ)。
    if (cn.id === 'veilFlurry' && boss.state === 'active' && boss.comboHitIndex <= 6) {
      const ang = boss.comboHitIndex * 2.35   // render のアーク STEP と同値
        + (boss.stateElapsed * 1000 / Math.max(1, cn.activeMs ?? 160)) * 0.7
      const edge = Math.abs(Math.cos(ang))
      atkScaleX *= 1 - 0.09 * edge
      atkScaleY *= 1 + 0.03 * edge
    }
  }

  // ===== タイムラインの手続きチャンネル (2026-07-29) =====
  //   キーが持つ offset/scale/rot を、既存の手続き層へ**加算**する。
  //   ★ここが案Aの合流点。 ボス名の if ではなくデータで動きを書けるようにするための唯一の口。
  //   ★offsetX は「ボスの前方向が正」。 画面向き→ソース画像空間へ facingMul で変換する
  //     (既存の recoil / guard brace と同じ規約)。
  //   ★描画専用。 boss.x・strike 判定・影は一切動かさない。
  if (tlMotion !== NO_POSE_MOTION) {
    const forwardScreen = boss.facing === 'left' ? -1 : 1
    shakeX += forwardScreen * facingMul * tlMotion.offsetX
    // 比較版 (DEV ?soleLock=1・src/boss_sole_lock.ts): offset.y を足元の伸び縮みへ置き換えて靴底を床に残す。配布版は素通し。
    const vertical = bossSoleLockEnabled()
      ? soleLockMotion(tlMotion.offsetY, tlMotion.scaleY, displayHeight * atkScaleY, tlCurrKey ?? currKey)
      : { offsetY: tlMotion.offsetY, scaleY: tlMotion.scaleY }
    shakeY += vertical.offsetY
    atkScaleX *= tlMotion.scaleX
    atkScaleY *= vertical.scaleY
  }
  // 止まった長い戻りに足す重心の沈み (縦の縮み)・引き戻しの傾き・息 (DEV ?recoverySway=1・src/recovery_sway.ts)。描くだけ。指定なし = NO_RECOVERY_SWAY。
  const sway = recoverySwayFor(bossId, boss)
  // 縦の縮みと傾きはどちらも足元が支点 = 足は床から動かない (めり込み・足滑りを出さない)。
  if (sway !== NO_RECOVERY_SWAY) atkScaleY *= sway.scaleY
  const drawRotRad = ((tlMotion.rot + sway.rot) * Math.PI) / 180

  // dev 計測フック: 「今どの状態の何msで、変位と拡大がいくつか」を外へ出す。
  //   モーションGIFの撮影 (scripts/capture_bride_branch_gif.mjs) が、実時間ではなく
  //   ゲーム内時刻でフレームを揃えるために読む。 揃えないと変更前後で別の瞬間が並ぶ。
  //   ★headless の requestAnimationFrame は 60fps より速く回るため、 rAF 回数で撮ると尺がズレる。
  if (import.meta.env.DEV) {
    ;(globalThis as { __bossMotionProbe?: unknown }).__bossMotionProbe = {
      tlRot: Math.round(tlMotion.rot * 100) / 100,
      swayRot: Math.round(sway.rot * 100) / 100, swayScaleY: Math.round(sway.scaleY * 10000) / 10000,
      tlOffX: Math.round(tlMotion.offsetX * 100) / 100,
      tlOffY: Math.round(tlMotion.offsetY * 100) / 100,
      bossId, state: boss.state, currKey, fixedCue: fixedScaleBrideBranchCue,
      ms: Math.round(boss.stateElapsed * 1000),
      shakeX: Math.round(shakeX * 100) / 100, shakeY: Math.round(shakeY * 100) / 100,
      growY: Math.round(atkScaleY * 1000) / 1000,
    }
  }

  // 2枚歩きの見た目の速さの波 (DEV ?walkSurge=1)。shakeX は向きを掛ける前の値なので facingMul で戻す。
  if (walkSurgeScreenPx !== 0) shakeX += walkSurgeScreenPx * facingMul

  // ===== 描画矩形 = 面積正規化スケール + 接地アンカー (2026-06-09 サイズ一貫性修正) =====
  //   旧: 画像高さ→displayHeight。 だが新キーポーズは透過余白が不均一で「獣が画像をどれだけ占めるか」が
  //   コマで違い → 画面サイズが変動した (例 contact は獣が画像の55%・windupは98%)。
  //   → 各画像の中身 bbox + 面積で「ピクセル/単位」を揃え、 bbox 下端を接地 (centerY+12) に固定する。
  //   高く立つ↑/低く伏せる↓ は bbox 高さの差として自然に残る。 meta 不能 (canvas 無) は従来式へフォールバック。
  const rectFor = (img: HTMLImageElement, key: SpriteKey): BossSpritePlacement => {
    const meta = getSpriteMeta(bossId, variant, key, img)
    if (meta) {
      const fellBackToFlat = mapFellBackToFlat
        || (variant !== null && variantFileLoaded.get(variantCacheKey)?.get(key) === false)
      const fajinArt = bossId === 'the-executioner' && variant === 'blade' && !fellBackToFlat
        ? FAJIN_DOWNCUT_ART[key] : undefined
      // 原画の後ろ足は bbox 右端。左右反転後も実際の壁に足裏を固定する。
      if (key === ('wall_kick' as SpriteKey) && (wallKickFootPinned(boss) || boss.currentAttack?.choreography === 'awokenRampageV5')) {
        const walls = arenaBoundsFor(boss.id)
        const wallX = boss.facing === 'right' ? walls.leftWall : walls.rightWall
        const placement = resolveBossSpriteArtPlacement(bossId, key, meta, centerX, centerY, displayHeight, facingMul,
          { variant, fellBackToFlat, scaleX: scaleK * bobK * atkScaleX })
        shakeX = (wallX - centerX) * facingMul - placement.opaqueRect.width / 2
      }
      return resolveBossSpriteArtPlacement(bossId, key, meta, centerX, centerY, displayHeight, facingMul, {
        variant, fellBackToFlat,
        scaleX: scaleK * bobK * atkScaleX, scaleY: scaleK * bobK * atkScaleY * bossRiseScaleY(),
        offsetX: shakeX, offsetY: bobY + shakeY + atkBobY,
        rotationRad: drawRotRad,
        // ?st6Polish=1 の振り下ろし・斬り上げ・据えの一打だけ、踏んでいる足で置く (src/exec_plant_foot.ts)。
        //   足の位置は元の絵の画素なので、枠を左へ広げた _wfix (砲の襟の3枚) はその分を足す。
        footAnchor: fellBackToFlat ? undefined
          : shiftFootAnchorX(plantFootAnchorFor(bossId, variant, key, boss.currentAttack?.id) ?? finisherFootAnchorFor(bossId, variant, key),
            execWeaponFixOriginX(bossId, variant, key, meta)),
        // Idle's feet lie 18 source pixels ahead of its bbox center. Preserve that small bias.
        horizontalAnchorX: bossId === 'the-executioner' && variant === 'cannon' && !fellBackToFlat && CANNON_FAJIN_ART[key]
          ? CANNON_FAJIN_ART[key]!.anchorX
          : bossId === 'the-executioner' && variant === 'cannon' && !fellBackToFlat && EXEC_CLOAK_CANNON_ANCHOR_X[key] !== undefined
            ? EXEC_CLOAK_CANNON_ANCHOR_X[key]
          : fajinArt ? fajinArt.footX - 18 / fajinArt.scale
          : bossId === 'the-executioner' && variant === 'blade' && !fellBackToFlat ? (EXEC_JAB_LUNGE_ANCHOR_X[key] ?? (key === 'step_settle' ? EXEC_CLOAK_SETTLE_ANCHOR_X : EXEC_CLOAK_FLOW_ANCHOR_X[key])) : undefined,
      })
    }
    const aspect = img.naturalHeight / img.naturalWidth
    const dhf = displayHeight * scaleK * bobK * atkScaleY
    const dwf = (displayHeight / aspect) * scaleK * bobK * atkScaleX
    return resolveBossSpritePlacement({
      imageKey: key,
      imageWidth: img.naturalWidth,
      imageHeight: img.naturalHeight,
      opaqueBounds: null,
      pivotX: centerX,
      pivotY: centerY + 12,
      scaleX: dwf / img.naturalWidth,
      scaleY: dhf / img.naturalHeight,
      offsetX: shakeX,
      offsetY: bobY + shakeY + atkBobY,
      facingMul,
      rotationRad: drawRotRad,
    })
  }
  // 補正・基準高・面積正規化は要求キーでなく、実際に描くキーから解決する。
  const r = rectFor(drawImg, drawKey)
  // 騎士と戴冠獣の絵の切り替えで跳ぶ所を、切り替え直後だけ曲げて寄せる比較版 (DEV ?tweenFix=1・src/tween_fix.ts)。
  let tweenMesh: TweenMesh | null = null
  let tweenProbe: unknown = null
  {
    const plan = tweenFixPlan(bossId, drawKey, time, drawKey === 'rest_from_rearm' ? knightRestElapsedMs(boss, ss) : null, (key) => {
      const im = key === drawKey ? drawImg : map.get(key as SpriteKey)
      if (!im?.complete || !im.naturalWidth) return null
      return { width: im.naturalWidth, height: im.naturalHeight, placement: key === drawKey ? r : rectFor(im, key as SpriteKey) }
    })
    if (plan) {
      tweenMesh = buildTweenMesh(plan, drawImg.naturalWidth, drawImg.naturalHeight)
      if (import.meta.env.DEV) {
        tweenProbe = { from: plan.from, e: plan.e, d: Object.fromEntries(plan.names.map((n, i) => [n, meshOffsetAt(tweenMesh!, plan.points[i][0], plan.points[i][1])])) }
      }
    }
  }
  // dev 計測フック: 描いた絵と置き方 (撮影が絵の中の点＝穂先・兜・頭を画面の位置へ写すため・scripts/capture_exec_unique_polish.mjs)。
  if (import.meta.env.DEV) {
    ;(globalThis as { __bossDrawProbe?: unknown }).__bossDrawProbe = {
      key: drawKey, bossId, state: boss.state, ms: Math.round(boss.stateElapsed * 1000), x: boss.x, facing: boss.facing, placement: r,
      // 描いた絵のファイル (撮影が絵の透明度から両足の位置を拾うため・scripts/capture_stun_finisher_cut.mjs)。
      src: drawImg.src,
      // ?tweenFix=1 で曲げて描いたコマの、部位ごとの動かした量 (絵の画素・scripts/measure_knight_hound_tween_1025.mjs)。
      tw: tweenProbe,
      // 歩きのコマの溶かしで重ねる前のコマ (撮影が両方の絵の床の足を測るため・scripts/capture_hound_walk_slide_1105.mjs)。
      prev: walkFade && prevAlpha > 0 && prevImg && isDrawableKey(prevKey) && prevKey !== drawKey
        ? { key: prevKey, alpha: prevAlpha, currAlpha, placement: rectFor(prevImg, prevKey) } : null,
    }
    ;(globalThis as { __projectBossPoint?: unknown }).__projectBossPoint = projectBossSpriteSourcePoint
  }

  ctx.save()
  ctx.translate(r.pivot.x, r.pivot.y)
  ctx.scale(r.facingMul, 1)
  ctx.translate(-r.pivot.x, -r.pivot.y)

  // タイムラインの回転 (2026-07-29)。 ピボットは足元 (接地点) = 立っている物が傾く自然な軸。
  //   ⚠️ facingMul の反転より内側に置く。 外側だと左右で傾きが逆になる。
  //   ⚠️ 影は別描画なので回らない = 「接地は動かない」規約を守る。
  if (r.rotationRad !== 0) {
    ctx.translate(r.pivot.x, r.pivot.y)
    ctx.rotate(r.rotationRad)
    ctx.translate(-r.pivot.x, -r.pivot.y)
  }

  // rembg で透過済みのため通常合成で OK (背景は alpha 0 のまま)
  ctx.globalAlpha = defeatAlpha

  // 回転の残像: 直前の絵を薄く現在の絵の奥へ重ねる（回っている間だけ・同じ軸足の位置）。
  const ghostKey = spinMs !== null ? awokenSpinGhostKey(drawKey, spinMs) as SpriteKey | null : null
  const ghostImg = ghostKey ? map.get(ghostKey) : undefined
  if (ghostKey && ghostImg?.complete && ghostImg.naturalWidth) {
    const rg = rectFor(ghostImg, ghostKey)
    ctx.globalAlpha = AWOKEN_SPIN_GHOST_ALPHA * defeatAlpha
    ctx.drawImage(ghostImg, rg.imageRect.x, rg.imageRect.y, rg.imageRect.width, rg.imageRect.height)
    ctx.globalAlpha = defeatAlpha
  }
  // 剣舞の絵ごとの色の差をならす比較版 (DEV ?bladeColor=idle|mid・src/blade_color_match.ts)。配布版は filter を触らない。
  const baseFilter = ctx.filter
  const colorFor = (key: string): void => {
    if (bossId !== 'blade-dancer') return
    ctx.filter = bladeColorFilter(SPRITE_FILE_OVERRIDE.get(`blade-dancer#${variant ?? ''}/${key}`) ?? key) ?? baseFilter
  }
  const parriedLook = resolveParriedPoseLook(bossId, ss.parryOverlayRemainingSec)
  const cutImg = parriedLook.cutToParried ? map.get('parried') : undefined
  if (cutImg?.complete && cutImg.naturalWidth) {
    // DEV ?parriedPose=cut (B): 弾かれの絵へ重ねずに替えて止める。
    const rc = rectFor(cutImg, 'parried')
    ctx.globalAlpha = defeatAlpha
    colorFor('parried')
    ctx.drawImage(cutImg, rc.imageRect.x, rc.imageRect.y, rc.imageRect.width, rc.imageRect.height)
  } else if (walkFade && !tweenMesh && prevAlpha > 0 && prevImg && isDrawableKey(prevKey) && prevKey !== drawKey) {
    // 歩きのコマの溶かし (DEV ?houndWalkFix=fade): 'lighter' で足すと重なった所の不透明度が 1 のまま
    //   (普通に重ねると真ん中で体が 75% に透ける)。別のキャンバスで足してから1回で描く。
    const off = walkFadeCanvas(ctx.canvas.width, ctx.canvas.height)
    const o = off.getContext('2d')!
    o.setTransform(1, 0, 0, 1, 0, 0)
    o.clearRect(0, 0, off.width, off.height)
    o.setTransform(ctx.getTransform())
    o.globalCompositeOperation = 'lighter'
    const rp = rectFor(prevImg, prevKey)
    o.globalAlpha = prevAlpha
    o.drawImage(prevImg, rp.imageRect.x, rp.imageRect.y, rp.imageRect.width, rp.imageRect.height)
    o.globalAlpha = currAlpha
    o.drawImage(drawImg, r.imageRect.x, r.imageRect.y, r.imageRect.width, r.imageRect.height)
    ctx.save()
    ctx.setTransform(1, 0, 0, 1, 0, 0)
    ctx.globalAlpha = defeatAlpha
    ctx.drawImage(off, 0, 0)
    ctx.restore()
  } else {
    if (prevAlpha > 0 && prevImg && isDrawableKey(prevKey) && prevKey !== drawKey) {
      const rp = rectFor(prevImg, prevKey)
      ctx.globalAlpha = prevAlpha * defeatAlpha
      colorFor(prevKey)
      ctx.drawImage(execBodyImg(bossId, prevKey, prevImg), rp.imageRect.x, rp.imageRect.y, rp.imageRect.width, rp.imageRect.height)
    }
    ctx.globalAlpha = (currAlpha > 0 ? currAlpha : 1) * defeatAlpha
    colorFor(drawKey)
    const bodyImg = execBodyImg(bossId, drawKey, drawImg)
    if (tweenMesh) drawTweenWarp(ctx, bodyImg, r.imageRect, tweenMesh)
    else ctx.drawImage(bodyImg, r.imageRect.x, r.imageRect.y, r.imageRect.width, r.imageRect.height)
  }
  if (bossId === 'blade-dancer') ctx.filter = baseFilter
  warmExecBodyImgs(bossId, map)
  // 要求どおりの絵を描けた時だけ更新する。代替コマで履歴を上書きしない。
  if (substitutedFrom === null) lastDrawableKeyByVariant.set(variantCacheKey, drawKey)

  // パリィ成功時の一瞬重ねるオーバーレイ ('parried' スプライト)
  //   PARRIED_OVERLAY_DISABLED のボスはスキップ (半透明二重写しを避ける §0.2)。
  //   手応えは recoil + hit_spark + hitStop + SFX の組み合わせで出す。
  //   2026-10-19 から既定で重ねない (resolveParriedPoseLook)。 重ねるのは DEV ?parriedPose=overlay の時だけ。
  if (parriedLook.overlayAlpha > 0) {
    const parryImg = map.get('parried')
    if (parryImg && parryImg.complete) {
      const rp = rectFor(parryImg, 'parried')
      ctx.globalAlpha = parriedLook.overlayAlpha * defeatAlpha
      ctx.drawImage(parryImg, rp.imageRect.x, rp.imageRect.y, rp.imageRect.width, rp.imageRect.height)
    }
  }

  // 攻撃命中の白フラッシュ: 同じ画像を 'lighter' 合成 + 白っぽく
  // (drawImage で source-atop な白塗りは難しいので、画像を再描画+透明度で代用)
  if (ss.impactFlashRemainingSec > 0) {
    const a = ss.impactFlashRemainingSec / IMPACT_FLASH_DURATION_SEC
    ctx.save()
    ctx.globalCompositeOperation = 'lighter'
    ctx.globalAlpha = a * ss.impactFlashIntensity
    ctx.drawImage(drawImg, r.imageRect.x, r.imageRect.y, r.imageRect.width, r.imageRect.height)
    ctx.restore()
  }

  ctx.restore()
  if (isAwokenRushOpening(boss)
    && ['rush_windup', 'rush_windup_fangs_0917', 'rush_windup_fangs_1007'].includes(drawKey)) {
    drawAwokenRushBrake(ctx, boss, [
      projectBossSpriteRelativePoint(r, [0.15, 0.956]),
      projectBossSpriteRelativePoint(r, [0.30, 0.982]),
    ], defeatAlpha, r.imageRect.height)
  }
  return r
}

// 止まった長い戻りに足す量 (DEV ?recoverySway=1・src/recovery_sway.ts)。戻りを途中で切られた時と倒れた時は足さない。
function recoverySwayFor(bossId: string, boss: Boss): RecoverySway {
  if (boss.state !== 'recovery' || !boss.currentAttack || boss.cutRecovery || boss.hp <= 0) return NO_RECOVERY_SWAY
  const recMs = boss.currentString?.beats[boss.beatIndex]?.recoveryOverrideMs ?? boss.currentAttack.recoveryMs
  return recoverySwayMotion(bossId, boss.stateElapsed * 1000, recMs)
}

/** 影の体の動き (render.ts の drawShadeBody が決める: 浮遊・溜めの張りと震え・弾かれ)。 */
export type ShadeBodyMotion = { bobY: number; shakeX: number; shakeY: number; scaleK: number; recoilX: number; recoilY: number }

// 名も無き亡霊を、ほかのボスと同じ描く段で描く (2026-10-26 docs/research/2026-10-26-shade-common-draw.md)。
//   置き場所 (resolveBossSpritePlacement) を求め、描く行列は 弾かれ → 左右反転 → 足元が支点の傾き → 足元を中心に張り → drawImage / 曲げ描き (?tweenFix)。
//   止まった戻りの体の動き (?recoverySway)・曲げ描き・撮影の口 (__bossDrawProbe) もここで通る。
//   ポーズ選び・絵の読み込み・透明度・崩れ方は render.ts 側のまま。素材は画面左向きが基準。
//   置き方は旧式と同じ: 高さ height の箱の下端を足元 (b.y + 浮遊 + footOffset) に置き、溜めの張りは足元を中心に膨らむ。
//   弾かれ (recoil) は画面の座標で足すので、反転の内側では向きを掛けて戻す。
export function drawShadeSprite(
  ctx: CanvasRenderingContext2D, boss: Boss, img: HTMLImageElement, key: string, m: ShadeBodyMotion,
  height: number, footOffset: number, time: number, imgOf: (key: string) => HTMLImageElement | null,
): BossSpritePlacement {
  const facingMul: 1 | -1 = boss.facing === 'right' ? -1 : 1
  const sway = recoverySwayFor(boss.id, boss)
  const placementOf = (im: HTMLImageElement, k: string): BossSpritePlacement => {
    const w = height * (im.naturalWidth / im.naturalHeight) * m.scaleK
    const h = height * m.scaleK * (sway !== NO_RECOVERY_SWAY ? sway.scaleY : 1) * bossRiseScaleY()
    return resolveBossSpritePlacement({
      imageKey: k as SpriteKey,
      imageWidth: im.naturalWidth,
      imageHeight: im.naturalHeight,
      opaqueBounds: null,
      pivotX: boss.x,
      pivotY: boss.y + m.bobY + footOffset + m.shakeY,
      scaleX: w / im.naturalWidth,
      scaleY: h / im.naturalHeight,
      offsetX: m.shakeX + m.recoilX * facingMul,
      offsetY: m.recoilY,
      facingMul,
      rotationRad: (sway.rot * Math.PI) / 180,
    })
  }
  const r = placementOf(img, key)
  let tweenMesh: TweenMesh | null = null
  let tweenProbe: unknown = null
  const plan = tweenFixPlan(boss.id, key, time, null, (k) => {
    const im = k === key ? img : imgOf(k)
    if (!im?.complete || !im.naturalWidth) return null
    return { width: im.naturalWidth, height: im.naturalHeight, placement: k === key ? r : placementOf(im, k) }
  })
  if (plan) {
    tweenMesh = buildTweenMesh(plan, img.naturalWidth, img.naturalHeight)
    if (import.meta.env.DEV) {
      tweenProbe = { from: plan.from, e: plan.e, d: Object.fromEntries(plan.names.map((n, i) => [n, meshOffsetAt(tweenMesh!, plan.points[i][0], plan.points[i][1])])) }
    }
  }
  if (import.meta.env.DEV) {
    ;(globalThis as { __bossDrawProbe?: unknown }).__bossDrawProbe = {
      key, bossId: boss.id, state: boss.state, ms: Math.round(boss.stateElapsed * 1000), x: boss.x, facing: boss.facing, placement: r,
      src: img.src, tw: tweenProbe,
    }
  }
  // 行列は旧の影と同じ順で組む (弾かれ → 反転 → [傾き] → 足元を中心に張り → 張る前の箱へ描く)。
  //   r と同じ置き方だが、ほかのボスの順 (張りを箱へ焼き込む) で組むと浮動小数の丸めで縁の数画素が変わる
  //   (600 通り中 88 通り・2026-10-26 の重ね比べ) ので、配布版の絵を1画素も変えないためにこの順を守る。
  const footX = boss.x + m.shakeX
  const footY = r.pivot.y
  const w = height * (img.naturalWidth / img.naturalHeight)
  const h = sway !== NO_RECOVERY_SWAY ? height * sway.scaleY : height
  ctx.save()
  ctx.translate(m.recoilX, m.recoilY)
  if (facingMul === -1) {
    ctx.translate(boss.x, 0)
    ctx.scale(-1, 1)
    ctx.translate(-boss.x, 0)
  }
  if (r.rotationRad !== 0) {
    ctx.translate(footX, footY)
    ctx.rotate(r.rotationRad)
    ctx.translate(-footX, -footY)
  }
  ctx.translate(footX, footY)
  ctx.scale(m.scaleK, m.scaleK)
  ctx.translate(-footX, -footY)
  const box = { x: footX - w / 2, y: footY - h, width: w, height: h }
  if (tweenMesh) drawTweenWarp(ctx, img, box, tweenMesh)
  else ctx.drawImage(img, box.x, box.y, box.width, box.height)
  ctx.restore()
  return r
}
